import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Language = "en" | "es";

const STORAGE_KEY = "adkavi:lang";

type Dict = Record<string, string>;

const STRINGS: Record<Language, Dict> = {
  en: {
    "nav.login": "Log In",
    "nav.joinWaitlist": "Join Waitlist",
    "lang.label": "Language",
    "lang.en": "English",
    "lang.es": "Spanish",
    "settings.language": "Language",
    "settings.languageDesc": "Choose how AdKavi talks to you.",
    "welcome.title": "Welcome to AdKavi 🎉",
    "welcome.subtitle": "Two quick steps to get your first campaign live.",
    "welcome.step1": "Connect your ad accounts",
    "welcome.step1desc": "Link Meta, TikTok, or Google so we can launch on your behalf.",
    "welcome.step2": "Create your first campaign",
    "welcome.step2desc": "Our AI writes the copy, picks the audience, and gets it ready for review.",
    "welcome.cta.connect": "Connect accounts",
    "welcome.cta.campaign": "Create first campaign",
    "welcome.skip": "I'll explore on my own",
    "welcomeConnect.title": "Welcome to AdKavi",
    "welcomeConnect.desc": "Connect your ad accounts to unlock real campaign data across all platforms.",
    "welcomeConnect.meta.desc": "Facebook & Instagram campaigns",
    "welcomeConnect.tiktok.desc": "TikTok Ads Manager",
    "welcomeConnect.google.desc": "Google Search, Display & Shopping",
    "welcomeConnect.connectBtn": "Connect",
    "welcomeConnect.youtube.name": "YouTube Ads",
    "welcomeConnect.youtube.desc": "Included with Google Ads",
    "welcomeConnect.youtube.badge": "Via Google",
    "welcomeConnect.skip": "Skip for now · Connect later from Settings",
    "approval.title": "Review your ad",
    "approval.copy": "Ad copy",
    "approval.creative": "Creative",
    "approval.targeting": "Targeting",
    "approval.requestChanges": "Request changes",
    "approval.approve": "Approve & schedule",
    "approval.changesPlaceholder": "What would you like to change?",
    "approval.notFound": "Campaign not found.",
    "approval.statusApproved": "Approved — moving to scheduled.",
    "approval.statusChanges": "Change requested. Your manager will revise.",
    "competitors.title": "Competitor research (optional)",
    "competitors.desc": "Search competitors to inform your messaging before launch.",
    "competitors.placeholder": "Add a competitor (brand or website)",
    "competitors.add": "Add",
    "competitors.skip": "Skip — continue without research",
    "competitors.empty": "Please enter a competitor before adding.",
    "competitors.duplicate": "That competitor is already in your list.",
    "checklist.title": "Get started",
    "checklist.hide": "Hide",
    "checklist.connect": "Connect ad account",
    "checklist.campaign": "Create first campaign",
    "checklist.budget": "Set budget",
    "checklist.launch": "Launch first ad",
    "tour.stepCounter": "Step {current} of {total}",
    "tour.next": "Next",
    "tour.finish": "Finish",
    "tour.skip": "Skip tour",
    "tour.pause": "Pause",
    "tour.resume": "Resume tour",
    "tour.step.dashboard.title": "Your Dashboard",
    "tour.step.dashboard.desc": "Track performance, spend, and key metrics across all your campaigns in one place.",
    "tour.step.campaigns.title": "Create a Campaign",
    "tour.step.campaigns.desc": "Launch new ad campaigns across Meta, TikTok, and Google with AI-generated copy and creative.",
    "tour.step.aiStudio.title": "AI Studio",
    "tour.step.aiStudio.desc": "Generate videos, voiceovers, avatars, and Spanish lip-sync content with AI.",
    "tour.step.content.title": "Content Calendar",
    "tour.step.content.desc": "Plan and schedule posts across platforms with multi-platform publishing.",
    "tour.step.competitors.title": "Competitor Research",
    "tour.step.competitors.desc": "Spy on competitor ads, see their strategies, and get AI insights to beat them.",
    "plan.gate.title": "This feature is locked",
    "plan.gate.descPrefix": "This feature is available on",
    "plan.gate.descSuffix": "and above. Upgrade to unlock it.",
    "plan.gate.upgrade": "Upgrade plan",
    "plan.lockedCard.desc": "Available on {plan} and above. Upgrade to unlock it.",
    "plan.lockedCard.upgrade": "Upgrade",
    "plan.upgradePrompt.desc": "This feature is available on {plan} and above. Upgrade your plan to unlock it.",
    "plan.upgradePrompt.notNow": "Not now",
    "plan.upgradePrompt.upgrade": "Upgrade plan",
    "plan.feature.abTesting": "A/B Testing",
    "plan.feature.budgetOptimizer": "AI Budget Optimizer",
    "plan.limit.campaignsTitle": "Campaign limit reached",
    "plan.limit.campaignsDesc": "Your plan includes up to {limit} campaigns. Upgrade to create more.",
    "plan.limit.campaignsReached": "You've used {count} of {limit} campaigns on your plan.",
    "plan.limit.creativesTitle": "Creative limit reached",
    "plan.limit.creativesDesc": "Your plan includes up to {limit} creatives. Upgrade to create more.",
    "approval.back": "Back to campaign",
    "approval.copyDesc": "AI-generated headline, description, and call-to-action.",
    "approval.creativeDesc": "Preview of the visual asset.",
    "approval.creativePreview": "Creative preview",
    "approval.cancel": "Cancel",
    "approval.submit": "Submit",
    "approval.loading": "Loading…",
    "approval.backToCampaigns": "Back to campaigns",
    "approval.field.locations": "Locations",
    "approval.field.age": "Age",
    "approval.field.gender": "Gender",
    "approval.field.interests": "Interests",
    "approval.field.language": "Language",
    "approval.field.platform": "Platform",
    "approval.field.all": "All",
    "approval.field.english": "English",
    "approval.headline": "Discover",
    "approval.body": "Tap below to see why customers are loving our latest. Limited-time launch offer — try it today.",
    "approval.cta.learn": "Learn More",
    "share.copied": "Share link copied — expires {date}",
    "share.expires": "Expires {date}",
    "invoices.exportCsv": "Export CSV",
    "invoices.csvNoRows": "No invoices match your filters.",
    "competitors.invalidUrl": "Please enter a valid brand name or full website URL.",
    "competitors.tooLong": "Competitor name is too long (max 80 characters).",
    "wizard.error.platforms": "Select at least one platform to continue.",
    "wizard.error.businessName": "Please enter your business name (at least 2 characters).",
    "wizard.error.industry": "Please choose an industry.",
    "wizard.error.promoting": "Briefly describe what you're promoting.",
    "wizard.error.goal": "Pick a campaign goal to continue.",
    "wizard.error.creative": "Choose how you'd like to create the ad.",
    "wizard.error.creativeUpload": "Upload an image or video to continue.",
    "wizard.error.creativeAi": "Pick an AI creative type to continue.",
    "share.expired.title": "This share link has expired",
    "share.expired.body": "Shared dashboard links are valid for 7 days. Ask the dashboard owner to send you a new one.",
    "share.expired.requestNew": "Request a new link",
    "share.expired.goHome": "Go to homepage",
    "share.request.emailLabel": "Your email",
    "share.request.emailPlaceholder": "you@example.com",
    "share.request.confirmEmailLabel": "Confirm email",
    "share.request.confirmEmailPlaceholder": "Re-enter your email",
    "share.request.note": "Optional message",
    "share.request.notePlaceholder": "Anything you'd like to mention…",
    "share.request.submit": "Send request",
    "share.request.sending": "Sending…",
    "share.request.success": "Request sent! The dashboard owner will be in touch.",
    "share.request.invalidEmail": "Please enter a valid email address.",
    "share.request.emailMismatch": "Emails don't match. Please double-check both fields.",
    "share.request.duplicate": "We already received a request from this email for this link. Hang tight — the owner will be in touch.",
    "share.request.error": "Couldn't send your request. Please try again.",
    "share.request.successTitle": "Request received",
    "share.request.successBody": "Thanks! We've sent your request to the dashboard owner. You should hear back within 24 hours at",
    "share.request.successFootnote": "If you don't hear back, check your spam folder or try again tomorrow.",
    "share.request.rateLimited": "You've reached the limit of 3 requests in 24 hours for this email. Please try again tomorrow.",
    "share.request.rateLimitedCountdown": "You can submit again in",
    "share.request.captchaLabel": "Quick check: what is {q}?",
    "share.request.captchaPlaceholder": "Answer",
    "share.request.captchaError": "That answer doesn't look right. Please try again.",
    "share.request.botBlocked": "Submission blocked. Please reload the page and try again.",
    "share.request.goHome": "Go to homepage",
    "share.request.retryReady": "You can submit your request again now.",
    "share.request.captchaHelp": "Solve the simple math problem to prove you're human.",
    "share.request.formAriaLabel": "Request a new shared dashboard link",
    "assistant.title": "AdKavi Assistant",
    "assistant.openAria": "Open AdKavi Assistant",
    "assistant.closeAria": "Close assistant",
    "assistant.welcome": "Hi! I'm your AdKavi Assistant. Ask me anything about using the platform.",
    "assistant.placeholder": "Ask about AdKavi...",
    "assistant.support": "Contact Support",
    "assistant.errorGeneric": "Sorry, I couldn't generate a response. Please contact support@adkavi.com if this keeps happening.",
    "assistant.errorNetwork": "Something went wrong reaching the assistant. Please try again or contact support@adkavi.com.",
    "assistant.suggestionsTitle": "Try asking:",
    "assistant.suggestion.campaign": "How do I create a campaign?",
    "assistant.suggestion.adAccount": "How do I connect my ad account?",
    "assistant.suggestion.voice": "How do I clone my voice?",
    "assistant.suggestion.upgrade": "How do I upgrade my plan?",

    // Navbar sections
    "nav.section.hero": "Home",
    "nav.section.features": "Features",
    "nav.section.howItWorks": "How It Works",
    "nav.section.pricing": "Pricing",
    "nav.section.testimonials": "Testimonials",
    "nav.menu.open": "Open menu",
    "nav.menu.close": "Close menu",

    // Hero
    "hero.badge": "The All-in-One AI Marketing Platform",
    "hero.title.1": "Create. Launch. Scale.",
    "hero.title.2": "All With AI.",
    "hero.subtitle": "Everything you need to run your marketing — ads, videos, voice, content, and analytics — from one dashboard.",
    "hero.subtitleEmphasis": "",
    "hero.cta.start": "Join the Waitlist",
    "hero.cta.login": "See How It Works",
    "hero.trial": "First 100 total founding members lock in their chosen plan · 14-day free trial · Cancel anytime",
    "hero.poweredBy": "Powered by",
    "hero.static": "CREATE.",
    "hero.cycling": "LAUNCH.|SCALE.|WIN.|GROW.",
    "hero.allWithAi": "ALL WITH AI.",
    "hero.waitlistNote": "First 100 total founding members lock in their chosen plan — 14-day free trial · Cancel anytime",

    // Features
    "features.eyebrow": "What AdKavi Does",
    "features.title.1": "All Your Marketing.",
    "features.title.2": "One Platform.",
    "features.ads.title": "Run Meta, TikTok, Google & YouTube Ads",
    "features.ads.desc": "Launch and manage ad campaigns directly from AdKavi without jumping between ad platforms.",
    "features.video.title": "AI Avatar Videos",
    "features.video.desc": "Generate professional videos of yourself powered by HeyGen without ever recording.",
    "features.voice.title": "Voice Cloning",
    "features.voice.desc": "Clone your voice with ElevenLabs and narrate videos automatically.",
    "features.social.title": "Social Posting & Scheduling",
    "features.social.desc": "Design carousels, schedule posts, and auto-publish to Facebook and Instagram.",

    // How It Works
    "how.title.1": "Launch Your",
    "how.title.2": "First AI Campaign",
    "how.title.3": "in",
    "how.title.4": "6 Steps",
    "how.step": "Step",
    "how.s1.title": "Connect Your Accounts",
    "how.s1.desc": "Link your Instagram, Facebook, TikTok, Google Search & Display, or YouTube account. Set your daily or total ad budget — you can start with as little as $5/day. Run the same ad across multiple platforms at once.",
    "how.s2.title": "Tell Us About Your Business",
    "how.s2.desc": "Enter your business name, industry, and what you're promoting. AdKavi AI helps you find your ideal audience.",
    "how.s3.title": "Pick Your Goal",
    "how.s3.desc": "Choose your objective: more leads, sales, followers, or WhatsApp messages.",
    "how.s4.title": "Create Your Ad Creative",
    "how.s4.desc": "Two ways to make your creative — upload what you have or let AI build it for you.",
    "how.s4.optionA": "Option A",
    "how.s4.optionAText": "Upload your own image or video.",
    "how.s4.optionB": "Option B",
    "how.s4.optionBText": "Create with AI — design a flyer or post, generate an AI avatar video of yourself with voice clone (no recording needed), or record yourself and let AI enhance it.",
    "how.s5.title": "AI Builds Your Campaign",
    "how.s5.desc": "AdKavi AI writes your ads for you — the headline, description, and call to action. It also selects your audience and builds your targeting strategy based on your creative.",
    "how.s6.title": "Review & Launch",
    "how.s6.desc": "Review your campaign and approve your creative. Then choose how to go live: Launch Now — go live across every platform simultaneously with one click. Schedule It — pick the exact date and time you want your campaign to start. AdKavi monitors and optimizes everything 24/7 automatically from the moment it launches.",

    // Pricing
    "pricing.title.1": "Simple Transparent Pricing.",
    "pricing.title.2": "No Surprises.",
    "pricing.subtitle": "All plans include a 14-day free trial. Cancel anytime.",
    "pricing.popular": "Most Popular",
    "pricing.startTrial": "Join Waitlist — Lock In This Price",
    "pricing.perMonth": "/mo",
    "pricing.gate.title": "Who are you building for?",
    "pricing.gate.creator.title": "I'm a Business or Creator",
    "pricing.gate.creator.desc": "Single account plans for individuals and small businesses.",
    "pricing.gate.agency.title": "I'm an Agency",
    "pricing.gate.agency.desc": "Manage up to 10 client accounts from one dashboard.",
    "pricing.forAgencies": "For Agencies",
    "pricing.forCreators": "For Businesses & Creators",
    "pricing.adSpendNote": "Ad spend goes directly to Meta, TikTok, and Google — AdKavi never holds your money. You stay in full control of your budget.",
    "pricing.viewIndividual": "Looking for a personal plan? View individual plans →",
    "pricing.viewAgency": "Are you a marketing agency? View agency plans →",

    // Testimonials
    "testimonials.title.1": "Loved by",
    "testimonials.title.2": "Real Businesses",
    "testimonials.t1.role": "Credit repair business owner",
    "testimonials.t1.quote": "AdKavi replaced my entire marketing team. I went from 5 leads a week to 30 leads a week in the first month.",
    "testimonials.t2.role": "Real estate agent",
    "testimonials.t2.quote": "I was spending $3,000 a month on a marketing agency. Now I pay $699 and get better results.",
    "testimonials.t3.role": "Fitness coach",
    "testimonials.t3.quote": "The AI video feature is insane. I generated 10 professional videos in one afternoon without recording anything.",

    // Footer
    "footer.tagline": "Powered by the world's most advanced AI.",
    "footer.features": "Features",
    "footer.pricing": "Pricing",
    "footer.howItWorks": "How It Works",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.cookies": "Cookie Policy",
    "footer.deleteData": "Delete My Data",
    "footer.deleteDataStatus": "Check Deletion Status",
    "delete.status.title": "Check Deletion Request Status",
    "delete.status.intro": "Enter the email you used and the request ID we showed you when you submitted your data deletion request.",
    "delete.status.email": "Email address",
    "delete.status.requestId": "Request ID",
    "delete.status.cta": "Check status",
    "delete.status.loading": "Looking up…",
    "delete.status.notFound": "We couldn't find a request matching that email and ID. Double-check both, or email support@adkavi.com for help.",
    "delete.status.back": "← Back to deletion request",
    "delete.status.submitted": "Submitted",
    "delete.status.processed": "Processed",
    "delete.status.expectedBy": "Expected by",
    "delete.status.request": "Request",
    "delete.status.statusPending": "Pending",
    "delete.status.statusCompleted": "Completed",
    "delete.status.statusRejected": "Rejected",
    "delete.status.helpPending": "We process deletion requests within 30 days, in line with GDPR, CCPA, and Meta Platform Terms.",
    "delete.status.helpCompleted": "Your data has been deleted. If you have questions, email support@adkavi.com.",
    "delete.status.helpRejected": "We could not process this request. Email support@adkavi.com for details.",
    "delete.status.shareable": "Bookmark this link to come back to this status anytime:",
    "footer.contact": "Contact",
    "footer.copyright": "© 2026 AdKavi. All rights reserved.",

    // Auth
    "auth.continueGoogle": "Continue with Google",
    "auth.or": "or",
    "auth.tab.signin": "Log In",
    "auth.tab.signup": "Sign Up",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.confirmPassword": "Confirm Password",
    "auth.name": "Name",
    "auth.forgot": "Forgot password?",
    "auth.signingIn": "Signing in…",
    "auth.signIn": "Log In",
    "auth.signUp": "Sign Up",
    "auth.creating": "Creating account…",
    "auth.startTrial": "Start Free Trial",
    "auth.signupChoosePlan": "Sign Up & Choose Plan",
    "auth.passwordHint": "At least 8 characters. Checked against known breaches.",
    "auth.showPassword": "Show password",
    "auth.hidePassword": "Hide password",
    "auth.googleFailed": "Google sign-in failed",
    "auth.passwordMismatch": "Passwords do not match.",
    "auth.welcomeBack": "Welcome back!",
    "auth.returning.title": "Welcome back!",
    "auth.returning.desc": "As a returning user, please select a plan to continue. The free trial is only available once per customer.",
    "auth.toast.checkInbox": "Check your inbox to verify your email.",
    "auth.toast.verifyFirst": "Please verify your email first.",
    "auth.toast.invalidCreds": "Invalid email or password.",
    "auth.toast.exists": "Account already exists. Try logging in.",
    "auth.demoHint": "Explore the full dashboard with sample data — no account required.",
    // Zod validation messages
    "auth.zod.nameRequired": "Name is required",
    "auth.zod.nameTooLong": "Name must be under 100 characters",
    "auth.zod.emailInvalid": "Invalid email address",
    "auth.zod.emailTooLong": "Email must be under 255 characters",
    "auth.zod.passwordTooShort": "Password must be at least 8 characters",
    "auth.zod.passwordTooLong": "Password must be under 72 characters",
    "auth.zod.passwordUppercase": "Password must include an uppercase letter",
    "auth.zod.passwordLowercase": "Password must include a lowercase letter",
    "auth.zod.passwordNumber": "Password must include a number",
    "auth.zod.passwordRequired": "Password is required",
    "auth.zod.passwordsMismatch": "Passwords do not match",
    "auth.zod.emailValid": "Enter a valid email address",

    // Forgot password
    "forgot.title": "Forgot your password?",
    "forgot.desc": "Enter your email and we'll send you a secure link to reset it.",
    "forgot.send": "Send reset link",
    "forgot.sending": "Sending…",
    "forgot.sentPrefix": "If an account exists for",
    "forgot.sentSuffix": ", a reset link is on its way. Check your inbox and spam folder.",
    "forgot.back": "Back to login",

    // Verify email
    "verify.title": "Verify your email",
    "verify.descPrefix": "We sent a verification link to",
    "verify.descSuffix": ". Click it to activate your account. The link may take a minute to arrive — check your spam folder too.",
    "verify.inbox": "your inbox",
    "verify.resend": "Resend verification email",
    "verify.sending": "Sending…",
    "verify.resendIn": "Resend in {s}s",
    "verify.back": "Back to login",
    "verify.toastVerified": "Email verified!",
    "verify.toastSent": "Verification email sent. Check your inbox.",
    "verify.toastWait": "Please wait a moment before requesting another email.",
    "verify.toastMaybe": "If your email is unverified, a new link is on its way.",
    "verify.placeholder": "you@example.com",

    // Reset password
    "reset.title": "Set a new password",
    "reset.descReady": "Enter your new password below.",
    "reset.descValidating": "Validating your reset link…",
    "reset.newPassword": "New password",
    "reset.confirm": "Confirm password",
    "reset.hint": "Min 8 chars, with uppercase, lowercase, and a number.",
    "reset.update": "Update password",
    "reset.updating": "Updating…",
    "reset.toastDone": "Password updated. You're signed in.",
    "reset.expired": "This reset link has expired or is invalid. Reset links are single-use and expire after 1 hour.",
    "reset.requestNew": "Request a new reset link →",

    // Sidebar
    "sidebar.workspace": "Workspace",
    "sidebar.dashboard": "Dashboard",
    "sidebar.campaigns": "Campaigns",
    "sidebar.content": "Content Calendar",
    "sidebar.aiStudio": "AI Studio",
    "sidebar.creative": "Creative Builder",
    "sidebar.benchmarks": "Benchmarks",
    "sidebar.clients": "Clients",
    "sidebar.settings": "Settings",
    "sidebar.locked": "locked",
    "sidebar.lockedTitle": "is locked",
    "sidebar.lockedDescPrefix": "This feature is available on",
    "sidebar.lockedDescSuffix": "and above. Upgrade to unlock it.",
    "sidebar.notNow": "Not now",
    "sidebar.upgrade": "Upgrade plan",

    // For Creators — AI clone & goals
    "creators.eyebrow": "For Content Creators",
    "creators.title.1": "Your AI clone",
    "creators.title.2": "never sleeps.",
    "creators.subtitle": "1 hour of setup = 30 days of content. Never miss a post. Never run out of ideas. Never burn out.",
    "creators.flow.1.title": "Upload your face & voice — once",
    "creators.flow.1.desc": "A short video and a voice sample. That's it.",
    "creators.flow.2.title": "AI clones you instantly",
    "creators.flow.2.desc": "Your digital twin takes over the work.",
    "creators.flow.3.title": "30 days of content in minutes",
    "creators.flow.3.desc": "Videos, carousels, captions — all scheduled.",
    "creators.pickGoal": "Pick a goal — AdKavi does the rest",
    "goal.followers.label": "Grow Followers",
    "goal.followers.desc": "Run targeted campaigns on Instagram and Facebook to attract real followers in your niche.",
    "goal.views.label": "Get More Views",
    "goal.views.desc": "Maximize video views on Instagram, Facebook, TikTok, and YouTube.",
    "goal.leads.label": "Get More Leads",
    "goal.leads.desc": "Collect names and contact info directly inside the ad — no website needed.",
    "goal.sales.label": "Drive More Sales",
    "goal.sales.desc": "Send people straight to your product or service page ready to buy.",
    "goal.whatsapp.label": "Get WhatsApp Messages",
    "goal.whatsapp.desc": "People tap your ad and it opens a WhatsApp chat with your business instantly.",
    "goal.event.label": "Promote an Event",
    "goal.event.desc": "Run awareness and RSVP ads targeting people near your event or in your niche.",
    "goal.traffic.label": "Drive Website Traffic",
    "goal.traffic.desc": "Send people directly to any page on your website.",
    "goal.calls.label": "Get Phone Calls",
    "goal.calls.desc": "People tap your ad and call your business directly — no website needed.",

    // Vision
    "vision.eyebrow": "Our Vision",
    "vision.title.1": "Every business owner and creator deserves a",
    "vision.title.2": "Full Marketing Platform",
    "vision.title.3": "— powered by AI.",
    "vision.body": "AdKavi helps you launch, monitor, and optimize your campaigns across every platform — so you can grow your business and build your brand from one dashboard.",
    "vision.grow": "",
    "vision.allInOne": "",

    // Problem
    "problem.eyebrow": "Sound familiar?",
    "problem.title.1": "Growing online shouldn't",
    "problem.title.2": "feel this hard.",
    "problem.subtitle": "Every business owner and creator hits the same walls — burning cash, looking amateur, and watching competitors win. AdKavi was built to end all of it.",
    "problem.p1.title": "Burning $2K–$10K/mo on agencies",
    "problem.p1.desc": "Slow turnarounds, mediocre creative, no transparency — and you're still the one writing the brief.",
    "problem.p2.title": "Hiring a team you can't afford",
    "problem.p2.desc": "A media buyer, editor, copywriter and strategist costs more than most businesses make in a year.",
    "problem.p3.title": "Hating being on camera",
    "problem.p3.desc": "You know video sells, but recording yourself feels awkward, time-consuming, and never looks pro.",
    "problem.p4.title": "Posting and getting ignored",
    "problem.p4.desc": "Hours of work, 47 views, zero leads. The algorithm doesn't reward effort — it rewards systems.",
    "problem.p5.title": "Burnout from posting daily",
    "problem.p5.desc": "Being your own creator, editor, marketer and CEO is unsustainable. Something always slips.",
    "problem.p6.title": "Juggling 6 different tools",
    "problem.p6.desc": "Canva, CapCut, Meta Ads, ChatGPT, a scheduler, a CRM — switching tabs all day, paying for each.",
    "problem.p7.title": "Bad creative kills good budget",
    "problem.p7.desc": "You can have the best product, but ugly ads and weak hooks waste every dollar you spend.",
    "problem.p8.title": "No clue who to target",
    "problem.p8.desc": "Audiences, lookalikes, interests, placements — the platforms are built to confuse you and overspend.",
    "problem.p9.title": "Ad costs going up, results going down",
    "problem.p9.desc": "CPMs keep rising. Without constant testing and fresh creative, your ROAS quietly collapses.",
    "problem.p10.title": "Scared of looking unprofessional",
    "problem.p10.desc": "One typo, one cringe video, one bad ad — and you lose trust with the exact customers you need.",
    "problem.p11.title": "Zero time, too many platforms",
    "problem.p11.desc": "Meta, TikTok, Google, YouTube, Instagram — you can't be everywhere, but you have to be.",
    "problem.p12.title": "Stuck invisible while competitors scale",
    "problem.p12.desc": "They're not smarter — they just have a system. Without one, you stay the best-kept secret in your market.",
    "problem.outro.1": "You don't need a bigger budget or another tool.",
    "problem.outro.2": "You need a system that just works.",

    // WhoFor
    "whofor.title.1": "Built for",
    "whofor.title.2": "Every Industry",
    "whofor.title.3": "That Wants to Grow",
    "whofor.subtitle": "From solo creators to agencies — if you need more clients, more views, or more sales, AdKavi was made for you.",
    "whofor.creators.label": "Content Creators",
    "whofor.creators.desc": "Go viral with AI-cloned videos & carousels",
    "whofor.credit.label": "Credit Repair Companies",
    "whofor.credit.desc": "Attract clients who need a fresh start",
    "whofor.realestate.label": "Real Estate Agents",
    "whofor.realestate.desc": "List more. Close faster. Automate your ads.",
    "whofor.medspa.label": "Medical Spas & Aesthetics",
    "whofor.medspa.desc": "Fill your books with high-value clients",
    "whofor.lawyers.label": "Lawyers & Law Firms",
    "whofor.lawyers.desc": "Get qualified leads without a big agency",
    "whofor.finance.label": "Funding & Finance Companies",
    "whofor.finance.desc": "Target business owners who need capital",
    "whofor.coaches.label": "Coaches & Consultants",
    "whofor.coaches.desc": "Sell your expertise. Scale your brand.",
    "whofor.contractors.label": "Contractors & Brokers",
    "whofor.contractors.desc": "More calls. More jobs. Less effort.",
    "whofor.health.label": "Healthcare & Wellness",
    "whofor.health.desc": "Grow your practice with compliant ads",
    "whofor.any.label": "Any Business Running Ads",
    "whofor.any.desc": "If you sell something, AdKavi grows it",

    // AI Manager
    "ai.eyebrow": "Your AI Campaign Manager Never Sleeps",
    "ai.title": "AdKavi Watches Your Ads So You Don't Have To",
    "ai.subtitle": "Most businesses waste money on ads that stopped working days ago. AdKavi catches it instantly.",
    "ai.card1.label": "Healthy Campaign",
    "ai.card1.text": "Your cost per lead is $12. AdKavi is scaling your best performing ad automatically.",
    "ai.card2.label": "Warning Detected",
    "ai.card2.text": "Your click rate dropped 40% today. AdKavi recommends a new headline — click to review.",
    "ai.card3.label": "Auto-Protected",
    "ai.card3.text": "Campaign paused. You were spending $50/day with 0 leads. AdKavi stopped it and rewrote your ad.",
    "ai.footer.1": "No more waking up to wasted ad spend.",
    "ai.footer.2": "AdKavi has your back 24/7.",

    // Results
    "results.title.1": "What Our Clients",
    "results.title.2": "Achieve",
    "results.s1": "Faster Ad Creation",
    "results.s2": "Cheaper Than Agencies",
    "results.s3": "Better ROI on Ad Spend",
    "results.s4": "AI Working in Background",
    "results.s5": "English & Spanish",

    // Trust / Security
    "trust.badge": "Your Data is Safe With AdKavi",
    "trust.title.1": "Your Business Data is",
    "trust.title.2": "100% Protected",
    "trust.subtitle": "AdKavi is built on enterprise-grade security. Your data is yours — always.",
    "trust.c1.title": "We Never Store Your Password",
    "trust.c1.desc": "You connect your Meta, TikTok, Google & YouTube accounts through official OAuth login — the same secure method used by the world's largest apps. We never see your password.",
    "trust.c2.title": "Your Data Stays Yours",
    "trust.c2.desc": "Your ad data, campaigns, and client information are never sold, shared, or used for any other purpose. What's yours stays yours.",
    "trust.c3.title": "Bank-Level Encryption",
    "trust.c3.desc": "All data is transmitted over HTTPS encryption. API keys and sensitive information are stored using enterprise-grade encryption — never in plain text.",
    "trust.c4.title": "Complete Data Isolation",
    "trust.c4.desc": "Every client account is fully isolated. No business can ever see another's campaigns, leads, or data. Built with row-level security.",
    "trust.c5.title": "Delete Anytime",
    "trust.c5.desc": "You can disconnect your ad accounts and request full data deletion at any time. No questions asked. You are always in control.",
    "trust.c6.title": "We Never Sell Your Data",
    "trust.c6.desc": "AdKavi never sells your business data to advertisers, data brokers, or third parties. Ever.",
    "trust.bar.1": "Secured by Enterprise Cloud",
    "trust.bar.2": "Encrypted with HTTPS",
    "trust.bar.3": "OAuth Protected",
    "trust.bar.4": "Privacy Policy Included",
    "trust.bar.5": "Complete Data Isolation",

    // Features Explained
    "fx.title.1": "Everything Included",
    "fx.title.2": "In Your Plan",
    "fx.subtitle": "Every tool, feature, and AI workflow you unlock with AdKavi — see exactly what you're getting before you choose a plan below.",
    "fx.cat.adCreation": "AD CREATION",
    "fx.cat.campaign": "CAMPAIGN MANAGEMENT",
    "fx.cat.audience": "AUDIENCE TARGETING",
    "fx.cat.goals": "CAMPAIGN GOALS",
    "fx.cat.analytics": "ANALYTICS",
    "fx.cat.content": "CONTENT",
    "fx.cat.competitor": "COMPETITOR RESEARCH",
    "fx.cat.video": "VIDEO & VOICE",
    "fx.video.note": "Included in Dominate and Agency Dominate plans",
    // Ad creation
    "fx.adCreation.1.name": "AI Ad Copy Writer",
    "fx.adCreation.1.desc": "Describe your business, AI writes your headline, text, and call to action in seconds.",
    "fx.adCreation.2.name": "Ad Creative Builder",
    "fx.adCreation.2.desc": "Drag and drop tool to design your ad image directly inside AdKavi.",
    "fx.adCreation.3.name": "AI Carousel Ads",
    "fx.adCreation.3.desc": "AI builds a swipeable multi-image ad automatically, ready to publish.",
    "fx.adCreation.4.name": "AI Image Ads",
    "fx.adCreation.4.desc": "AI generates a ready-to-publish photo ad for your campaign.",
    // Campaign management
    "fx.campaign.1.name": "Campaign Health Monitor",
    "fx.campaign.1.desc": "See which ads are healthy, which need attention, and which should be paused.",
    "fx.campaign.2.name": "Auto-Pause Protection",
    "fx.campaign.2.desc": "If an ad wastes money for 48 hours with no results, the system pauses it automatically.",
    "fx.campaign.3.name": "AI Budget Optimizer",
    "fx.campaign.3.desc": "Automatically moves your money toward the ads that are working.",
    "fx.campaign.4.name": "A/B Testing",
    "fx.campaign.4.desc": "Runs 2 versions of your ad at the same time, keeps the winner, stops the loser.",
    "fx.campaign.5.name": "Multi-Platform Launch",
    "fx.campaign.5.desc": "Launch to Meta, TikTok, Google Search + Display & YouTube at the same time with one click.",
    "fx.campaign.6.name": "Approval Workflow",
    "fx.campaign.6.desc": "Review and approve every ad before it goes live. You're always in control.",
    "fx.campaign.7.name": "Launch Now or Schedule",
    "fx.campaign.7.desc": "Choose to publish instantly or pick the perfect date and time.",
    // Audience targeting
    "fx.audience.1.name": "Custom Audiences",
    "fx.audience.1.desc": "Target people who already follow you or visited your website.",
    "fx.audience.2.name": "Retargeting",
    "fx.audience.2.desc": "Show ads to people who visited your page but did not take action yet.",
    "fx.audience.3.name": "Lookalike Audiences",
    "fx.audience.3.desc": "Find brand new people who look exactly like your best customers.",
    // Campaign goals
    "fx.goals.1.name": "Lead Forms",
    "fx.goals.1.desc": "Person clicks your ad, a form pops up inside Facebook or Instagram, they submit their name and email without ever leaving the app.",
    "fx.goals.2.name": "WhatsApp Campaigns",
    "fx.goals.2.desc": "Your ad button sends people straight to your WhatsApp chat instantly.",
    "fx.goals.3.name": "Sales",
    "fx.goals.3.desc": "Send people straight to your product or service page ready to buy.",
    "fx.goals.4.name": "Phone Calls",
    "fx.goals.4.desc": "People tap your ad and call your business directly — no website needed.",
    "fx.goals.5.name": "Website Traffic",
    "fx.goals.5.desc": "Send people directly to any page on your website.",
    "fx.goals.6.name": "Views",
    "fx.goals.6.desc": "Maximize video views on Instagram, Facebook, TikTok, and YouTube.",
    "fx.goals.7.name": "Grow Followers",
    "fx.goals.7.desc": "Run targeted campaigns on Instagram, Facebook, and TikTok to attract real followers in your niche.",
    "fx.goals.8.name": "Promote an Event",
    "fx.goals.8.desc": "Run awareness and RSVP ads targeting people near your event or in your niche.",
    // Analytics
    "fx.analytics.1.name": "All Platforms One Dashboard",
    "fx.analytics.1.desc": "See all your results from Meta, Facebook, TikTok, Google Search + Display & YouTube in one place. No more jumping between apps.",
    "fx.analytics.2.name": "Industry Benchmarks",
    "fx.analytics.2.desc": "AdKavi tells you if your cost per result is good or bad for your specific industry.",
    "fx.analytics.3.name": "Cost Alerts",
    "fx.analytics.3.desc": "Get notified the moment you are spending too much per result so you never waste money.",
    "fx.analytics.4.name": "Ad Spend Tracker",
    "fx.analytics.4.desc": "See exactly how much you spent vs how much you made.",
    "fx.analytics.5.name": "Best Performing Ad",
    "fx.analytics.5.desc": "AdKavi shows which ad is making the most money.",
    "fx.analytics.6.name": "Platform Comparison",
    "fx.analytics.6.desc": "See which works best for you: Meta, TikTok, Google Search + Display, or YouTube.",
    // Content
    "fx.content.1.name": "Content Calendar",
    "fx.content.1.desc": "See all your scheduled posts in a monthly calendar view across every platform.",
    "fx.content.2.name": "AI Caption Writer",
    "fx.content.2.desc": "AI writes your Instagram and TikTok captions with hashtags automatically.",
    "fx.content.3.name": "Multi-Platform Publishing",
    "fx.content.3.desc": "Create one post, publish to Meta, TikTok, Google & YouTube all at the same time.",
    // Competitor research
    "fx.competitor.1.name": "Competitor Ads",
    "fx.competitor.1.desc": "See the exact ads your competitors are currently running. Use them as inspiration to create better ads faster.",
    "fx.competitor.2.name": "Ad Duration Tracker",
    "fx.competitor.2.desc": "See how long a competitor's ad has been running. Longer running = it's working.",
    "fx.competitor.3.name": "Platform Spy",
    "fx.competitor.3.desc": "See which platforms your competitors advertise on: Meta, TikTok, Google Search + Display & YouTube.",
    "fx.competitor.4.name": "AI Insight",
    "fx.competitor.4.desc": "AdKavi analyzes what's working in their ads and tells you exactly how to beat them.",
    // Video & voice
    "fx.video.1.name": "Voice Cloning",
    "fx.video.1.desc": "Record yourself for 30 seconds. AI duplicates your exact voice forever. Every voiceover sounds like you.",
    "fx.video.2.name": "AI Voiceovers",
    "fx.video.2.desc": "Add your cloned voice to any video automatically. No recording needed.",
    "fx.video.3.name": "Translate + Dub to Spanish",
    "fx.video.3.desc": "Upload your English video, AdKavi replaces the audio with a perfect Spanish version automatically.",
    "fx.video.4.name": "Personal AI Avatar",
    "fx.video.4.desc": "Upload a 2-minute video of yourself. AI clones your face. You never have to film again.",
    "fx.video.5.name": "AI Avatar Videos",
    "fx.video.5.desc": "Type a script, your AI self appears on camera saying it. Professional video ads in minutes.",
    "fx.video.6.name": "AI Video Script Writer",
    "fx.video.6.desc": "AI writes exactly what your avatar will say based on your campaign goal.",
    "fx.video.7.name": "Spanish Lip Sync",
    "fx.video.7.desc": "Your AI avatar's mouth moves perfectly in sync with Spanish words. Looks 100% natural on camera.",
    "fx.video.8.name": "Direct to Campaign",
    "fx.video.8.desc": "Finished video publishes straight into your ad campaign. No downloading, no uploading, no editing needed.",

    // Affiliate
    "affiliate.title.1": "Earn With",
    "affiliate.title.2": "AdKavi",
    "affiliate.subtitle": "Love AdKavi? Share it and get paid. Earn 10% of your referral's first month payment — no limits, no caps.",
    "affiliate.c1.title": "Easy Sharing",
    "affiliate.c1.desc": "Get your unique link in seconds.",
    "affiliate.c2.title": "Instant Tracking",
    "affiliate.c2.desc": "See every referral in your dashboard.",
    "affiliate.c3.title": "Get Paid",
    "affiliate.c3.desc": "Receive your commission automatically.",
    "affiliate.cta": "Join Affiliate Program",

    // Pricing plan checklist (raw strings → translations)
    "plan.cat.AD CREATION": "AD CREATION",
    "plan.cat.CAMPAIGN MANAGEMENT": "CAMPAIGN MANAGEMENT",
    "plan.cat.AUDIENCE TARGETING": "AUDIENCE TARGETING",
    "plan.cat.CAMPAIGN GOALS": "CAMPAIGN GOALS",
    "plan.cat.ANALYTICS": "ANALYTICS",
    "plan.cat.CONTENT": "CONTENT",
    "plan.cat.COMPETITOR RESEARCH": "COMPETITOR RESEARCH",
    "plan.cat.VIDEO & VOICE": "VIDEO & VOICE",
    "plan.meta.Up to 15 campaigns": "Up to 15 campaigns",
    "plan.meta.Unlimited campaigns": "Unlimited campaigns",
    "plan.meta.14-day free trial — cancel anytime": "14-day free trial — cancel anytime",
    "plan.meta.Email support": "Email support",
    "plan.meta.Priority email support": "Priority email support",
    "plan.meta.30 AI videos included every month": "30 AI videos included every month",
    "plan.meta.Early access to all new features": "Early access to all new features",
    "plan.meta.1 team member": "1 team member",
    "plan.meta.Priority chat support": "Priority chat support",
    "plan.meta.Up to 15 campaigns per client": "Up to 15 campaigns per client",
    "plan.meta.Up to 2 team members": "Up to 2 team members",
    "plan.meta.14-day free trial — 1 client included, cancel anytime": "14-day free trial — 1 client included, cancel anytime",
    "plan.meta.Dedicated support": "Dedicated support",
    "plan.meta.Unlimited campaigns per client": "Unlimited campaigns per client",
    "plan.meta.Up to 3 team members": "Up to 3 team members",
    "plan.meta.Priority dedicated support": "Priority dedicated support",
    "plan.meta.30 AI videos per month per client": "30 AI videos per month per client",
    "plan.meta.Up to 5 team members": "Up to 5 team members",

    // Dashboard
    "dash.title": "Dashboard",
    "dash.live": "Live",
    "dash.connecting": "Connecting…",
    "dash.metaConnected": "Connected to Meta account: {account}",
    "dash.connectMeta": "Connect Meta",
    "dash.newAiCampaign": "New AI campaign",
    "dash.dataPoints": "{shown} of {total} data point{plural}",
    "dash.range.7": "Last 7 days",
    "dash.range.30": "Last 30 days",
    "dash.range.90": "Last 90 days",
    "dash.range.custom": "Custom range",
    "dash.range.all": "All time",
    "dash.platform.all": "All platforms",
    "dash.allCampaigns": "All campaigns",
    "dash.pickDateRange": "Pick a date range",
    "dash.useBrowserCurrency": "Use my browser currency",
    "dash.useBrowserCurrencyTitle": "Set currency from your browser locale",
    "dash.clear": "Clear",
    "dash.exportCsv": "Export CSV",
    "dash.exportCsvTitle": "Download current view as CSV",
    "dash.share": "Share",
    "dash.shareTitle": "Copy a shareable link to this dashboard view (expires in 7 days)",
    "dash.shareCopied": "Share link copied to clipboard",
    "dash.shareExpires": "Expires {date}",
    "dash.shareFallback": "Share link",
    "dash.metric.spend": "Spend",
    "dash.metric.reach": "Reach",
    "dash.metric.clicks": "Clicks",
    "dash.metric.leads": "Leads",
    "dash.metric.conversions": "Conversions",
    "dash.metric.cpl": "CPL",
    "dash.metric.roas": "ROAS",
    "dash.metric.revenue": "Revenue",
    "dash.metric.impressions": "Impressions",
    "dash.metric.ctr": "CTR",
    "dash.metric.cpc": "CPC",
    "dash.metric.convRate": "Conv. rate",
    "dash.empty.title": "Waiting for your first data point",
    "dash.empty.desc": "This dashboard updates the moment metrics arrive. Connect your ad source — the numbers will populate live.",
    "dash.recent": "Recent activity",
    "dash.sampleData": "Sample data",
    "dash.recentLast": "Last {n}",
    "dash.col.when": "When",
    "dash.col.spend": "Spend",
    "dash.col.reach": "Reach",
    "dash.col.clicks": "Clicks",
    "dash.col.leads": "Leads",
    "dash.col.conv": "Conv.",
    "dash.col.revenue": "Revenue",
    "dash.chart.spendRevenue": "Spend vs. Revenue",
    "dash.chart.spendRevenueSub": "Daily totals",
    "dash.chart.funnel": "Clicks, Leads & Conversions",
    "dash.chart.funnelSub": "Funnel over time",
    "dash.chart.roas": "ROAS",
    "dash.chart.roasSub": "Return on ad spend (×)",
    "dash.chart.reach": "Reach",
    "dash.chart.reachSub": "People reached per day",
    "dash.chart.download": "Download chart",
    "dash.chart.downloadPng": "Download as PNG",
    "dash.chart.downloadSvg": "Download as SVG",
    "dash.chart.exportFailed": "Export failed",
    "dash.connect.prompt": "Connect an ad account",
    "dash.connect.desc": "to see real campaign data here. You're viewing sample data.",
    "dash.connect.btn": "Connect Account",
    "dash.autoPause.title": "Campaign auto-paused",
    "dash.autoPause.viewCampaigns": "View campaigns",
    "dash.bestAd.title": "Best Performing Ad",
    "dash.bestAd.spent": "spent",
    "dash.bestAd.revenue": "revenue",
    "dash.spendToday.title": "Spend Today",
    "dash.benchmark.title": "vs Industry Benchmark",
    "dash.benchmark.yours": "Yours",
    "dash.benchmark.industry": "Industry avg",
    "dash.benchmark.viewAll": "View all benchmarks",
    // Platform breakdown
    "platform.title": "By platform",
    "platform.subtitle": "Per-channel performance across Meta, TikTok, Google, and YouTube.",
    "platform.sample": "Sample data · live once connected",
    "platform.mock": "Mock",
    "platform.impr": "Impr.",
    "platform.clicks": "Clicks",
    "platform.spend": "Spend",
    "platform.roas": "ROAS",
    "platform.ctr": "CTR",
    "platform.cpc": "CPC",
    "platform.conv": "Conv.",
    "platform.spendShare": "Spend share",
    "platform.allPlatforms": "All platforms:",
    "platform.revenue": "Revenue",
    // Campaign Health Widget
    "health.title": "Campaign Health",
    "health.subtitle": "AdKavi AI monitors your campaigns every 24h and recommends fixes when results slow down.",
    "health.rescan": "Re-scan",
    "health.status.healthy": "Healthy",
    "health.status.warning": "Warning",
    "health.status.alert": "Alert",
    "health.pausedOne": "We paused {n} campaign to protect your budget.",
    "health.pausedMany": "We paused {n} campaigns to protect your budget.",
    "health.reviewToRelaunch": "Review recommendations to relaunch.",
    "health.alertSpent": "has spent {amount} with low results.",
    "health.alertHas3Recs": "AdKavi AI has 3 recommendations to fix it.",
    "health.empty": "No campaigns to monitor yet. Create one to start tracking health.",
    "health.review": "Review",
    "health.recsTitle": "AdKavi AI Recommendations",
    "health.close": "Close",
    "health.generating": "Generating recommendations…",
    "health.autoFix": "Auto-Fix Now",
    "health.reviewFirst": "Review Changes First",
    "health.toast.applied": "Auto-Fix applied — monitoring the new variant.",
    "health.fallbackName": "Your campaign",
    "health.campaign": "Campaign",
    // Wizard toasts
    "wizard.toast.platformLocked": "This platform requires a higher plan.",
    "wizard.toast.upgrade": "Upgrade",
    "wizard.toast.trialLimit.title": "Trial limit reached",
    "wizard.toast.trialLimit.desc": "Switch client or wait until day 15 to launch more campaigns.",
    "wizard.toast.launched": "Campaign launched!",
    "wizard.toast.generationFailed": "Generation failed. Please try again.",
    "wizard.toast.translationFailed": "Translation failed. Please try again.",
    "wizard.toast.launchFailed": "Launch failed. Please try again.",
    "wizard.toast.saveFailed": "Could not save campaign. Please try again.",
    // Campaign list — no-account dialog
    "campaignsList.connect.title": "Connect an ad account first",
    "campaignsList.connect.desc": "To launch campaigns you need to connect at least one ad account — Meta, Google, or TikTok.",
    "campaignsList.connect.cancel": "Cancel",
    "campaignsList.connect.btn": "Connect Account",
    // Delete data page
    "deleteData.toast.invalidInput": "Invalid input",
    "deleteData.toast.alreadySubmitted": "We've already received a recent request for this email. We process requests within 30 days — please check status or email support@adkavi.com if you need help.",
    "deleteData.toast.submitFailed": "Could not submit request. Please email support@adkavi.com.",
    "deleteData.toast.idCopied": "Request ID copied",
    "deleteData.toast.copyFailed": "Could not copy",
    // Campaign status badges
    "campaignsList.status.active": "Active",
    "campaignsList.status.paused": "Paused",
    "campaignsList.status.draft": "Draft",
    "campaignsList.status.scheduled": "Scheduled",
    "campaignsList.status.failed": "Failed",
    "campaignsList.status.needs_changes": "Needs changes",
    "campaignsList.status.archived": "Archived",
    // Wizard — navigation
    "wizard.back": "Back",
    "wizard.next": "Next",
    "wizard.backToList": "Back to campaigns",
    // Wizard — step labels
    "wizard.step.accounts": "Accounts",
    "wizard.step.business": "Business",
    "wizard.step.goal": "Goal",
    "wizard.step.budget": "Budget",
    "wizard.step.creative": "Creative",
    "wizard.step.aiBuild": "AI Build",
    "wizard.step.review": "Review",
    // Wizard — post-launch success
    "wizard.success.scheduledTitle": "Campaign scheduled!",
    "wizard.success.liveTitle": "Your campaign is live!",
    "wizard.success.scheduledDesc": "Your campaign will launch on {date}.",
    "wizard.success.liveDesc": "It usually takes 5–30 minutes for Meta to review and activate your ads.",
    "wizard.success.label.name": "Campaign name",
    "wizard.success.label.platform": "Platform",
    "wizard.success.label.budget": "Budget",
    "wizard.success.label.duration": "Duration",
    "wizard.success.label.duration.value": "{n} days",
    "wizard.success.metaCampaignId": "Meta Campaign ID",
    "wizard.success.googleCampaignId": "Google Ads Campaign ID",
    "wizard.success.youtubeCampaignId": "YouTube Campaign ID",
    "wizard.success.tiktokUpsell.title": "Want to run this on TikTok too?",
    "wizard.success.tiktokUpsell.desc": "Reach a younger audience — we already have your copy, creative, and targeting. Takes 2 minutes.",
    "wizard.success.tiktokUpsell.btn": "Create TikTok Version",
    "wizard.success.tiktokUpsell.later": "Maybe later",
    "wizard.success.viewAll": "View All Campaigns",
    "wizard.success.dashboard": "Go to Dashboard",
    // Wizard — header
    "wizard.header.title": "Launch Your AI Campaign",
    "wizard.header.subtitle": "Seven simple steps. Each one unlocks the next.",
    // Wizard — Step 1 (Accounts)
    "wizard.step1.title": "Step 1 — Connect Your Accounts",
    "wizard.step1.desc": "Select the platforms to run this ad on.",
    "wizard.step1.loading": "Loading connections…",
    "wizard.step1.noAccounts": "No accounts connected.",
    "wizard.step1.settingsLink": "Go to Settings › Integrations to connect your platforms.",
    "wizard.step1.settingsLinkPre": "Go to",
    "wizard.step1.settingsLinkPost": "to connect your platforms.",
    "wizard.step1.platformsLabel": "Platforms",
    "wizard.step1.connectBadge": "Connect",
    "wizard.step1.tip": "Tip: run the same ad across multiple platforms at once.",
    "wizard.step1.manageIntegrations": "Manage integrations",
    // Wizard — Step 2 (Business)
    "wizard.step2.title": "Step 2 — Tell Us About Your Business",
    "wizard.step2.desc": "AdKavi AI will use this to find your ideal audience automatically.",
    "wizard.step2.bizName": "Business name",
    "wizard.step2.bizNamePlaceholder": "e.g. Acme Coffee Co.",
    "wizard.step2.industry": "Industry",
    "wizard.step2.industryPlaceholder": "Select your industry",
    "wizard.step2.promoting": "What are you promoting?",
    "wizard.step2.promotingPlaceholder": "e.g. Our new fall espresso blend, $18/bag with free shipping over $40",
    // Wizard — Step 3 (Goal)
    "wizard.step3.title": "Step 3 — Pick Your Goal",
    "wizard.step3.desc": "Choose what you want this campaign to achieve.",
    "wizard.step3.selectedBadge": "Selected",
    "wizard.step3.competitors.toggle": "Competitor research",
    "wizard.step3.competitors.optional": "(optional)",
    "wizard.step3.competitors.show": "Show",
    "wizard.step3.competitors.hide": "Hide",
    "wizard.step3.competitors.desc": "Search competitors to inform your messaging before launching. We'll keep these alongside your campaign.",
    "wizard.step3.competitors.placeholder": "Add a competitor (brand or website)",
    // Wizard — Goal cards
    "wizard.goal.followers.title": "Grow Followers",
    "wizard.goal.followers.desc": "Build your audience and brand presence.",
    "wizard.goal.views.title": "Get More Views",
    "wizard.goal.views.desc": "Maximize video views and reach.",
    "wizard.goal.leads.title": "Get More Leads",
    "wizard.goal.leads.desc": "Collect contact info from interested people.",
    "wizard.goal.sales.title": "Drive More Sales",
    "wizard.goal.sales.desc": "Send people to your store or checkout.",
    "wizard.goal.whatsapp.title": "Get WhatsApp Messages",
    "wizard.goal.whatsapp.desc": "Start conversations on WhatsApp.",
    "wizard.goal.calls.title": "Get Phone Calls",
    "wizard.goal.calls.desc": "People tap your ad and call you directly.",
    "wizard.goal.event.title": "Promote an Event",
    "wizard.goal.event.desc": "Drive RSVPs or ticket sales.",
    "wizard.goal.traffic.title": "Drive Website Traffic",
    "wizard.goal.traffic.desc": "Send visitors to your website.",
    // Wizard — Step 4 (Budget)
    "wizard.step4.title": "Step 4 — Budget & Ad Account Setup",
    "wizard.step4.desc": "Set how much you want to spend and how long the campaign runs.",
    "wizard.step4.budgetTypeLabel": "Budget type",
    "wizard.step4.dailyBudget.title": "Daily Budget",
    "wizard.step4.dailyBudget.desc": "Spend a set amount every day (e.g. $20/day).",
    "wizard.step4.totalBudget.title": "Total Budget",
    "wizard.step4.totalBudget.desc": "Spread one total amount across the run (e.g. $500 total).",
    "wizard.step4.dailyLabel": "Daily budget (USD)",
    "wizard.step4.totalLabel": "Total budget (USD)",
    "wizard.step4.durationLabel": "Campaign duration (days)",
    "wizard.step4.reach.label": "Estimated reach",
    "wizard.step4.reach.value": "{n} people",
    "wizard.step4.reach.desc": "Based on a ${total} total spend at industry-average CPM. Real reach varies by platform, audience, and creative.",
    "wizard.step4.reach.note": "Estimates are based on industry averages. Connect your ad account for real-time reach data.",
    "wizard.step4.warning.title": "Budget below platform minimum",
    "wizard.step4.warning.item": "{platform} requires at least ${min}/day. Your effective daily spend is ${spend}/day.",
    "wizard.step4.warning.hint": "Increase your budget or remove platforms that don't meet the minimum to continue.",
    "wizard.step4.notice": "Your ad budget goes directly to the ad platforms. AdKavi never holds or touches your ad spend — we just manage it for you.",
    "wizard.step4.setup.title": "Don't have an ad account yet? Set one up free:",
    "wizard.step4.setup.desc": "Create the ad account on the platform itself, then connect it in AdKavi Settings.",
    "wizard.step4.setup.meta": "Set up Meta Ads Account",
    "wizard.step4.setup.tiktok": "Set up TikTok Ads Account",
    "wizard.step4.setup.google": "Set up Google Ads Account",
    // Wizard — Step 5 (Creative)
    "wizard.step5.title": "Step 5 — Create Your Ad Creative",
    "wizard.step5.desc": "Two ways to make your creative.",
    "wizard.step5.optionA": "Option A",
    "wizard.step5.upload.title": "Upload your own image or video",
    "wizard.step5.upload.desc": "Use creative you already have.",
    "wizard.step5.optionB": "Option B",
    "wizard.step5.ai.title": "Create with AI",
    "wizard.step5.ai.desc": "Design a flyer or post, generate an AI avatar video with voice clone, or enhance a recording.",
    "wizard.step5.uploadLabel": "Upload image or video",
    "wizard.step5.selectedFile": "Selected: {name}",
    "wizard.step5.aiType": "Pick an AI creative type",
    "wizard.step5.flyer": "Flyer / Post",
    "wizard.step5.avatar": "AI Avatar Video",
    "wizard.step5.enhance": "Enhance Recording",
    "wizard.step5.aiHint": "You can also build this in our AI Studio and come back to launch.",
    "wizard.step5.aiHintPre": "You can also build this in our",
    "wizard.step5.aiHintPost": "and come back to launch.",
    // Wizard — Step 6 (AI Build)
    "wizard.step6.title": "Step 6 — AI Builds Your Campaign",
    "wizard.step6.desc": "Review and edit the ad copy, location, and audience.",
    "wizard.step6.generating": "AdKavi AI is writing your ad…",
    "wizard.step6.adLang": "Ad language:",
    "wizard.step6.adCopy": "Ad Copy",
    "wizard.step6.regenerate": "Regenerate",
    "wizard.step6.english": "English",
    "wizard.step6.spanish": "Spanish",
    "wizard.step6.headline": "Headline",
    "wizard.step6.description": "Description",
    "wizard.step6.headlineEs": "Headline (Spanish)",
    "wizard.step6.descriptionEs": "Description (Spanish)",
    "wizard.step6.headlineEsPlaceholder": "Spanish headline",
    "wizard.step6.descriptionEsPlaceholder": "Spanish description",
    "wizard.step6.esTip": "Tip: click \"Regenerate\" with Spanish selected and the AI will write the Spanish version automatically.",
    "wizard.step6.cta": "Call to Action",
    "wizard.step6.location.title": "Target Location",
    "wizard.step6.location.label": "Add cities, states, countries, or zip codes",
    "wizard.step6.location.placeholder": "e.g. Miami, FL or 33101",
    "wizard.step6.location.radius": "Radius around location",
    "wizard.step6.audience.title": "Describe Your Audience",
    "wizard.step6.audience.optional": "optional",
    "wizard.step6.audience.placeholder": "e.g. Women 25–45 in Miami who are interested in fitness and healthy eating",
    "wizard.step6.audience.translate": "Translate to Meta Targeting",
    "wizard.step6.audience.translating": "Translating…",
    "wizard.step6.audience.translated": "Audience translated — review the settings below",
    "wizard.step6.target.title": "Target Audience",
    "wizard.step6.target.age": "Age range",
    "wizard.step6.target.gender": "Gender",
    "wizard.step6.target.genderAll": "All",
    "wizard.step6.target.genderMen": "Men",
    "wizard.step6.target.genderWomen": "Women",
    "wizard.step6.target.interests": "Interests",
    "wizard.step6.target.interestsHint": "AI pre-selected these based on your business. Add or remove as needed.",
    "wizard.step6.target.interestPlaceholder": "Add an interest",
    "wizard.step6.target.language": "Language",
    "wizard.step6.confirm": "I've reviewed and confirmed the ad copy, location, and audience.",
    // Wizard — Step 7 (Review & Launch)
    "wizard.step7.title": "Step 7 — Review Your Campaign",
    "wizard.step7.desc": "Double-check every detail below. When everything looks right, hit launch.",
    "wizard.step7.label.name": "Campaign name",
    "wizard.step7.label.industry": "Industry",
    "wizard.step7.label.promoting": "Promoting",
    "wizard.step7.label.platforms": "Platforms",
    "wizard.step7.label.goal": "Goal",
    "wizard.step7.label.budget": "Budget",
    "wizard.step7.label.creative": "Creative",
    "wizard.step7.label.competitors": "Competitor research",
    "wizard.step7.noneAdded": "None added",
    "wizard.step7.adCopy": "Ad Copy",
    "wizard.step7.targeting": "Targeting",
    "wizard.step7.label.locations": "Locations",
    "wizard.step7.label.radius": "Radius",
    "wizard.step7.label.age": "Age",
    "wizard.step7.label.gender": "Gender",
    "wizard.step7.label.interests": "Interests",
    "wizard.step7.label.language": "Language",
    "wizard.step7.schedule.title": "When should this go live?",
    "wizard.step7.schedule.desc": "Launch immediately or schedule for a specific date and time.",
    "wizard.step7.schedule.now.title": "Launch now",
    "wizard.step7.schedule.now.desc": "Goes live immediately after review",
    "wizard.step7.schedule.later.title": "Schedule for later",
    "wizard.step7.schedule.later.desc": "Choose a future date & time",
    "wizard.step7.schedule.dateLabel": "Start date & time",
    "wizard.step7.launching": "Launching…",
    "wizard.step7.scheduleCampaign": "Schedule Campaign",
    "wizard.step7.launchCampaign": "Launch Campaign",
    // Wizard — budget summary values
    "wizard.budget.daily": "${daily}/day × {days} days",
    "wizard.budget.total": "${total} total",
    "wizard.budget.dailyFull": "${daily}/day × {days} days = ${computed}",
    "wizard.budget.totalFull": "${total} total over {days} days",
    // Campaign detail toasts
    "campaign.toast.loadFailed": "Could not load campaign",
    "campaign.toast.notFound": "Campaign not found",
    "campaign.toast.durationMin": "Duration must be at least 1 day",
    "campaign.toast.dailyMin": "Daily budget must be > 0",
    "campaign.toast.totalMin": "Total budget must be > 0",
    "campaign.toast.budgetUpdated": "Budget updated",
    // Common
    "common.signInFirst": "Please sign in first",
    "common.copied": "Copied to clipboard",
    "common.copyFailed": "Couldn't copy",
    "common.saveFailed": "Couldn't save",
    "common.deleteFailed": "Delete failed",
    "common.signInToSave": "Sign in to save",
    "common.signInToSchedule": "Sign in to schedule",
    "common.somethingWrong": "Something went wrong. Please try again.",
    "common.tryAgain": "Try again in a moment.",
    "common.undo": "Undo",
    "common.undone": "Undone",
    "common.undoFailed": "Couldn't undo",
    // AI Studio toasts
    "ai.toast.tellAdvertising": "Tell us what you're advertising",
    "ai.toast.rateLimit": "Slow down — rate limit hit. Try again in a moment.",
    "ai.toast.creditsExhausted": "AI credits exhausted",
    "ai.toast.generationFailed": "Generation failed",
    "ai.toast.noVariations": "No variations returned. Try refining your prompt.",
    "ai.toast.generated": "Generated {n} variations",
    "ai.toast.savedDraft": "Saved as draft on the calendar",
    "ai.toast.addAvatarScript": "Add a script for the avatar to read",
    "ai.toast.startVideoFailed": "Couldn't start video",
    "ai.toast.heygenNoId": "HeyGen didn't return a video ID",
    "ai.toast.videoReady": "Video ready",
    "ai.toast.heygenFailed": "HeyGen render failed",
    "ai.toast.heygenShorter": "Try a shorter script",
    "ai.toast.addNarrationText": "Add some text to narrate",
    "ai.toast.voFailed": "Couldn't generate voiceover",
    "ai.toast.noAudio": "No audio returned",
    "ai.toast.voReady": "Voiceover ready",
    "ai.toast.describeClip": "Describe the cinematic clip you want",
    "ai.toast.clipFailed": "Couldn't generate clip",
    "ai.toast.noVideo": "No video returned",
    "ai.toast.tellVideoAbout": "Tell us what the video is about",
    "ai.toast.scriptFailed": "Couldn't generate script",
    "ai.toast.scriptReady": "Script ready",
    "ai.toast.copiedScript": "Copied voiceover script",
    "ai.toast.pasteEnglishUrl": "Paste a public URL of the English MP3 or MP4",
    "ai.toast.dubFailed": "Couldn't start dub",
    "ai.toast.dubReady": "Spanish dub ready",
    "ai.toast.dubAudioFailed": "Dubbed but couldn't fetch audio",
    "ai.toast.dubError": "Dub failed",
    "ai.toast.dubTimeout": "Timed out waiting for dub",
    // Cinematic Pro
    "cinematicPro.title": "Cinematic Pro",
    "cinematicPro.subtitle": "AI writes your story → 4 Kling clips → ElevenLabs voiceover → merged into one pro video.",
    "cinematicPro.briefLabel": "Describe your brand or product",
    "cinematicPro.briefPlaceholder": "e.g. AdKavi — AI platform that writes bilingual ads, generates videos, and launches Meta & TikTok campaigns in minutes",
    "cinematicPro.advanced": "Advanced options",
    "cinematicPro.aspect": "Aspect ratio",
    "cinematicPro.aspect.landscape": "Landscape (YouTube, Meta)",
    "cinematicPro.aspect.vertical": "Vertical (Reels, TikTok)",
    "cinematicPro.aspect.square": "Square (Feed)",
    "cinematicPro.showTitles": "Show beat title overlays",
    "cinematicPro.generate": "Generate Cinematic Pro video",
    "cinematicPro.generating": "Starting pipeline…",
    "cinematicPro.stage.generating_clips": "Kling rendering {done}/{total} clips…",
    "cinematicPro.stage.merging": "Merging clips + voiceover with Creatomate…",
    "cinematicPro.stage.completed": "Video ready!",
    "cinematicPro.finalVideo": "Final Video (merged)",
    "cinematicPro.voiceover": "Voiceover (40 sec)",
    "cinematicPro.download": "Download",
    "cinematicPro.addToCampaign": "Add to Campaign",
    "cinematicPro.regenerate": "Regenerate",
    "cinematicPro.poweredBy": "Claude · Kling 1.6 · ElevenLabs · Creatomate",
    "cinematicPro.beats": "Story beats",
    "cinematicPro.clipDone": "Clip ready",
    "cinematicPro.clipPending": "Generating…",
    "cinematicPro.clipFailed": "Failed",
    "cinematicPro.toast.briefRequired": "Describe your brand first",
    "cinematicPro.toast.started": "Started — story + 4 clips + voiceover + merge (~5 min)",
    "cinematicPro.toast.failed": "Generation failed",
    "cinematicPro.toast.ready": "Video ready!",
    // AI Image Ad
    "ai.image.title": "AI Image Ads",
    "ai.image.subtitle": "Describe your business or campaign and AI will generate a ready-to-use ad image.",
    "ai.image.briefLabel": "Describe your business, product, or campaign",
    "ai.image.briefPlaceholder": "e.g. A premium organic skincare brand launching a hydrating serum for women 25-45. Clean, minimal, sunlit feel.",
    "ai.image.format": "Format",
    "ai.image.style": "Style",
    "ai.image.format.square": "Square (1:1)",
    "ai.image.format.story": "Story (9:16)",
    "ai.image.format.landscape": "Landscape (16:9)",
    "ai.image.format.portrait": "Portrait (4:5)",
    "ai.image.style.photoreal": "Photorealistic",
    "ai.image.style.lifestyle": "Lifestyle",
    "ai.image.style.studio": "Studio product",
    "ai.image.style.minimal": "Minimal / clean",
    "ai.image.style.bold": "Bold & graphic",
    "ai.image.generate": "Generate image ad",
    "ai.image.generating": "Generating image…",
    "ai.image.download": "Download",
    "ai.image.addToCampaign": "Add to Campaign",
    "ai.image.regenerate": "Regenerate",
    "ai.image.toast.brief": "Describe your business or campaign first",
    "ai.image.toast.failed": "Couldn't generate image",
    "ai.image.toast.ready": "Image ad ready",
    "ai.image.toast.saved": "Saved to your creatives",
    "ai.image.toast.saveFailed": "Couldn't save creative",
    "ai.image.poweredBy": "Prompt by Claude · Image by Luma Photon",
    // AI Carousel Ad
    "ai.carousel.title": "AI Carousel Ads",
    "ai.carousel.subtitle": "AI builds a swipeable multi-slide ad with copy and images for each slide.",
    "ai.carousel.briefLabel": "Describe your business, product, or campaign",
    "ai.carousel.briefPlaceholder": "e.g. A meal-prep service for busy professionals — 5 chef-made dinners per week, no shopping.",
    "ai.carousel.format": "Format",
    "ai.carousel.style": "Style",
    "ai.carousel.slides": "Slides",
    "ai.carousel.generate": "Generate carousel",
    "ai.carousel.generating": "Generating slides…",
    "ai.carousel.regenSlide": "Regen",
    "ai.carousel.upload": "Upload",
    "ai.carousel.downloadAll": "Download all",
    "ai.carousel.toast.brief": "Describe your business or campaign first",
    "ai.carousel.toast.failed": "Couldn't generate carousel",
    "ai.carousel.toast.ready": "Carousel ready",
    "ai.carousel.toast.regenerated": "Slide regenerated",
    "ai.carousel.toast.uploaded": "Image uploaded",
    "ai.carousel.poweredBy": "Copy by Claude · Images by Nano Banana",
    // AI Caption Writer
    "ai.caption.title": "AI Caption Writer",
    "ai.caption.subtitle": "Describe your post or upload an image and get a ready-to-publish caption with hashtags.",
    "ai.caption.descLabel": "Describe your post (optional if you upload an image)",
    "ai.caption.descPlaceholder": "e.g. New summer collection drop — linen dresses, beach vibes, launching Friday.",
    "ai.caption.imageLabel": "Reference image (optional)",
    "ai.caption.uploadImage": "Upload an image",
    "ai.caption.platform": "Platform",
    "ai.caption.tone": "Tone",
    "ai.caption.tone.professional": "Professional",
    "ai.caption.tone.fun": "Fun",
    "ai.caption.tone.urgent": "Urgent",
    "ai.caption.tone.inspirational": "Inspirational",
    "ai.caption.language": "Language",
    "ai.caption.generate": "Generate caption",
    "ai.caption.generating": "Writing caption…",
    "ai.caption.copy": "Copy",
    "ai.caption.saveToCalendar": "Save to Content Calendar",
    "ai.caption.regenerate": "Regenerate",
    "ai.caption.toast.needInput": "Add a description or upload an image first",
    "ai.caption.toast.failed": "Couldn't generate caption",
    "ai.caption.toast.ready": "Caption ready",
    "ai.caption.toast.savedToCalendar": "Saved to Content Calendar as a draft",
    "ai.caption.toast.imageTooLarge": "Image is too large (max 4MB)",
    "ai.caption.poweredBy": "Powered by Claude — review before publishing.",
    "ai.caption.variants": "Variants",
    "ai.caption.variantsHint": "Generate multiple versions to choose from",
    "ai.caption.variant": "Variant",
    "ai.caption.pickVariant": "Pick a variant",
    "ai.caption.hashtagCount": "Hashtag count",
    "ai.caption.hashtagFew": "Few (3-5)",
    "ai.caption.hashtagMedium": "Medium (7-12)",
    "ai.caption.hashtagMany": "Many (12-20)",
    "ai.caption.modeLabel": "Format",
    "ai.caption.modeSingle": "Single post",
    "ai.caption.modeCarousel": "Carousel",
    "ai.caption.slideCount": "Slides",
    "ai.caption.slidesTitle": "Carousel slides",
    "ai.caption.slidesHint": "Click a slide to preview it",
    "ai.caption.slide": "Slide",
    "ai.caption.slideHook": "Hook",
    "ai.caption.slideCta": "CTA",
    "ai.caption.hashtagsExport.button": "Export",
    "ai.caption.hashtagsExport.copyAs": "Copy as…",
    "ai.caption.hashtagsExport.withHash": "With # (space-separated)",
    "ai.caption.hashtagsExport.plain": "Plain words (no #)",
    "ai.caption.hashtagsExport.comma": "Comma-separated",
    "ai.caption.hashtagsExport.lines": "One per line",
    "ai.caption.hashtagsExport.download": "Download .txt",
    "ai.caption.hashtagsExport.copied": "Copied {n} hashtag(s)",
    "ai.caption.hashtagsExport.downloaded": "Hashtags downloaded",
    "ai.caption.hashtagsTitle": "Hashtags",
    "ai.caption.hashtagsHint": "Click to remove. They sync to your copy & save.",
    "ai.caption.dropImage": "Drop image, paste, or click to upload",
    "ai.caption.dropHint": "PNG/JPG up to 4MB",
    "ai.caption.removeImage": "Remove image",
    "ai.caption.preview": "Preview",
    "ai.caption.previewHint": "How it will look on the selected platform",
    "ai.caption.scheduleFor": "Schedule for",
    "ai.caption.pickDate": "Pick a date",
    "ai.caption.time": "Time",
    "ai.caption.toast.imagePasted": "Image pasted",
    // Settings toasts
    "settings.toast.saveFailed": "Failed to save",
    "settings.toast.profileUpdated": "Profile updated",
    "settings.toast.dataDownloaded": "Your data has been downloaded",
    "settings.toast.downloadFailed": "Download failed",
    "settings.toast.accountDeleted": "Your account has been permanently deleted.",
    "settings.toast.accountDeleteFailed": "Couldn't delete account",
    "settings.toast.portalFailed": "Couldn't open billing portal",
    "settings.toast.noInvoicePdfs": "No invoice PDFs available to download.",
    "settings.toast.invoicesDownloadedOne": "Downloaded {n} invoice.",
    "settings.toast.invoicesDownloadedMany": "Downloaded {n} invoices.",
    "settings.toast.zipFailed": "Couldn't build ZIP",
    "settings.toast.noInvoicesExport": "No invoices to export.",
    "settings.toast.exportedCsvOne": "Exported {n} invoice as CSV.",
    "settings.toast.exportedCsvMany": "Exported {n} invoices as CSV.",
    "settings.toast.savedPreference": "Saved as a preference",
    // Settings UI
    "settings.title": "Settings",
    "settings.subtitle": "Manage your account and subscription.",
    "settings.tab.profile": "Profile",
    "settings.tab.aiAgent": "AI Agent",
    "settings.tab.channels": "Channels",
    "settings.tab.integrations": "Integrations",
    "settings.tab.controls": "Controls",
    "settings.tab.billing": "Billing",
    "settings.profile.desc": "Your display name and reporting currency.",
    "settings.profile.email": "Email",
    "settings.profile.emailHint": "Email is managed by your account login and can't be changed here.",
    "settings.profile.displayName": "Display name",
    "settings.profile.displayNamePlaceholder": "e.g. Jane Marketing",
    "settings.profile.currency": "Reporting currency",
    "settings.profile.currencyHint": "All metrics on the dashboard are displayed in this currency.",
    "settings.profile.save": "Save profile",
    "settings.danger.title": "Danger Zone",
    "settings.danger.desc": "Irreversible actions for your account.",
    "settings.danger.downloadTitle": "Download My Data",
    "settings.danger.downloadDesc": "Download a copy of all your campaign data before deleting.",
    "settings.danger.downloadBtn": "Download My Data",
    "settings.danger.preparing": "Preparing…",
    "settings.danger.deleteTitle": "Delete Account",
    "settings.danger.deleteDesc": "Permanently delete your AdKavi account, all campaigns, and connected ad accounts. This cannot be undone.",
    "settings.danger.deleteBtn": "Delete My Account",
    "settings.danger.confirmTitle": "Are you sure?",
    "settings.danger.confirmWhat": "This will permanently delete:",
    "settings.danger.confirmList1": "Your AdKavi account",
    "settings.danger.confirmList2": "All your campaigns and ad data",
    "settings.danger.confirmList3": "All connected Meta, TikTok, Google & YouTube accounts",
    "settings.danger.confirmList4": "All your content and creatives",
    "settings.danger.confirmTypePre": "Type",
    "settings.danger.confirmTypePost": "to confirm",
    "settings.danger.deleting": "Deleting…",
    "settings.danger.permanentlyDelete": "Permanently Delete",
    "settings.billing.loading": "Loading billing…",
    "settings.billing.noActivePlan": "No active plan",
    "settings.billing.noActivePlanDesc": "Pick a plan to start your 14-day free trial. Card required, cancel anytime.",
    "settings.billing.startTrialTitle": "Start your free trial",
    "settings.billing.startTrialDesc": "8 plans for creators, businesses, and agencies. 14 days free.",
    "settings.billing.viewPlans": "View plans",
    "settings.billing.cancelAtPeriodEnd": "· Cancels at period end",
    "settings.billing.trialEnds": "Free trial ends",
    "settings.billing.trialChargeWarning": "Your card will be charged ${price} on that day unless you cancel.",
    "settings.billing.accessEnds": "Access ends",
    "settings.billing.renews": "Renews",
    "settings.billing.on": "on",
    "settings.billing.opening": "Opening…",
    "settings.billing.manageBilling": "Manage billing",
    "settings.billing.upgradePlan": "Upgrade or switch plan",
    "settings.billing.comparePlans": "Compare plans",
    "settings.billing.support": "Billing support",
    "settings.billing.supportDesc": "Questions about charges, refunds, or invoices? We're here to help.",
    "settings.billing.contactSupport": "Contact Billing Support",
    "settings.billing.supportFootnote": "We typically reply within one business day.",
    "settings.inv.title": "Invoices",
    "settings.inv.desc": "Download your past invoices and receipts.",
    "settings.inv.loading": "Loading invoices…",
    "settings.inv.loadError": "Couldn't load invoices: {error}",
    "settings.inv.empty": "No invoices yet — they'll appear here after your first payment.",
    "settings.inv.search": "Search invoices",
    "settings.inv.searchPlaceholder": "Search by invoice number, amount, or date…",
    "settings.inv.status": "Status",
    "settings.inv.statusAll": "All statuses",
    "settings.inv.statusPaid": "Paid",
    "settings.inv.statusPending": "Pending",
    "settings.inv.statusFailed": "Failed",
    "settings.inv.statusVoided": "Voided",
    "settings.inv.statusDraft": "Draft",
    "settings.inv.from": "From",
    "settings.inv.to": "To",
    "settings.inv.exportCsv": "Export CSV",
    "settings.inv.zipping": "Zipping…",
    "settings.inv.downloadAll": "Download all ({n})",
    "settings.inv.noMatch": "No invoices match your filters.",
    "settings.inv.colDate": "Date",
    "settings.inv.colInvoice": "Invoice",
    "settings.inv.colAmount": "Amount",
    "settings.inv.colStatus": "Status",
    "settings.inv.colPdf": "PDF",
    "settings.inv.colView": "View",
    "settings.controls.title": "Compliance & Controls",
    "settings.controls.desc": "Set safety guardrails so every campaign meets your brand and legal requirements.",
    "settings.controls.preview": "Preview",
    "settings.controls.activateHint": "Full compliance controls activate when your ad account is connected.",
    "settings.controls.ageTitle": "Age restrictions",
    "settings.controls.ageDesc": "Limit campaigns to specific age ranges (e.g. 21+ for alcohol, 18+ for gambling).",
    "settings.controls.geoTitle": "Geographic restrictions",
    "settings.controls.geoDesc": "Block ads from running in specific countries, states, or cities.",
    "settings.controls.contentTitle": "Content category restrictions",
    "settings.controls.contentDesc": "Prevent ads from appearing alongside sensitive content (violence, politics, etc.).",
    "settings.controls.effectHint": "This will take effect once your ad account is connected.",
    // Creative toasts
    "creative.toast.loadFailed": "Couldn't load creatives",
    "creative.toast.signInToSave": "Sign in to save",
    "creative.toast.saveFailed": "Save failed",
    "creative.toast.updated": "Creative updated",
    "creative.toast.saved": "Creative saved",
    "creative.toast.deleteFailed": "Delete failed",
    "creative.toast.deleted": "Creative deleted",
    "creative.toast.exportedPng": "Exported PNG",
    "creative.toast.exportFailed": "Export failed",
    // Content toasts
    "content.toast.loadFailed": "Couldn't load posts",
    "content.toast.deleteFailed": "Delete failed",
    "content.toast.deleted": "Post deleted",
    "content.toast.addTitle": "Add a title",
    "content.toast.pickDate": "Pick a date and time",
    "content.toast.saveFailed": "Couldn't save",
    "content.toast.updated": "Post updated",
    "content.toast.trialLimit": "Trial limit reached",
    "content.toast.scheduleFailed": "Couldn't schedule",
    "content.toast.scheduled": "Post scheduled",
    "publish.title": "Publish to platforms",
    "publish.desc": "Pick where to publish. Each platform can have its own caption, format, and time.",
    "publish.empty": "Select one or more platforms above.",
    "publish.field.format": "Format",
    "publish.field.when": "Schedule",
    "publish.field.caption": "Caption",
    "publish.field.captionPlaceholder": "Platform-specific caption (optional)",
    "publish.status.scheduled": "Scheduled",
    "publish.status.published": "Published",
    "publish.status.failed": "Failed",
    "publish.toast.syncFailed": "Couldn't sync platform publications.",
    "content.generate.button": "Generate with AI",
    "content.generate.generating": "Generating…",
    "content.generate.pick": "Pick a suggestion — click to use",
    "content.generate.failed": "Couldn't generate caption",
    // Content Calendar UI
    "content.title": "Content Calendar",
    "content.subtitle": "Schedule and manage your posts across every platform.",
    "content.createPost": "Create Post",
    "content.stat.thisMonth": "This month",
    "content.stat.scheduled": "Scheduled",
    "content.stat.published": "Published",
    "content.stat.drafts": "Drafts",
    "content.cal.today": "Today",
    "content.cal.sun": "Sun",
    "content.cal.mon": "Mon",
    "content.cal.tue": "Tue",
    "content.cal.wed": "Wed",
    "content.cal.thu": "Thu",
    "content.cal.fri": "Fri",
    "content.cal.sat": "Sat",
    "content.cal.more": "+{n} more",
    "content.empty": "No posts scheduled — click any date or + to create your first post",
    "content.dialog.editTitle": "Edit post",
    "content.dialog.scheduleTitle": "Schedule a post",
    "content.dialog.editDesc": "Update details or change the schedule.",
    "content.dialog.scheduleDesc": "Plan a new post on your calendar.",
    "content.field.title": "Title",
    "content.field.titlePlaceholder": "Spring sale launch",
    "content.field.platform": "Platform",
    "content.field.type": "Type",
    "content.field.when": "When",
    "content.field.status": "Status",
    "content.field.caption": "Caption",
    "content.field.captionPlaceholder": "Write your caption…",
    "content.status.draft": "Draft",
    "content.status.scheduled": "Scheduled",
    "content.status.published": "Published",
    "content.status.failed": "Failed",
    "content.kind.post": "Post",
    "content.kind.carousel": "Carousel",
    "content.kind.reel": "Reel",
    "content.kind.story": "Story",
    "content.kind.video": "Video",
    "content.badge.createdPost": "Created post",
    "content.pub.loading": "Loading…",
    "content.btn.delete": "Delete",
    "content.btn.cancel": "Cancel",
    "content.btn.save": "Save changes",
    "content.btn.schedule": "Schedule post",
    "content.cal.prevMonth": "Previous month",
    "content.cal.nextMonth": "Next month",
    // Growth Analytics
    "growth.title": "Growth Analytics",
    "growth.subtitle": "Track your content publishing activity",
    "growth.stat.thisMonth": "This month",
    "growth.stat.published": "Published",
    "growth.stat.scheduled": "Scheduled",
    "growth.stat.drafts": "Drafts",
    "growth.chart.weekly": "Posts per week",
    "growth.chart.weeklySub": "Last 8 weeks",
    "growth.chart.platforms": "By platform",
    "growth.upcoming.title": "Next 7 days",
    "growth.upcoming.empty": "No posts scheduled in the next 7 days.",
    "growth.connect.title": "See real engagement metrics",
    "growth.connect.desc": "Connect your social accounts to see likes, comments, shares, and follower growth.",
    "growth.connect.cta": "Go to Settings",
    "sidebar.growth": "Growth Analytics",
    "sidebar.inbox": "Conversations",
    // Clients toasts
    "clients.toast.deleteFailed": "Couldn't delete",
    "clients.toast.removed": "Client removed",
    "clients.toast.addName": "Add a client name",
    "clients.toast.saveFailed": "Couldn't save",
    "clients.toast.updated": "Client updated",
    "clients.toast.addFailed": "Couldn't add",
    "clients.toast.added": "Client added",
    // Clients page UI
    "clients.heading": "Clients",
    "clients.desc": "Each client gets its own campaigns, posts, and creatives. Use the sidebar switcher to scope your work.",
    "clients.addBtn": "Add client",
    "clients.loading": "Loading clients…",
    "clients.empty.title": "No clients yet",
    "clients.empty.desc": "Add your first client account to start organizing campaigns, posts, and creatives.",
    "clients.activeBadge": "Active",
    "clients.activateHint": "Click activate to scope work to this client",
    "clients.activateBtn": "Activate",
    "clients.editBtn": "Edit",
    "clients.deleteBtn": "Delete",
    "clients.deleteConfirm": "Delete this client account? Existing campaigns and posts will be unlinked.",
    "clients.dialog.editTitle": "Edit client",
    "clients.dialog.addTitle": "Add a client",
    "clients.dialog.desc": "Give the client a name and pick a brand color so it's easy to spot in lists.",
    "clients.dialog.nameLabel": "Name",
    "clients.dialog.colorLabel": "Brand color",
    "clients.dialog.cancel": "Cancel",
    "clients.dialog.saving": "Saving…",
    "clients.dialog.saveBtn": "Save changes",
    "clients.dialog.addBtn": "Add client",
    // Integration toasts
    "integration.toast.disconnectFailed": "Couldn't disconnect",
    "integration.toast.googleDisconnected": "Google disconnected",
    "integration.toast.metaDisconnected": "Meta disconnected",
    "integration.toast.tiktokDisconnected": "TikTok disconnected",
    "integration.toast.metaConnected": "Meta connected",
    "integration.toast.metaConnectFailed": "Couldn't connect Meta",
    "integration.toast.tiktokStartFailed": "Couldn't start TikTok connection",
    "integration.toast.googleStartFailed": "Couldn't start Google connection",
    // Voice/Avatar toasts
    "voice.toast.invalidFormat": "Invalid format",
    "voice.toast.invalidFormatDesc": "Please upload an MP3 or WAV file.",
    "voice.toast.tooLarge": "File too large",
    "voice.toast.tooLargeDesc": "Max 50 MB. Yours is {size} MB.",
    "voice.toast.cloning": "Cloning your voice — ready in a few minutes.",
    "voice.toast.uploadFailed": "Upload failed",
    "avatar.toast.invalidFormatDesc": "Please upload an MP4 or WebM file.",
    "avatar.toast.tooLargeDesc": "Max 100 MB. Yours is {size} MB.",
    "avatar.toast.processing": "Processing your avatar — 20–30 minutes.",
    // Video library
    "video.toast.deleteFailed": "Couldn't delete video",
    "video.toast.deleted": "Video deleted",
    // Promo code
    "promo.toast.validateFailed": "Could not validate code",
    "promo.toast.invalid": "Invalid promo code",
    "promo.toast.applied": "{code} applied",
    // Billing/checkout
    "checkout.toast.signInFirst": "Please sign in first",
    "checkout.toast.startFailed": "Couldn't start checkout",
    // Account type / onboarding
    "onboarding.toast.saveChoiceFailed": "Couldn't save your choice. Please try again.",
    // Admin audit
    "admin.toast.noRowsExport": "No rows to export",
    "admin.toast.exportedRows": "Exported {n} rows",
    "admin.toast.exportFailed": "Export failed",
    // Audience Insights
    "audience.title": "Audience Insights",
    "audience.subtitle": "Who's engaging with your ads — age, location, device and top segment.",
    "audience.demoData": "Demo data",
    "audience.age": "Age range",
    "audience.gender": "Gender",
    "audience.gender.female": "Female",
    "audience.gender.male": "Male",
    "audience.gender.other": "Other",
    "audience.location": "Top locations",
    "audience.country.us": "United States",
    "audience.country.mx": "Mexico",
    "audience.country.es": "Spain",
    "audience.country.ar": "Argentina",
    "audience.country.co": "Colombia",
    "audience.device": "Device",
    "audience.device.mobile": "Mobile",
    "audience.device.desktop": "Desktop",
    "audience.device.tablet": "Tablet",
    "audience.topSegment": "Top performing segment",
    "audience.segment.urban25": "Urban 25–34, mobile-first",
    "audience.segment.parents": "Parents 30–44",
    "audience.segment.smbOwners": "Small business owners",
    "audience.segment.fitness": "Fitness & wellness enthusiasts",
    "audience.segment.techEarly": "Tech early adopters",
    "audience.topSegmentDetail": "{share}% of conversions · {roas}× ROAS",
    // Audit Trail
    "audit.title": "Activity log",
    "audit.subtitle": "Timeline of every action taken on this campaign.",
    "audit.demoNote": "Showing recent activity. Older entries are archived after 90 days.",
    "audit.user.you": "You",
    "audit.user.aiManager": "AI Manager",
    "audit.event.created": "Campaign created",
    "audit.event.budget_updated": "Budget updated",
    "audit.event.approved": "Approved for launch",
    "audit.event.launched": "Launched live",
    "audit.event.paused": "Paused",
    "audit.event.edited": "Creative edited",
    "audit.event.viewed": "Viewed",
    "audit.detail.creativeUpdated": "Headline and CTA refined by AI Studio.",
    "audit.detail.budgetSet": "Daily budget configured and saved.",
    "audit.detail.budgetGuardrail": "Auto-paused — daily spend cap reached.",
    // Campaign detail page
    "campaign.back": "Back to campaigns",
    "campaign.export": "Export Report",
    "campaign.loading": "Loading campaign…",
    "campaign.budget.title": "Budget vs Spend",
    "campaign.budget.desc": "How much of your total budget has been spent so far.",
    "campaign.budget.spent": "{amount} spent",
    "campaign.budget.budget": "{amount} budget",
    "campaign.budget.used": "{pct}% used",
    "campaign.budget.remaining": "{amount} remaining",
    "campaign.platform.title": "Platform Breakdown",
    "campaign.platform.desc": "Budget and spend per ad platform.",
    "campaign.platform.note": "Budget is allocated to the platform selected during campaign setup. Multi-platform allocation coming soon.",
    "campaign.platform.active": "Active",
    "campaign.details.title": "Budget Details",
    "campaign.details.desc": "Daily budget, total budget, duration, and estimated reach.",
    "campaign.details.edit": "Edit",
    "campaign.details.cancel": "Cancel",
    "campaign.details.save": "Save changes",
    "campaign.details.budgetType": "Budget type",
    "campaign.details.budgetTypeDaily": "Daily Budget",
    "campaign.details.budgetTypeTotal": "Total Budget",
    "campaign.details.dailyLabel": "Daily budget (USD)",
    "campaign.details.totalLabel": "Total budget (USD)",
    "campaign.details.durationLabel": "Duration (days)",
    "campaign.details.newTotal": "New total",
    "campaign.details.newReach": "New estimated reach",
    "campaign.details.people": "{n} people",
    "campaign.details.dailyValue": "{amount}/day",
    "campaign.details.daysValue": "{n} days",
    "campaign.field.budgetType": "Budget type",
    "campaign.field.dailyBudget": "Daily budget",
    "campaign.field.totalBudget": "Total budget",
    "campaign.field.duration": "Duration",
    "campaign.field.estimatedReach": "Estimated reach",
    "campaign.perf.title": "Performance",
    "campaign.perf.desc": "Lifetime totals from connected platforms.",
    "campaign.perf.reach": "Reach",
    "campaign.perf.clicks": "Clicks",
    "campaign.perf.leads": "Leads",
    "campaign.perf.conversions": "Conversions",
    "campaign.perf.revenue": "Revenue",
    "campaign.perf.spend": "Spend",
    "campaign.perf.roas": "ROAS",
    "campaign.confirm.title": "Confirm budget changes",
    "campaign.confirm.dailyBody": "You're about to change this campaign's budget to {daily}/day × {days} days = {total}. This will affect spend pacing and platform delivery. Continue?",
    "campaign.confirm.totalBody": "You're about to change this campaign's budget to {total} total over {days} days. This will affect spend pacing and platform delivery. Continue?",
    "campaign.confirm.cancel": "Cancel",
    "campaign.confirm.confirm": "Yes, save changes",
    // Campaigns list page
    "campaignsList.title": "Campaigns",
    "campaignsList.subtitle": "All ad campaigns across your connected accounts.",
    "campaignsList.newAi": "New AI campaign",
    "campaignsList.trialLimit": "Trial limit reached for this client",
    "campaignsList.loading": "Loading campaigns…",
    "campaignsList.empty.title": "No campaigns yet",
    "campaignsList.empty.body": "Generate your first campaign with AI — brief, creatives, and targeting in seconds.",
    "campaignsList.empty.cta": "Create campaign",
    "campaignsList.col.name": "Name",
    "campaignsList.col.platform": "Platform",
    "campaignsList.col.budget": "Budget",
    "campaignsList.col.spend": "Spend progress",
    "campaignsList.col.status": "Status",
    "campaignsList.col.created": "Created",
    // Audience AI recommendation
    "audience.rec.title": "AI recommendation",
    "audience.rec.refresh": "Refresh",
    "audience.rec.loading": "Analyzing your audience…",
    "audience.rec.empty": "No recommendation available yet.",
    "audience.rec.error": "Couldn't generate a recommendation. Please try again.",
    // Audit trail filters & PDF export
    "audit.filter.actions": "Actions",
    "audit.filter.actionType": "Filter by action",
    "audit.filter.dateRange": "Date range",
    "audit.filter.from": "From",
    "audit.filter.to": "To",
    "audit.filter.reset": "Reset",
    "audit.empty.filtered": "No actions match your filters.",
    "audit.export.pdf": "Export PDF",
    "audit.pdf.title": "Campaign Audit Trail",
    "audit.pdf.campaign": "Campaign",
    "audit.pdf.dateRange": "Date range",
    "audit.pdf.generated": "Generated",
    "audit.pdf.actions": "Actions",
    "audit.pdf.noResults": "No actions in the selected range.",
    "audit.pdf.exported": "Audit trail exported.",
    "audit.pdf.exportFailed": "Couldn't export the audit trail.",
    // Metric drill-down
    "metric.detail.subtitle": "Daily breakdown for the selected period.",
    "metric.detail.empty": "No data available for this metric in the selected range.",
    "metric.detail.total": "Total",
    "metric.detail.avg": "Average / day",
    "metric.detail.peak": "Peak",
    "metric.detail.trend": "Trend",
    "metric.detail.openAria": "open detailed breakdown",
    "costAlerts.title": "Cost Alerts",
    "costAlerts.desc": "Get an email when your cost per result exceeds a threshold (max 1 email per day per alert).",
    "costAlerts.loading": "Loading alerts…",
    "costAlerts.empty": "No alerts yet. Add one below to get notified.",
    "costAlerts.addTitle": "Add a new alert",
    "costAlerts.add": "Add",
    "costAlerts.added": "Alert added",
    "costAlerts.deleted": "Alert removed",
    "costAlerts.delete": "Delete alert",
    "costAlerts.duplicate": "An alert for this metric already exists",
    "costAlerts.invalidThreshold": "Enter a threshold greater than 0",
    "costAlerts.metricLabel": "Metric",
    "costAlerts.thresholdLabel": "Threshold",
    "costAlerts.thresholdPlaceholder": "e.g. 2.50",
    "costAlerts.thresholdLine": "Threshold: {value}",
    "costAlerts.lastSent": "Last alert sent {date}",
    "costAlerts.checkNow": "Check now",
    "costAlerts.checkSent": "Sent {n} alert email(s)",
    "costAlerts.checkBreachOnly": "Threshold exceeded but already notified in the last 24h",
    "costAlerts.checkNoBreach": "All alerts within threshold",
    "costAlerts.metric.cpc": "Cost per Click (CPC)",
    "costAlerts.metric.cpl": "Cost per Lead (CPL)",
    "costAlerts.metric.cpa": "Cost per Conversion (CPA)",
    "costAlerts.metric.spend": "Total spend (last 24h)",
    "costAlerts.history.title": "Alert history",
    "costAlerts.history.desc": "Recent notifications sent for this campaign.",
    "costAlerts.history.empty": "No alerts have been sent yet.",
    "costAlerts.history.loading": "Loading history…",
    "costAlerts.history.show": "Show history",
    "costAlerts.history.hide": "Hide history",
    "costAlerts.history.row": "{metric} — observed {observed}, threshold {threshold}",
    "costAlerts.history.sentAt": "Sent {date}",
    "costAlerts.sendTest": "Send test email",
    "costAlerts.testSent": "Test email sent to {email}",
    "costAlerts.testFailed": "Could not send test email",
    // Budget Optimizer
    "optimizer.title": "AI Budget Optimizer",
    "optimizer.subtitle": "Reallocate budget toward your best-performing ads.",
    "optimizer.loading": "Analyzing ad performance…",
    "optimizer.empty": "No ads tracked yet for this campaign.",
    "optimizer.seed": "Add sample ads",
    "optimizer.seeded": "Sample ads created",
    "optimizer.bestPerformer": "Top performer",
    "optimizer.spend": "Spend",
    "optimizer.conversions": "Conv.",
    "optimizer.shareSum": "Current shares total {sum}% — recommendation will normalize to 100%.",
    "optimizer.recTitle": "Recommended budget shift",
    "optimizer.recBody": "Move budget from {losers} into {winner} based on ROAS and cost per conversion.",
    "optimizer.apply": "Apply recommendation",
    "optimizer.applied": "Budget split updated",
    "optimizer.needMoreData": "Need at least two ads with spend to make a recommendation.",
    "optimizer.noChange": "Budget split is already optimized.",
    // A/B Testing
    "ab.title": "A/B Testing",
    "ab.subtitle": "Run two ad variants head-to-head and let us call the winner.",
    "ab.loading": "Loading tests…",
    "ab.empty": "No tests yet. Start one to compare two ads.",
    "ab.needTwoAds": "Add at least two active ads to this campaign to start an A/B test.",
    "ab.missingAds": "Test refers to ads that no longer exist",
    "ab.newTest": "New A/B test",
    "ab.created": "A/B test started",
    "ab.stopped": "Test stopped",
    "ab.completedWithPause": "Winner declared — loser paused",
    "ab.completedTie": "Test completed — it's a tie",
    "ab.adPaused": "Ad paused",
    "ab.started": "Started",
    "ab.durationDays": "{n}d window",
    "ab.spendThreshold": "Stop at ${v} total spend",
    "ab.stop": "Stop",
    "ab.pause": "Pause",
    "ab.paused": "Paused",
    "ab.variant": "Variant",
    "ab.declareWinner": "Declare winner & pause loser",
    "ab.markTie": "Mark as tie",
    "ab.triggerSpend": "Spend threshold reached",
    "ab.triggerDuration": "Test window elapsed",
    "ab.recWinner": "{winner} is winning. Pause {loser} and shift effort to the winner.",
    "ab.recTie": "Both variants are performing similarly — no clear winner.",
    "ab.endedWinner": "Winner: {winner}",
    "ab.endedTie": "Ended in a tie",
    "ab.status.running": "Running",
    "ab.status.completed": "Completed",
    "ab.status.stopped": "Stopped",
    "ab.stat.spend": "Spend",
    "ab.stat.conv": "Conv.",
    "ab.stat.clicks": "Clicks",
    "ab.stat.revenue": "Revenue",
    "ab.error.nameRequired": "Give your test a name",
    "ab.error.pickTwo": "Pick two different ads",
    "ab.dialog.title": "New A/B test",
    "ab.dialog.desc": "Pick two ads from this campaign and set a stop condition.",
    "ab.dialog.name": "Test name",
    "ab.dialog.namePh": "Hook copy v1 vs v2",
    "ab.dialog.variantA": "Variant A",
    "ab.dialog.variantB": "Variant B",
    "ab.dialog.pickAd": "Choose an ad…",
    "ab.dialog.duration": "Duration (days)",
    "ab.dialog.spend": "Spend threshold",
    "ab.dialog.note": "We call a winner when either condition is reached. Leave a field empty to ignore it.",
    "ab.dialog.create": "Start test",
    "ai.caption.preset.title": "Brand voice preset",
    "ai.caption.preset.placeholder": "Choose a preset…",
    "ai.caption.preset.none": "No preset",
    "ai.caption.preset.saveCurrent": "Save as preset",
    "ai.caption.preset.styleNotes": "Style notes",
    "ai.caption.preset.styleNotesPlaceholder": "e.g. friendly but expert, never use emojis at the start, always end with a question",
    "ai.caption.preset.keywords": "Brand keywords",
    "ai.caption.preset.keywordsPlaceholder": "comma-separated, e.g. handmade, sustainable, small-batch",
    "ai.caption.preset.keywordsHint": "Up to 20 keywords. Separate with commas.",
    "ai.caption.preset.dialogTitle": "Save brand voice preset",
    "ai.caption.preset.dialogDesc": "Save your current tone, style notes, and keywords for one-click reuse.",
    "ai.caption.preset.nameLabel": "Preset name",
    "ai.caption.preset.namePlaceholder": "e.g. Founder voice, Holiday sale",
    "ai.caption.preset.nameRequired": "Please enter a preset name",
    "ai.caption.preset.save": "Save preset",
    "ai.caption.preset.cancel": "Cancel",
    "ai.caption.preset.saved": "Preset saved",
    "ai.caption.preset.deleted": "Preset deleted",
    "sendToCampaign.title": "Send to Campaign",
    "sendToCampaign.description": "Attach this video as a creative on a campaign.",
    "sendToCampaign.loading": "Loading campaigns…",
    "sendToCampaign.tab.existing": "Existing campaign",
    "sendToCampaign.tab.new": "New campaign",
    "sendToCampaign.pickLabel": "Choose a campaign",
    "sendToCampaign.pickPlaceholder": "Select a campaign…",
    "sendToCampaign.noCampaigns": "You don't have any campaigns yet. Create one below.",
    "sendToCampaign.newNameLabel": "Campaign name",
    "sendToCampaign.newNamePlaceholder": "e.g. Spring Launch",
    "sendToCampaign.cancel": "Cancel",
    "sendToCampaign.submit": "Send",
    "sendToCampaign.defaultName": "AI Studio video",
    "sendToCampaign.error.nameRequired": "Please enter a campaign name",
    "sendToCampaign.error.pickCampaign": "Please pick a campaign",
    "sendToCampaign.toast.sent": "Video sent to campaign",
    "sendToCampaign.toast.view": "View campaign",
    "sidebar.competitors": "Competitor Research",
    "sidebar.audiences": "Audience Targeting",
    "audiences.eyebrow": "Audience targeting",
    "audiences.title": "Build smarter audiences",
    "audiences.subtitle": "Create custom, retargeting, and lookalike audiences. Sync them to Meta, TikTok, and Google when your ad accounts are connected.",
    "audiences.status.ready": "Ready",
    "audiences.status.pending": "Pending API connection",
    "audiences.tabs.custom": "Custom Audiences",
    "audiences.tabs.retarget": "Retargeting",
    "audiences.tabs.lookalike": "Lookalike",
    "audiences.field.name": "Audience name",
    "audiences.custom.title": "Custom audience",
    "audiences.custom.desc": "Build an audience from your website visitors or by uploading a customer list (CSV).",
    "audiences.custom.namePlaceholder": "e.g. Site visitors – last 30 days",
    "audiences.custom.website": "From a website URL",
    "audiences.custom.upload": "From a customer list",
    "audiences.custom.contacts": "contacts",
    "audiences.custom.createFromWebsite": "Create from website",
    "audiences.custom.createFromList": "Create from list",
    "audiences.custom.listTitle": "Your custom audiences",
    "audiences.custom.empty": "No custom audiences yet. Create your first one above.",
    "audiences.retarget.title": "Retargeting audience",
    "audiences.retarget.desc": "Target people who saw your ad but didn't convert.",
    "audiences.retarget.namePlaceholder": "e.g. Spring sale – non-converters",
    "audiences.retarget.campaign": "Source campaign",
    "audiences.retarget.pickCampaign": "Pick a campaign",
    "audiences.retarget.noCampaigns": "No campaigns yet — create one first.",
    "audiences.retarget.window": "Lookback window",
    "audiences.retarget.days": "days",
    "audiences.retarget.conditionLabel": "Condition: saw the ad but did not convert.",
    "audiences.retarget.create": "Create retargeting audience",
    "audiences.retarget.listTitle": "Your retargeting audiences",
    "audiences.retarget.empty": "No retargeting audiences yet.",
    "audiences.lookalike.title": "Lookalike audience",
    "audiences.lookalike.desc": "Find new people who behave like your best customers.",
    "audiences.lookalike.namePlaceholder": "e.g. Top buyers — 1% lookalike",
    "audiences.lookalike.source": "Source audience",
    "audiences.lookalike.pickSource": "Pick a custom audience",
    "audiences.lookalike.noSources": "Create a custom audience first.",
    "audiences.lookalike.country": "Country",
    "audiences.lookalike.similarity": "Similarity",
    "audiences.lookalike.similarityHint": "Lower % = closer to your source. Higher % = wider reach.",
    "audiences.lookalike.create": "Create lookalike",
    "audiences.lookalike.listTitle": "Your lookalike audiences",
    "audiences.lookalike.empty": "No lookalike audiences yet.",
    "audiences.error.nameRequired": "Please enter a name.",
    "audiences.error.urlRequired": "Please enter a website URL.",
    "audiences.error.fileRequired": "Please upload a customer list.",
    "audiences.error.retargetFields": "Please enter a name and pick a campaign.",
    "audiences.error.lookalikeFields": "Please enter a name and pick a source audience.",
    "audiences.error.createFailed": "Couldn't save audience. Please try again.",
    "audiences.toast.customCreated": "Custom audience saved.",
    "audiences.toast.retargetCreated": "Retargeting audience saved.",
    "audiences.toast.lookalikeCreated": "Lookalike audience saved.",
    "competitorsPage.eyebrow": "Competitor research",
    "competitorsPage.title": "Spy on your competitors",
    "competitorsPage.subtitle": "Search a brand or page, see every active ad they're running, how long it has been live, and an AI plan to outperform them.",
    "competitorsPage.searchPlaceholder": "Enter competitor name or website (e.g. nike.com)",
    "competitorsPage.searchCta": "Search ads",
    "competitorsPage.error.emptyQuery": "Enter a competitor to search.",
    "competitorsPage.error.searchFailed": "Couldn't load ads. Please try again.",
    "competitorsPage.error.insightFailed": "Couldn't generate insights.",
    "competitorsPage.mockBadge": "Showing simulated data",
    "competitorsPage.mockDesc": "Live Meta Ad Library data is coming soon. The layout, filters, and AI analysis will work the same once it's wired in.",
    "competitorsPage.empty.title": "Pick a competitor to start",
    "competitorsPage.empty.desc": "Try a brand name like \"Gymshark\" or a domain like \"hubspot.com\". We'll pull every active ad we can see.",
    "competitorsPage.platformSpy.title": "Platform Spy",
    "competitorsPage.platformSpy.desc": "Where {brand} is currently running ads.",
    "competitorsPage.ads.title": "{brand}'s active ads",
    "competitorsPage.ads.summary": "{count} ads · {long} long-running",
    "competitorsPage.ads.longBadge": "Long running = working",
    "competitorsPage.ads.testingBadge": "Testing",
    "competitorsPage.ads.runningFor": "Running {days} days",
    "competitorsPage.insight.title": "AI Insight — how to beat them",
    "competitorsPage.insight.desc": "Our AI reviews their ads and gives you a concrete plan tailored to your campaigns.",
    "competitorsPage.insight.cta": "Generate AI insights",
    "competitorsPage.insight.regenerate": "Regenerate",
    "competitorsPage.insight.loading": "Analyzing competitor ads…",
    "competitorsPage.insight.working": "What's working for them",
    "competitorsPage.insight.weak": "Where they're weak",
    "competitorsPage.insight.beat": "How to beat them",
    "competitorsPage.insight.hooks": "Hooks to test",
    "lipsync.title": "Spanish Lip Sync",
    "lipsync.subtitle": "Dub a video to Spanish and generate a lip-synced avatar version automatically.",
    "lipsync.tabs.upload": "Upload video",
    "lipsync.tabs.library": "From library",
    "lipsync.upload.cta": "Click to upload a video",
    "lipsync.upload.hint": "MP4, MOV, WebM up to 100 MB",
    "lipsync.library.empty": "No videos in your library yet. Generate one above first.",
    "lipsync.library.placeholder": "Pick a video from your library",
    "lipsync.library.start": "Start Spanish lip sync",
    "lipsync.libraryItem": "Untitled video",
    "lipsync.step.uploading": "Uploading",
    "lipsync.step.dubbing": "Dubbing to Spanish",
    "lipsync.step.lipsync": "Lip sync",
    "lipsync.step.ready": "Ready",
    "lipsync.actions.download": "Download",
    "lipsync.actions.sendToCampaign": "Send to Campaign",
    "lipsync.actions.startOver": "Start over",
    "lipsync.toast.signInRequired": "Please sign in to upload videos.",
    "lipsync.toast.invalidFileType": "Please choose a video file.",
    "lipsync.toast.fileTooBig": "Video must be under 100 MB.",
    "lipsync.toast.uploadFailed": "Upload failed",
    "lipsync.toast.dubFailed": "Spanish dubbing failed",
    "lipsync.toast.dubTimeout": "Dubbing took too long. Please try a shorter clip.",
    "lipsync.toast.transcriptFailed": "Couldn't fetch the Spanish transcript.",
    "lipsync.toast.lipsyncFailed": "Lip sync generation failed",
    "lipsync.toast.lipsyncTimeout": "Lip sync took too long. Try again.",
    "lipsync.toast.ready": "Spanish lip-synced video is ready!",
    "notifications.aria": "Notifications",
    "notifications.title": "Notifications",
    "notifications.markAllRead": "Mark all as read",
    "notifications.empty": "You're all caught up.",
    "notifications.manage": "Notification settings",
    "notifications.costAlert.title": "Cost alert triggered",
    "notifications.costAlert.body": "{metric} on {campaign} crossed your threshold.",
    "notifications.abWinner.title": "A/B test winner detected",
    "notifications.abWinner.body": "{name}: variant {winner} is leading on ROAS/CPA.",
    "notifications.autoPause.title": "Campaign auto-paused",
    "notifications.autoPause.body": "{campaign} was paused: {reason}",
    "notifications.deletion.title": "Data deletion request submitted",
    "notifications.deletion.body": "Request {id} received. We'll email you with updates.",
    "whitelabel.tab": "Branding",
    "whitelabel.title": "White-label branding",
    "whitelabel.description": "Replace AdKavi branding inside your dashboard with your own logo, color, and company name.",
    "whitelabel.lockedDesc": "White-label is available on the {plan} plan and above.",
    "whitelabel.upgrade": "Upgrade plan",
    "whitelabel.enableLabel": "Enable white-label",
    "whitelabel.enableDesc": "Show your branding instead of AdKavi inside the dashboard.",
    "whitelabel.companyNameLabel": "Company name",
    "whitelabel.companyNamePlaceholder": "Acme Agency",
    "whitelabel.companyNameHelp": "Replaces the AdKavi name in the sidebar and tab titles.",
    "whitelabel.logoLabel": "Logo",
    "whitelabel.logoHelp": "PNG, JPG, SVG or WebP. Recommended 256×256, max 2 MB.",
    "whitelabel.uploadLogo": "Upload logo",
    "whitelabel.removeLogo": "Remove logo",
    "whitelabel.colorLabel": "Primary brand color",
    "whitelabel.colorHelp": "Used for buttons, highlights, and active states across the dashboard.",
    "whitelabel.previewLabel": "Preview",
    "whitelabel.save": "Save branding",
    "whitelabel.toast.saved": "Branding updated",
    "whitelabel.toast.saveFailed": "Couldn't save branding",
    "whitelabel.toast.uploaded": "Logo uploaded",
    "whitelabel.toast.uploadFailed": "Logo upload failed",
    "whitelabel.toast.tooLarge": "Logo must be 2 MB or smaller",
    "webhooks.tab": "Webhooks",
    "webhooks.title": "Webhooks",
    "webhooks.description": "Send real-time event data to Zapier, Make, or any HTTPS endpoint.",
    "webhooks.add.title": "Add a webhook",
    "webhooks.add.label": "Label",
    "webhooks.add.url": "Endpoint URL",
    "webhooks.add.events": "Events to send",
    "webhooks.add.cta": "Add webhook",
    "webhooks.empty": "No webhooks yet. Add one above to start delivering events.",
    "webhooks.loading": "Loading webhooks…",
    "webhooks.enabled": "Enabled",
    "webhooks.disabled": "Disabled",
    "webhooks.toggleEnabled": "Toggle webhook",
    "webhooks.test": "Test",
    "webhooks.delete": "Delete",
    "webhooks.confirmDelete": "Delete this webhook?",
    "webhooks.secret": "Signing secret",
    "webhooks.toggleSecret": "Show/hide secret",
    "webhooks.lastDelivery": "Last delivery",
    "webhooks.event.campaign.launched": "Campaign launched",
    "webhooks.event.ad.paused": "Ad paused",
    "webhooks.event.ab.winner_declared": "A/B winner declared",
    "webhooks.event.cost_alert.triggered": "Cost alert triggered",
    "webhooks.toast.added": "Webhook added",
    "webhooks.toast.deleted": "Webhook deleted",
    "webhooks.toast.badUrl": "URL must start with http:// or https://",
    "webhooks.toast.noEvents": "Pick at least one event",
    "webhooks.toast.testOk": "Test delivered ({status})",
    "webhooks.toast.testFail": "Test failed: {error}",

    // Navbar labels
    "nav.home": "Home",
    "nav.agencies": "Agencies",

    // Footer
    "footer.slogan": "Create. Launch. Scale.",

    // FAQ
    "faq.title": "Got Questions? We Have",
    "faq.titleHighlight": "Answers.",
    "faq.q1": "Do I need experience running ads to use AdKavi?",
    "faq.a1": "Not at all. AdKavi was built for people with zero ads experience. Our AI handles everything — you just describe your business and your goal and AdKavi does the rest.",
    "faq.q2": "Will AdKavi work for my industry?",
    "faq.a2": "Yes. AdKavi works for any business — e-commerce, real estate, healthcare, restaurants, coaches, agencies, content creators and more. If you sell something AdKavi can help you grow it.",
    "faq.q3": "Do I need a big ad budget?",
    "faq.a3": "No. You can start with as little as $5 per day. AdKavi optimizes your budget automatically so every dollar works as hard as possible.",
    "faq.q4": "Is my business data and ad account safe?",
    "faq.a4": "100%. We connect via official OAuth — we never see or store your passwords. All data is fully encrypted and never sold or shared with anyone.",
    "faq.q5": "Can I cancel anytime?",
    "faq.a5": "Yes. No contracts, no commitments. Cancel anytime directly from your dashboard in one click. No questions asked.",
    "faq.q6": "What platforms does AdKavi support?",
    "faq.a6": "Meta (Facebook & Instagram), TikTok, Google Search & Display, and YouTube — with more platforms coming soon.",
    "faq.q7": "How is AdKavi different from hiring a marketing agency?",
    "faq.a7": "AdKavi gives you everything an agency does — strategy, creative, campaigns, optimization, and reporting — at a fraction of the cost, with full transparency and control. No retainers, no waiting, no guessing.",
    "faq.q8": "What happens if I cancel my founding member plan?",
    "faq.a8": "If you cancel your founding member subscription and come back later, the founding price may no longer be available. You would need to rejoin at the regular price, currently $297/mo. Founding member pricing stays active as long as your subscription remains active.",

    // ValueStack
    "valuestack.title": "What",
    "valuestack.titleHighlight": "AdKavi",
    "valuestack.titleEnd": "Replaces",
    "valuestack.copywriter": "Copywriter",
    "valuestack.videoEditor": "Video Editor",
    "valuestack.adManager": "Ad Manager",
    "valuestack.designTool": "Design Tool",
    "valuestack.total": "Total if hired separately:",
    "valuestack.starter": "AdKavi Launch: $149/mo",
    "valuestack.savings": "That's 97% less — with better results.",
    "valuestack.cta": "Join Waitlist",

    // Comparison
    "comparison.title": "Why Smart Businesses Choose",
    "comparison.bestValue": "BEST VALUE",
    "comparison.col.agency": "Marketing Agency",
    "comparison.col.diy": "DIY Ads",
    "comparison.col.tools": "Separate Tools",
    "comparison.row.cost": "Monthly Cost",
    "comparison.row.launch": "Time to Launch First Ad",
    "comparison.row.copyAi": "AI Writes Your Ad Copy",
    "comparison.row.videoAi": "AI Video Creation (No Recording)",
    "comparison.row.voice": "Voice Cloning",
    "comparison.row.multiplatform": "Multi-Platform from One Dashboard",
    "comparison.row.optimize": "24/7 Auto-Optimization",
    "comparison.row.spy": "Competitor Ad Spying",
    "comparison.row.cancel": "Cancel Anytime",
    "comparison.row.creative": "Ad Creative Builder",
    "comparison.row.carousel": "AI Carousel Ads",
    "comparison.row.image": "AI Image Ads",
    "comparison.cell.adkavi.cost": "From $149/mo",
    "comparison.cell.agency.cost": "$2,000–$10,000+",
    "comparison.cell.diy.cost": "Wasted ad spend",
    "comparison.cell.tools.cost": "$178–$400+/mo",
    "comparison.cell.tools.cost.note": "(6+ apps combined)",
    "comparison.cell.adkavi.launch": "Under 10 min",
    "comparison.cell.agency.launch": "2–6 weeks",
    "comparison.cell.diy.launch": "Days of setup",
    "comparison.cell.tools.launch": "Manual setup",

    // ForBusinesses
    "forbiz.eyebrow": "For Businesses",
    "forbiz.title": "Built for owners who need",
    "forbiz.titleHighlight": "real customers",
    "forbiz.titleEnd": "— fast.",
    "forbiz.desc": "From the dinner rush to closing day — AdKavi writes, designs, and launches what brings them in.",
    "forbiz.biz1.label": "Restaurant Owner",
    "forbiz.biz1.outcome": "Fill empty tables every weekend",
    "forbiz.biz2.label": "Real Estate Agent",
    "forbiz.biz2.outcome": "Get listing inquiries on autopilot",
    "forbiz.biz3.label": "Credit Repair Co.",
    "forbiz.biz3.outcome": "Generate 100+ qualified leads/mo",
    "forbiz.biz4.label": "Boutique Store",
    "forbiz.biz4.outcome": "Turn followers into paying buyers",
    "forbiz.biz5.label": "Gym Owner",
    "forbiz.biz5.outcome": "Sign new members weekly",
    "forbiz.biz6.label": "Car Dealership",
    "forbiz.biz6.outcome": "Drive showroom visits with video ads",
    "forbiz.output1": "AI Video Ads",
    "forbiz.output2": "Carousels & Posts",
    "forbiz.output3": "Full Ad Campaigns",
    "forbiz.banner": "All launched to Meta · TikTok · Google — in minutes",

    // SeeInAction
    "seeinaction.title": "Launch Your First AI Campaign in",
    "seeinaction.titleHighlight": "Under 10 Minutes.",
    "seeinaction.desc": "Watch how real businesses go from zero to running ads on Meta, TikTok, Google & YouTube — in one afternoon.",
    "seeinaction.playAria": "Play demo video",
    "seeinaction.watchDemo": "Watch the 60-second AdKavi demo",
    "seeinaction.comingSoon": "Demo video coming soon",

    // Problem stats
    "problem.stat1.value": "Hours",
    "problem.stat1.label": "WASTED EVERY WEEK",
    "problem.stat1.desc": "Complex platforms, constant context-switching, and manual setup drain time you should be spending on your business.",
    "problem.stat2.value": "Thousands",
    "problem.stat2.label": "SPENT ON AGENCIES",
    "problem.stat2.desc": "High retainer fees, slow turnarounds, no transparency, and generic creative that rarely moves the needle.",
    "problem.stat3.value": "Most",
    "problem.stat3.label": "AD BUDGETS WASTED WITHOUT A SYSTEM",
    "problem.stat3.desc": "Without AI optimization and fresh creative, ad spend quietly burns with little measurable return.",

    // Pricing
    "pricing.foundingOffer": "Limited to the first 100 total founding members",

    // AccountTypeOnboarding
    "onboarding.welcome": "Welcome to AdKavi 👋",
    "onboarding.desc": "Tell us who you are so we can show the right plans and tools for you.",
    "onboarding.business.title": "I'm a business or creator",
    "onboarding.business.desc": "Run ads for one brand or account.",
    "onboarding.agency.title": "I'm an agency",
    "onboarding.agency.desc": "Manage multiple client accounts in one place.",
    "onboarding.footer": "You can change this anytime from Settings.",
    "onboarding.saving": "Saving…",

    // Integration connect dialogs
    "connect.meta.title": "Connect your Meta account",
    "connect.meta.demoNote": "Demo mode: live OAuth is pending Meta app review.",
    "connect.meta.login.title": "Sign in to Facebook",
    "connect.meta.login.desc": "Mock login screen — no credentials are sent anywhere.",
    "connect.meta.email": "Email or phone",
    "connect.meta.password": "Password",
    "connect.meta.emailMismatch.title": "Email mismatch",
    "connect.meta.adkavi.email": "AdKavi email",
    "connect.meta.meta.email": "Meta email",
    "connect.meta.choose.title": "Choose an ad account",
    "connect.meta.choose.desc": "Select which Meta ad account AdKavi should connect to.",
    "connect.tiktok.title": "Connect your TikTok Ads account",
    "connect.tiktok.continue": "Continue with TikTok",
    "connect.google.title": "Connect your Google Ads account",
    "connect.google.continue": "Continue with Google",
    "connect.redirecting": "Redirecting…",
    "connect.disconnect": "Disconnect",
    "connect.disconnecting": "Disconnecting…",
    "connect.meta.desc": "Sync ad performance from Facebook & Instagram.",
    "connect.tiktok.desc": "Sync ad performance from TikTok Ads Manager.",
    "connect.google.desc": "Sync ad performance from Google Ads — includes YouTube as a placement.",

    // AdAccount coming soon
    "adaccount.title": "Connect {platform}",
    "adaccount.badge": "Coming soon",
    "adaccount.desc": "Ad account connection is coming soon. Once connected, you'll be able to launch ads to {platform} directly from AdKavi.",
    "adaccount.feature1.title": "One-click ad launches",
    "adaccount.feature1.desc": "Push approved creatives straight to your ad account.",
    "adaccount.feature2.title": "Real-time spend & reach",
    "adaccount.feature2.desc": "See live performance, spend, and ROAS inside AdKavi.",
    "adaccount.feature3.title": "Full compliance controls",
    "adaccount.feature3.desc": "Age, geo, and content restrictions enforced at the source.",
    "adaccount.gotIt": "Got it",

    // Agency waitlist dialog
    "agency.waitlist.title": "Join the Waitlist",
    "agency.waitlist.desc": "Be the first to know when AdKavi goes live.",
    "agency.waitlist.success.title": "You're on the list!",
    "agency.waitlist.success.desc": "We'll reach out when AdKavi goes live.",
    "agency.waitlist.close": "Close",
    "agency.waitlist.fullName": "Full Name",
    "agency.waitlist.agencyName": "Agency Name",
    "agency.waitlist.email": "Email",
    "agency.waitlist.businessType": "Business Type",
    "agency.waitlist.placeholder": "Select your business type",
    "agency.waitlist.joining": "Joining…",
    "agency.waitlist.join": "Join Waitlist",

    // AgencyHero
    "agency.hero.badge": "Built for agencies",
    "agency.hero.title1": "Run Your Clients' Ads.",
    "agency.hero.title2": "Scale Your Agency.",
    "agency.hero.desc": "AdKavi gives agencies one dashboard to manage every client's campaigns across Meta, TikTok, Google & YouTube — with AI doing the heavy lifting.",
    "agency.hero.joinWaitlist": "Join Waitlist",
    "agency.hero.seePricing": "See Agency Pricing",

    // AgencyBenefits
    "agency.benefits.title": "What Agencies Get",
    "agency.benefits.desc": "Everything you need to manage clients, launch campaigns, and prove results.",
    "agency.benefits.c1.title": "Client Management",
    "agency.benefits.c1.desc": "Switch between client accounts instantly. Each client sees only their own data.",
    "agency.benefits.c2.title": "Unified Dashboard",
    "agency.benefits.c2.desc": "All client campaigns, spend, and ROAS in one view. No more tab switching.",
    "agency.benefits.c3.title": "AI Does The Work",
    "agency.benefits.c3.desc": "AdKavi writes the copy, builds the creative, targets the audience, and optimizes automatically.",
    "agency.benefits.c4.title": "Client Reporting",
    "agency.benefits.c4.desc": "Share a live performance dashboard directly with each client showing their spend, ROAS, clicks, and leads in real time.",

    // AgencyFeatures
    "agency.features.title": "Everything You Can Do For Every Client",
    "agency.features.desc": "One AdKavi account. Every tool your clients need to grow.",
    "agency.features.f1.title": "Multi-Platform Ads",
    "agency.features.f1.desc": "Launch campaigns on Meta, TikTok, Google Search & Display, and YouTube for any client with one click.",
    "agency.features.f2.title": "AI Avatar Videos",
    "agency.features.f2.desc": "Generate professional video ads using your client's AI avatar — no filming required.",
    "agency.features.f3.title": "Voice Cloning",
    "agency.features.f3.desc": "Clone any client's voice in 30 seconds. Every voiceover sounds like them automatically.",
    "agency.features.f4.title": "Content Scheduling",
    "agency.features.f4.desc": "Plan and auto-publish posts to Meta, TikTok & YouTube for every client.",
    "agency.features.f5.title": "Competitor Research",
    "agency.features.f5.desc": "Show clients exactly what their competitors are running and how to beat them.",
    "agency.features.f6.title": "Audience Targeting",
    "agency.features.f6.desc": "Custom, retargeting, and lookalike audiences built automatically for each client.",
    "agency.features.f7.title": "Client Analytics",
    "agency.features.f7.desc": "Share a live dashboard with each client showing spend, ROAS, clicks, and leads.",
    "agency.features.f8.title": "AI Budget Optimizer",
    "agency.features.f8.desc": "AI automatically shifts each client's budget toward their best performing ads.",
    "agency.features.f9.title": "Auto-Pause Protection",
    "agency.features.f9.desc": "Stops wasting client money if an ad underperforms for 48 hours.",
    "agency.features.f10.title": "AI Ad Copywriter",
    "agency.features.f10.desc": "AI writes headlines, descriptions, and CTAs for every client campaign instantly.",
    "agency.features.f11.title": "AI Lead Agent Per Client",
    "agency.features.f11.desc": "Each client gets their own AI agent that learns their business and responds to leads on WhatsApp, Instagram DMs, and Facebook Messenger automatically.",
    "agency.features.f12.title": "Client Isolation",
    "agency.features.f12.desc": "Every client sees only their own data. Zero cross-contamination between accounts.",

    // AgencyHowItWorks
    "agency.how.title": "How It Works For Agencies",
    "agency.how.desc": "From onboarding to reporting in three steps.",
    "agency.how.step": "STEP",
    "agency.how.s1.title": "Add your clients",
    "agency.how.s1.desc": "Create a client profile, connect their ad accounts.",
    "agency.how.s2.title": "Launch campaigns",
    "agency.how.s2.desc": "AI builds and launches across all platforms simultaneously.",
    "agency.how.s3.title": "Report results",
    "agency.how.s3.desc": "Share live performance dashboards with clients directly.",

    // AgencyCtaBanner
    "agency.cta.title": "Stop Juggling Tools. Start Scaling.",
    "agency.cta.desc": "Join agencies already on the waitlist.",
    "agency.cta.button": "Join the Waitlist",

    // Video Library
    "video.library.title": "My Video Library",
    "video.library.empty": "No videos yet",
    "video.library.deleteTitle": "Delete this video?",
    "video.library.cancel": "Cancel",
    "video.library.campaign": "Campaign: ",

    // Credits / Avatar / Voice
    "credits.dialog.title": "Add Video Credits",
    "credits.total": "Total",
    "avatar.dialog.title": "Create Your AI Avatar",
    "avatar.uploading": "Uploading…",
    "avatar.choose": "Choose video file",
    "voice.dialog.title": "Clone Your Voice",
    "voice.uploading": "Uploading…",
    "voice.choose": "Choose audio file",

    // Dashboard Preview
    "dashboard.title": "See Every Result",
    "dashboard.titleHighlight": "In Real Time",
    "dashboard.sampleNote": "Sample data shown for illustration purposes",
    "dashboard.desc": "AdKavi tracks every metric that matters so you always know exactly how your ads and content are performing.",
    "dashboard.cardTitle": "Campaign Dashboard",
    "dashboard.cardPeriod": "Last 30 days",
    "dashboard.cardLive": "Live data",
    "dashboard.metric.spend": "Total Ad Spend",
    "dashboard.metric.reach": "Reach",
    "dashboard.metric.clicks": "Clicks",
    "dashboard.metric.leads": "Leads Generated",
    "dashboard.metric.sales": "Sales Conversions",
    "dashboard.metric.cpl": "Cost Per Lead",
    "dashboard.metric.roas": "ROAS",
    "dashboard.metric.topCampaign": "Top Campaign",
    "dashboard.metric.topCampaignValue": "Spring Promo",
    "dashboard.footer": "Connect your Facebook account and see your live data populate instantly. No spreadsheets. No guessing. Just results.",
    "dashboard.stat1": "Faster ad creation",
    "dashboard.stat2": "A fraction of agency costs",
    "dashboard.stat3": "Better ROI on ad spend",
    "dashboard.stat4Suffix": "AI working in background",

    // AI Agent Section
    "aiagent.badge": "New Feature",
    "aiagent.title": "Never Miss a Lead Again.",
    "aiagent.subtitle": "Your AI Agent Responds For You.",
    "aiagent.desc": "AdKavi studies your Instagram, Facebook, and website — then responds to every WhatsApp message, Instagram DM, and Facebook message automatically using your tone, your pricing, and your offers.",
    "aiagent.c1.title": "Business Brain",
    "aiagent.c1.desc": "AI reads your social profiles and website to learn everything about your business before responding.",
    "aiagent.c2.title": "Multi-Channel Replies",
    "aiagent.c2.desc": "Responds on WhatsApp, Instagram DMs, and Facebook Messenger from one place. Available channels vary by plan.",
    "aiagent.c3.title": "24/7 Availability",
    "aiagent.c3.desc": "Every lead gets an instant reply even while you sleep, on weekends, and on holidays.",
    "aiagent.cta": "Join Waitlist",

    // ROI Savings
    "roi.title": "See Exactly How Much You're",
    "roi.titleHighlight": "Wasting Right Now",
    "roi.desc": "Enter your current numbers and see what AdKavi saves you.",
    "roi.feeLabel": "Monthly Agency or Freelancer Fee",
    "roi.spendLabel": "Monthly Ad Spend",
    "roi.spending": "You're spending {amount}/month on management fees",
    "roi.withAdkavi": "With AdKavi Launch ($149/mo) you save",
    "roi.perMonth": "per month",
    "roi.perYear": "per year",
    "roi.pocketPre": "That's",
    "roi.pocketSuf": "back in your pocket",
    "roi.cta": "Start Saving Today — Free 14-Day Trial",

    // Features Lead Agent card
    "features.leadagent.title": "AI Lead Agent",
    "features.leadagent.desc": "Connect your WhatsApp, Instagram DMs, and Facebook Messenger. AdKavi's AI studies your business and responds to every lead automatically — 24/7.",

    // Common
    "common.cancel": "Cancel",
    "common.saving": "Saving…",
    "common.loading": "Loading…",
    "common.uploading": "Uploading…",
    "app.signOut": "Sign out",

    // Feature Showcase
    "showcase.eyebrow": "See It In Action",
    "showcase.title": "Every Tool. One Platform.",
    "showcase.desc": "Pick any feature and watch AdKavi work — from ad creation to lead responses to live reporting.",
    // AI Showcase section
    "aiShowcase.eyebrow": "Try It Live",
    "aiShowcase.title.1": "Watch AI Build",
    "aiShowcase.title.2": "Your Ads.",
    "aiShowcase.subtitle": "Type any business below and Claude writes a real ad in seconds — English and Spanish simultaneously.",
    "aiShowcase.tab.claude": "Live Ad Writer",
    "aiShowcase.tab.images": "AI Images",
    "aiShowcase.tab.video": "Avatar Video",
    "aiShowcase.tab.voice": "Voiceover",
    "aiShowcase.tab.bilingual": "EN vs ES",
    "aiShowcase.claude.label": "Describe your business",
    "aiShowcase.claude.placeholder": "e.g. Credit repair for Spanish-speaking families in Miami",
    "aiShowcase.claude.goal": "Campaign goal",
    "aiShowcase.claude.goal.leads": "Lead Generation",
    "aiShowcase.claude.goal.sales": "Sales",
    "aiShowcase.claude.goal.awareness": "Brand Awareness",
    "aiShowcase.claude.goal.traffic": "Website Traffic",
    "aiShowcase.claude.generate": "Generate My Ad",
    "aiShowcase.claude.generating": "Claude is writing your ad…",
    "aiShowcase.claude.tryAnother": "Try another business",
    "aiShowcase.claude.hint": "Real Claude AI · No signup required · Your data is never stored",
    "aiShowcase.claude.error": "Generation failed — try again.",
    "aiShowcase.images.provider": "Recraft",
    "aiShowcase.images.title": "AI-Generated Ad Creatives",
    "aiShowcase.images.subtitle": "Your Ad. Your Brand. Generated in seconds.",
    "aiShowcase.images.placeholder": "Sample images",
    "aiShowcase.images.runScript": "Run scripts/generate-samples.ts to populate",
    "aiShowcase.images.bullet.1": "Upload your photo or generate from a text prompt",
    "aiShowcase.images.bullet.2": "Match your brand colors automatically",
    "aiShowcase.images.bullet.3": "Carousel, single image, or video thumbnail formats",
    "aiShowcase.images.bullet.4": "Background removal built in",
    "aiShowcase.images.bullet.5": "Animate your still image to video with Luma AI",
    "aiShowcase.images.bullet.6": "Ready to publish — no design tools needed",
    "aiShowcase.video.provider": "HeyGen",
    "aiShowcase.video.title": "AI Avatar Ad",
    "aiShowcase.video.subtitle": "Your Face. Your Voice. Zero Recording Required.",
    "aiShowcase.video.placeholder": "Sample video",
    "aiShowcase.video.runScript": "Invoke the generate-samples edge function to populate",
    "aiShowcase.video.processing": "Avatar video is rendering…",
    "aiShowcase.video.processingNote": "HeyGen typically takes 3–8 minutes. Check back shortly.",
    "aiShowcase.video.checkStatus": "Check status",
    "aiShowcase.video.bullet.1": "Record once, clone your voice and face forever",
    "aiShowcase.video.bullet.2": "Type your script — your avatar delivers it on camera",
    "aiShowcase.video.bullet.3": "Spanish lip sync: same avatar, same face, Spanish audio",
    "aiShowcase.video.bullet.4": "30 avatar videos/month on Dominate plans",
    "aiShowcase.video.bullet.5": "Goes straight into your campaign — no editing needed",
    "aiShowcase.voice.provider": "ElevenLabs",
    "aiShowcase.voice.title": "AI Voiceover",
    "aiShowcase.voice.subtitle": "Your Voice. Every Video. Automatically.",
    "aiShowcase.voice.script": "Script",
    "aiShowcase.voice.placeholder": "Sample voiceover",
    "aiShowcase.voice.runScript": "Run scripts/generate-samples.ts to populate",
    "aiShowcase.voice.bullet.1": "30-second recording — AI duplicates your voice",
    "aiShowcase.voice.bullet.2": "Sounds like you, not a robot",
    "aiShowcase.voice.bullet.3": "Translate and dub to Spanish automatically",
    "aiShowcase.voice.bullet.4": "Works on avatar videos, ads, and content",
    "aiShowcase.bilingual.en": "English",
    "aiShowcase.bilingual.es": "Spanish",
    "aiShowcase.bilingual.title": "Same brief. Two languages.",
    "aiShowcase.bilingual.subtitle": "One Business. Two Markets. Zero Extra Work.",
    "aiShowcase.bilingual.note": "Sample generated live above — one click, two markets.",
    "aiShowcase.bilingual.bullet.1": "Every ad written in English AND Spanish simultaneously",
    "aiShowcase.bilingual.bullet.2": "Avatar videos dubbed and lip-synced in Spanish",
    "aiShowcase.bilingual.bullet.3": "DM auto-replies in the customer's language",
    "aiShowcase.bilingual.bullet.4": "Reach 41M Spanish speakers in the US",
    "aiShowcase.cta": "Join Waitlist — Try It Free",
    "aiShowcase.animate.button": "Animate My Ad",
    "aiShowcase.animate.animating": "Luma AI is animating your ad… ~30 seconds",
    "aiShowcase.animate.tooltip": "Turn this image into a 5-second video ad with Luma AI",
    "inbox.title": "Conversations Inbox",
    "inbox.subtitle": "AI-handled messages across WhatsApp, Instagram DMs, and Facebook Messenger.",
    "inbox.refresh": "Refresh",
    "inbox.stat.total": "Total",
    "inbox.stat.replied": "Replied",
    "inbox.stat.pending": "Pending",
    "inbox.stat.failed": "Failed",
    "inbox.filter.allChannels": "All channels",
    "inbox.filter.allStatuses": "All statuses",
    "inbox.filter.channel": "Channel",
    "inbox.filter.status": "Status",
    "inbox.filter.clear": "Clear",
    "inbox.search.placeholder": "Search contact, message…",
    "inbox.col.channel": "Channel",
    "inbox.col.contact": "Contact",
    "inbox.col.message": "Message",
    "inbox.col.status": "Status",
    "inbox.loading": "Loading conversations…",
    "inbox.empty.title": "No conversations yet",
    "inbox.empty.desc": "Once your AI agent is live and Meta webhooks are connected, conversations will appear here in real time.",
    "inbox.empty.setup": "Set up AI Agent →",
    "inbox.empty.noMatch": "No conversations match your filters.",
    "inbox.showing": "Showing {shown} of {total} conversations",
    "inbox.expanded.lead": "Lead message",
    "inbox.expanded.aiReply": "AI reply",
    "inbox.status.replied": "replied",
    "inbox.status.pending": "pending",
    "inbox.status.failed": "failed",
    "bench.badge": "Performance benchmarks",
    "bench.title": "Industry Benchmarks",
    "bench.desc": "Compare your campaigns against industry averages for click-through rate, cost per click, cost per lead, and return on ad spend. Numbers are blended across Meta, TikTok and Google.",
    "bench.higher": "Higher is better",
    "bench.lower": "Lower is better",
    "bench.col.industry": "Industry",
    "bench.col.ctr": "CTR (avg / good)",
    "bench.col.cpc": "CPC (avg / great)",
    "bench.col.cpl": "CPL (good range)",
    "bench.col.roas": "ROAS (avg / good)",
    "bench.sources": "Sources: Wordstream 2024, AdEspresso, LocaliQ. Benchmarks are directional — your specific offer, creative quality and audience overlap matter more than any single number.",
    // Checkout pages
    "checkout.loading": "Loading checkout…",
    "checkout.changePlan": "Change plan",
    "checkout.trialHeading": "Start your 14-day free trial",
    "checkout.trialDesc": "We won't charge you for 14 days. Cancel anytime from Settings → Billing.",
    "checkout.youreStarting": "You're starting",
    "checkout.perMonthAfterTrial": "/mo after trial",
    "checkout.return.title": "Your free trial is live",
    "checkout.return.desc": "You won't be charged for 14 days. Manage your plan anytime from Settings → Billing.",
    "checkout.return.cta": "Go to dashboard",
    "checkout.return.reference": "Reference:",
    "billing.title.switch": "Switch plan",
    "billing.title.choose": "Choose your plan",
    "billing.desc.switch": "You're on {plan}. Pick a new plan to switch.",
    "billing.desc.trial": "All plans include a 14-day free trial. Cancel anytime.",
    "billing.audience.creator": "For Businesses & Creators",
    "billing.audience.agency": "For Agencies",
    "billing.badge.popular": "Most Popular",
    "billing.badge.current": "Current plan",
    "billing.card.trial": "14-day free trial",
    "billing.card.perMonth": "/mo",
    "billing.card.current": "You're on this plan",
    "billing.card.choose": "Choose {name}",
    "billing.dialog.title": "Start your {name} trial",
    "billing.dialog.desc": "No charge for 14 days. Cancel anytime from Settings → Billing.",
    "dash.agent.setupTitle": "Set Up Your AI Agent",
    "dash.agent.setupDesc": "Your AI agent can auto-reply to WhatsApp, Instagram DMs, and Facebook Messenger messages 24/7 — even while you sleep.",
    "dash.agent.setupCta": "Set Up AI Agent",
    "dash.agent.name": "AI Agent",
    "dash.agent.active": "Active",
    "dash.agent.monitoring": "Monitoring WhatsApp, Instagram DMs & Messenger",
    "dash.agent.viewConvos": "View Conversations",
    "dash.agent.convosToday": "Conversations today",
    "dash.agent.lastReply": "Last reply",
    "dash.agent.justNow": "just now",
    "dash.agent.minsAgo": "{n}m ago",
    "dash.agent.hoursAgo": "{n}h ago",
    "dash.agent.daysAgo": "{n}d ago",
    "creative.guide.template": "Choose a template",
    "creative.guide.content": "Add brand & message",
    "creative.guide.colors": "Pick colors",
    "creative.guide.image": "Add an image",
    "creative.guide.save": "Save or export",
    "creative.cat.all": "All",
    "creative.cat.ads": "Ads",
    "creative.cat.photo": "Photo",
    "creative.cat.minimal": "Minimal",
    "creative.cat.promo": "Promo",
    "creative.cat.social": "Social",
    "creative.zoom.fit": "Fit",
    "creative.zoom.fitScreen": "Fit to screen",
    "creative.zoom.pct": "{pct}% zoom",
    "creative.palette.sunset": "Sunset",
    "creative.palette.midnight": "Midnight",
    "creative.palette.cream": "Cream",
    "creative.palette.indigo": "Indigo",
    "creative.palette.forest": "Forest",
    "creative.palette.peach": "Peach",
    "creative.defaultName": "Untitled creative",
    "creative.section.content": "Content",
    "creative.section.colors": "Colors",
    "creative.section.image": "Image",
    "creative.section.format": "Format",
    "creative.section.export": "Export",
    "creative.btn.save": "Save creative",
    "creative.btn.update": "Update creative",
    "creative.btn.new": "New",
    "creative.btn.saveShort": "Save",
    "creative.btn.exportPng": "Export PNG",
    "creative.btn.exportPngFmt": "Export PNG ({w}×{h})",
    "creative.btn.chooseBrandLib": "Choose from Brand Library",
    "creative.section.templates": "Templates",
    "creative.section.recent": "Recent",
    "creative.section.guide": "Setup guide",
    "creative.tpl.empty": "No templates in this category yet.",
    "creative.field.name": "Creative Name",
    "creative.field.brand": "Brand",
    "creative.field.headline": "Headline",
    "creative.field.subheadline": "Subheadline",
    "creative.field.cta": "CTA Button",
    "creative.field.bg": "BG",
    "creative.field.text": "Text",
    "creative.field.accent": "Accent",
    "creative.img.empty": "No image added yet.",
    "creative.img.emptyHint": "Used by Photo Overlay and Bold Split.",
    "creative.img.pasteUrl": "Or paste image URL…",
    "creative.img.corsHint": "For PNG export, use a CORS-friendly URL (Unsplash, your Brand Library, or Supabase Storage).",
    "creative.savedAt": "Saved {time}",
    "creative.lastSavedAt": "Last saved {time}",
    "creative.loadedFromSaved": "Loaded from saved",
    "creative.fmt.square": "Square 1:1",
    "creative.fmt.story": "Story 9:16",
    "creative.fmt.landscape": "Landscape 1.91:1",
    "creative.tpl.splitBold.name": "Bold Split",
    "creative.tpl.splitBold.desc": "Big type on a colored block, image on the side.",
    "creative.tpl.overlayHero.name": "Photo Overlay",
    "creative.tpl.overlayHero.desc": "Full-bleed photo with gradient overlay and CTA.",
    "creative.tpl.minimalMono.name": "Minimal Mono",
    "creative.tpl.minimalMono.desc": "Editorial typography on a clean background.",
    "creative.tpl.promoBanner.name": "Promo Banner",
    "creative.tpl.promoBanner.desc": "Discount-style headline with bright accent ribbon.",
    "creative.tpl.quoteCard.name": "Quote / Testimonial",
    "creative.tpl.quoteCard.desc": "Big quote, attribution underneath. Great for social proof.",
    "brand.lib.title": "My Brand Library",
    "brand.lib.desc": "Upload once, reuse across all ad tools",
    "brand.lib.upload": "Upload",
    "brand.lib.catAll": "All ({n})",
    "brand.lib.storageFull": "Storage full — delete some images to upload more.",
    "brand.lib.storageUsed": "{used} of {total} used",
    "brand.lib.emptyAll": "No brand images yet. Click to upload your first.",
    "brand.lib.emptyCat": "No {cat} yet.",
    "brand.lib.err.imagesOnly": "Images only",
    "brand.lib.err.maxSize": "Max 10 MB per image",
    "brand.lib.success.added": "Image added to Brand Library",
    "brand.lib.cat.photo": "Photos",
    "brand.lib.cat.logo": "Logos",
    "brand.lib.cat.product": "Products",
    "brand.lib.cat.background": "Backgrounds",
    "brand.lib.cat.graphic": "Graphics",
    "studio.tab.start": "Start Here",
    "studio.tab.copy": "Ad Copy",
    "studio.tab.images": "Images",
    "studio.tab.videos": "Videos",
    "studio.tab.voice": "Voice + Spanish",
    "studio.tab.library": "My Library",
    "studio.tone.confident": "Confident",
    "studio.tone.playful": "Playful",
    "studio.tone.luxury": "Luxury",
    "studio.tone.friendly": "Friendly",
    "studio.tone.urgent": "Urgent",
    "studio.tone.professional": "Professional",
    "studio.tone.bold": "Bold",
    "studio.goal.awareness": "Brand Awareness",
    "studio.goal.traffic": "Traffic",
    "studio.goal.leads": "Leads",
    "studio.goal.sales": "Sales",
    "studio.goal.appInstalls": "App Installs",
    "studio.subtitle": "Create ads, captions, images, videos, and voiceovers from one place.",
    "studio.creativeBuilderLink": "Creative Builder",
    "studio.guided.campaign.title": "Full Ad Campaign",
    "studio.guided.campaign.desc": "Ad copy, image ideas, video suggestions, and CTA for one complete offer",
    "studio.guided.post.title": "Social Media Post",
    "studio.guided.post.desc": "Caption, hashtags, and post copy for any platform",
    "studio.guided.video.title": "Video Ad",
    "studio.guided.video.desc": "Script, hook, CTA, voiceover text, and video prompt",
    "studio.guided.spanish.title": "Spanish / Bilingual Content",
    "studio.guided.spanish.desc": "English + Spanish versions, dubbing, and lip-sync options",
    "studio.guided.tool.title": "Use One Specific Tool",
    "studio.guided.tool.desc": "Browse all AI tools and pick exactly what you need",
    "studio.adWriter.title": "AI Ad Writer",
    "studio.adWriter.productLabel": "What are you advertising?",
    "studio.adWriter.audienceLabel": "Target audience (optional)",
    "studio.adWriter.offerLabel": "Offer / hook (optional)",
    "studio.adWriter.platformLabel": "Platform",
    "studio.adWriter.goalLabel": "Goal",
    "studio.adWriter.toneLabel": "Tone",
    "studio.adWriter.variationsLabel": "Variations",
    "studio.adWriter.generating": "Generating…",
    "studio.adWriter.generateBtn": "Generate ad copy",
    "studio.adWriter.poweredBy": "Powered by Claude. Outputs are AI-generated — review before publishing.",
    "studio.adWriter.variationsCount": "{n} variations generated",
    "studio.adWriter.openInCreative": "Open in Creative Builder",
    "studio.images.subtitle": "Generate AI-powered ad images, carousels, and animated visuals for your campaigns.",
    "studio.videos.lockedTitle": "AI Video Generation",
    "studio.videos.lockedDesc": "Generate talking videos, lifestyle clips, and Cinematic Pro videos. Available on Dominate and above.",
    "studio.voice.subtitle": "Generate voiceovers, clone your voice, and translate or dub content into Spanish.",
    "studio.voice.voGeneratorTitle": "Voiceover Generator",
    "studio.voice.voiceCloneTitle": "Voice Clone",
    "studio.voice.avatarSetupTitle": "AI Avatar Setup",
    "studio.voice.dubTitle": "Translate + Dub to Spanish",
    "studio.library.creditsTitle": "Video Credits Remaining",
    "studio.library.buyCredits": "Buy Credits",
    "studio.library.emptyTitle": "Your saved videos will appear here",
    "studio.library.emptyDesc": "Generate your first video to see it in your library.",
    "studio.tool.adWriter.name": "AI Ad Writer",
    "studio.tool.adWriter.desc": "Generate headlines, primary text, and CTAs",
    "studio.tool.caption.name": "Caption Writer",
    "studio.tool.caption.desc": "Social captions with hashtags",
    "studio.tool.scriptWriter.name": "Video Script Writer",
    "studio.tool.scriptWriter.desc": "Hook → body → CTA scripts",
    "studio.tool.imageAds.name": "AI Image Ads",
    "studio.tool.imageAds.desc": "Generate ad images from a prompt",
    "studio.tool.carousel.name": "Carousel Ads",
    "studio.tool.carousel.desc": "Multi-slide carousel copy & visuals",
    "studio.tool.talkingVideo.name": "Talking Video",
    "studio.tool.talkingVideo.desc": "Spokesperson video with your script — no camera needed.",
    "studio.tool.cinematicPro.name": "Cinematic Pro",
    "studio.tool.cinematicPro.desc": "AI story → 4 Kling clips → voiceover → merged video.",
    "studio.tool.lifestyleVideo.name": "Lifestyle Video",
    "studio.tool.lifestyleVideo.desc": "Cinematic AI video from a text prompt with Luma.",
    "studio.tool.voiceover.name": "Voiceover Generator",
    "studio.tool.voiceover.desc": "Lifelike narration in seconds",
    "studio.tool.dub.name": "Translate + Dub",
    "studio.tool.dub.desc": "Dub English content into Spanish",
    "studio.start.heroTitle": "Start with a guided campaign or choose a specific AI tool below.",
    "studio.start.heroSubtitle": "AdKavi guides you step-by-step — from your offer to finished ad copy, images, and videos.",
    "studio.start.startGuided": "Start Guided Campaign",
    "studio.start.browseTools": "Browse All Tools",
    "studio.start.quickAccess": "Quick Access",
    "studio.start.guideTitle": "Not sure where to start?",
    "studio.start.guide.fullCampaign.label": "Full Campaign",
    "studio.start.guide.fullCampaign.desc": "AdKavi creates everything for one offer: copy, CTAs, and suggestions.",
    "studio.start.guide.adCopy.label": "Ad Copy",
    "studio.start.guide.adCopy.desc": "Only need text? Generate headlines, captions, or scripts.",
    "studio.start.guide.images.label": "Images",
    "studio.start.guide.images.desc": "Need graphics or carousel ads? Generate branded visuals.",
    "studio.start.guide.videos.label": "Videos",
    "studio.start.guide.videos.desc": "Create Reels, TikToks, Shorts, or avatar ads.",
    "studio.start.guide.voice.label": "Voice + Spanish",
    "studio.start.guide.voice.desc": "Voiceovers, dubbing, translations, or bilingual content.",
    "studio.start.startSetup": "Start Guided Setup",
    "studio.guided.header": "Guided Campaign Setup",
    "studio.guided.step1Title": "Step 1 — What do you want to create?",
    "studio.guided.step2Title": "Step 2 — Tell us about your offer",
    "studio.guided.step3Title": "Step 3 — Your content is ready!",
    "studio.guided.step1Desc": "Choose the type of content you want to create. AdKavi will guide you through the right tools.",
    "studio.guided.step2.campaign": "We'll generate ad copy, CTA options, and suggest what to create next.",
    "studio.guided.step2.post": "We'll write captions and hashtags tailored to your audience.",
    "studio.guided.step2.video": "We'll take you to the video tools with your details pre-filled.",
    "studio.guided.step2.spanish": "We'll take you to the Voice + Spanish tools to dub or translate your content.",
    "studio.guided.audienceLabel": "Who is your target audience? (optional)",
    "studio.guided.offerLabel": "What is your offer or hook? (optional)",
    "studio.guided.continue": "Continue",
    "studio.guided.back": "Back",
    "studio.guided.step3NextTitle": "Your copy is ready! What's next?",
    "studio.guided.step3GenVisuals": "🖼️ Generate visuals",
    "studio.guided.step3CreateVideo": "🎬 Create a video",
    "studio.guided.step3Design": "Design in Creative Builder",
    "studio.guided.step3ContinueBtn": "Continue to Ad Copy tab",
    "studio.guided.step3ExitBtn": "Exit guided mode",
    "studio.var.copy": "Copy",
    "studio.var.copied": "Copied",
    "studio.var.saveToCalendar": "Save to calendar",
    "studio.empty.title": "Generate your first ad",
    "studio.empty.desc": "Fill out the form on the left, pick a platform and tone, and we'll write multiple high-converting ad variations tailored to your audience.",
    "studio.empty.alreadyHave": "Already have copy?",
    "studio.empty.creativeBuilderLink": "Go to Creative Builder",
    "studio.avatar.hint15": "Aim for ~40 words in your script for a 15-second video.",
    "studio.avatar.hint30": "Aim for ~75 words in your script for a 30-second video.",
    "studio.avatar.hint60": "Aim for ~150 words in your script for a 60-second video.",
    "studio.avatar.hint90": "Aim for ~225 words in your script for a 90-second video.",
    "studio.avatar.regenConfirm": "You've already used your free regeneration for this video. This will consume one credit. Continue?",
    "studio.avatar.regenCreditUsed": "Regenerating — 1 credit will be used",
    "studio.avatar.regenFree": "Regenerating — free regeneration",
    "studio.avatar.rendering": "Rendering avatar video — this can take 1–3 minutes",
    "studio.avatar.title": "Talking Video",
    "studio.avatar.desc": "Your branded spokesperson video — no camera needed.",
    "studio.avatar.brandImageCaption": "Brand image",
    "studio.avatar.addBrandImage": "Add brand image",
    "studio.avatar.submitting": "Submitting…",
    "studio.avatar.renderingBtn": "Rendering…",
    "studio.avatar.generateBtn": "Generate video",
    "studio.avatar.poweredBy": "Powered by HeyGen",
    "studio.regen.credit": "Regenerate (1 credit)",
    "studio.regen.free": "Regenerate (free)",
    "studio.common.download": "Download",
    "studio.vo.title": "Voiceover Generator",
    "studio.vo.desc": "Lifelike narration in seconds. Great for reels, ads, and explainers.",
    "studio.vo.generateBtn": "Generate voiceover",
    "studio.vo.downloadMp3": "Download MP3",
    "studio.vo.poweredBy": "Powered by ElevenLabs",
    "studio.luma.regenConfirm": "You've already used your free regeneration for this clip. This will consume one credit. Continue?",
    "studio.luma.toastMock": "Sample clip ready (mock)",
    "studio.luma.toastRegenCredit": "Regenerated — 1 credit used",
    "studio.luma.toastRegenFree": "Regenerated (free)",
    "studio.luma.toastReady": "Cinematic clip ready",
    "studio.luma.title": "Lifestyle Video",
    "studio.luma.desc": "Generate cinematic AI lifestyle videos from a text prompt with Luma Dream Machine.",
    "studio.luma.aspectRatio": "Aspect Ratio",
    "studio.luma.startingFrame": "Starting frame",
    "studio.luma.addStartingImage": "Add starting image",
    "studio.luma.renderingBtn": "Rendering…",
    "studio.luma.generateBtn": "Generate clip",
    "studio.luma.samplePreview": "Sample preview",
    "studio.luma.poweredBy": "Powered by Luma AI",
    "studio.script.title": "Video Script Writer",
    "studio.script.desc": "Hook → body → CTA, with on-screen text and b-roll ideas.",
    "studio.script.sec15": "15 sec",
    "studio.script.sec30": "30 sec",
    "studio.script.sec60": "60 sec",
    "studio.script.writing": "Writing…",
    "studio.script.generateBtn": "Generate script",
    "studio.script.hook": "Hook",
    "studio.script.body": "Body",
    "studio.script.cta": "CTA",
    "studio.script.onScreenText": "On-screen text",
    "studio.script.broll": "B-roll ideas",
    "studio.script.copyVO": "Copy voiceover",
    "studio.script.poweredBy": "Powered by Claude",
    "studio.dub.submitting": "Submitting…",
    "studio.dub.title": "Translate + Dub to Spanish",
    "studio.dub.desc": "Paste a public URL to your English MP3/MP4 and we'll translate and dub it to Spanish, preserving voice.",
    "studio.dub.dubbing": "Dubbing…",
    "studio.dub.generateBtn": "Dub to Spanish",
    "studio.dub.downloadMp3": "Download Spanish MP3",
    "studio.dub.timingNote": "Dubbing typically takes 1–3 minutes depending on length. Source must be publicly downloadable.",
    "studio.dub.poweredBy": "Powered by ElevenLabs",
    // AI Video Creator
    "aiVideo.title": "AI Video Creator",
    "aiVideo.desc": "Generate complete videos — script, storyboard, voiceover, and final edit.",
    "aiVideo.step.purpose": "What are you creating today?",
    "aiVideo.purpose.organic": "Organic Social Content",
    "aiVideo.purpose.organic.desc": "Post, Reel, Story, or general social media content",
    "aiVideo.purpose.paid_ad": "Paid Video Ad",
    "aiVideo.purpose.paid_ad.desc": "Video intended to run as an advertisement",
    "aiVideo.purpose.promo": "Product or Service Promo",
    "aiVideo.purpose.promo.desc": "Showcase a specific product or service",
    "aiVideo.purpose.educational": "Educational Video",
    "aiVideo.purpose.educational.desc": "Teach, explain, or demonstrate",
    "aiVideo.purpose.brand": "Brand Awareness",
    "aiVideo.purpose.brand.desc": "Build recognition for your brand",
    "aiVideo.purpose.testimonial": "Testimonial",
    "aiVideo.purpose.testimonial.desc": "Share a customer success story",
    "aiVideo.purpose.announcement": "Announcement",
    "aiVideo.purpose.announcement.desc": "Announce a launch, event, or update",
    "aiVideo.step.preferences": "Video Preferences",
    "aiVideo.prefs.about": "What is this video about?",
    "aiVideo.prefs.platform": "Platform",
    "aiVideo.prefs.contentStyle": "Content Style",
    "aiVideo.prefs.length": "Video Length",
    "aiVideo.prefs.format": "Format",
    "aiVideo.prefs.language": "Language",
    "aiVideo.prefs.voice": "Voice",
    "aiVideo.prefs.voice.cloned": "My Cloned Voice",
    "aiVideo.prefs.voice.library": "Choose From Voice Library",
    "aiVideo.prefs.voice.music": "No Voice — Music Only",
    "aiVideo.prefs.voice.noClone": "Create My Voice Clone",
    "aiVideo.prefs.captions": "Word-by-Word Captions",
    "aiVideo.prefs.cta": "Call to Action",
    "aiVideo.prefs.cta.none": "No CTA",
    "aiVideo.prefs.socialCopy": "Social Copy (optional)",
    "aiVideo.prefs.generateScript": "Generate Script & Storyboard",
    "aiVideo.prefs.back": "Back",
    "aiVideo.generating": "Writing your script and storyboard…",
    "aiVideo.step.review": "Review Your Script & Storyboard",
    "aiVideo.review.summary": "Summary",
    "aiVideo.review.script": "Script",
    "aiVideo.review.scenes": "Storyboard Scenes",
    "aiVideo.review.credits": "Credits required",
    "aiVideo.review.creditsNote": "{n} credit will be used only after the video renders successfully.",
    "aiVideo.review.editScript": "Edit Script",
    "aiVideo.review.editStoryboard": "Edit Storyboard",
    "aiVideo.review.changePrefs": "Change Preferences",
    "aiVideo.review.generate": "Generate Final Video",
    "aiVideo.review.bilingualNote": "English + Spanish = 2 videos = 2 credits",
    "aiVideo.rendering.title": "Generating Your Video",
    "aiVideo.rendering.clips": "{done} of {total} clips ready",
    "aiVideo.rendering.merging": "Merging clips and voiceover…",
    "aiVideo.rendering.note": "This takes 3–5 minutes. You can stay on this page or check My Videos when ready.",
    "aiVideo.complete.title": "Your Video Is Ready",
    "aiVideo.complete.download": "Download Now",
    "aiVideo.complete.postNow": "Post Now",
    "aiVideo.complete.schedule": "Schedule",
    "aiVideo.complete.convertToAd": "Convert to Paid Ad",
    "aiVideo.complete.runAsAd": "Run as an Ad",
    "aiVideo.complete.downloadCaption": "Download Caption",
    "aiVideo.complete.copyCaption": "Copy Caption",
    "aiVideo.complete.copyHashtags": "Copy Hashtags",
    "aiVideo.complete.anotherSize": "Create Another Size",
    "aiVideo.complete.viewInLibrary": "View in My Videos",
    "aiVideo.complete.connectAccount": "Connect your account to use this option.",
    "aiVideo.complete.noChargeForSize": "Creating another size does not use an additional credit.",
    "aiVideo.toast.aboutRequired": "Please describe what this video is about",
    "aiVideo.toast.scriptFailed": "Script generation failed — please try again",
    "aiVideo.toast.renderFailed": "Render failed — your credit was not charged",
    "aiVideo.toast.noCredits": "You need at least {n} video credit to generate this video",
    "aiVideo.toast.submitted": "Video generation started — rendering takes 3–5 minutes",
    "studio.tool.aiVideoCreator.name": "AI Video Creator",
    "studio.tool.aiVideoCreator.desc": "Full video pipeline — script, storyboard, voiceover, and final edit.",
  },
  es: {
    "nav.login": "Iniciar sesión",
    "nav.joinWaitlist": "Unirse a la lista",
    "lang.label": "Idioma",
    "lang.en": "Inglés",
    "lang.es": "Español",
    "settings.language": "Idioma",
    "settings.languageDesc": "Elige cómo AdKavi se comunica contigo.",
    "welcome.title": "Bienvenido a AdKavi 🎉",
    "welcome.subtitle": "Dos pasos rápidos para lanzar tu primera campaña.",
    "welcome.step1": "Conecta tus cuentas publicitarias",
    "welcome.step1desc": "Conecta Meta, TikTok o Google para lanzar por ti.",
    "welcome.step2": "Crea tu primera campaña",
    "welcome.step2desc": "Nuestra IA escribe el texto, elige el público y la deja lista para revisar.",
    "welcome.cta.connect": "Conectar cuentas",
    "welcome.cta.campaign": "Crear primera campaña",
    "welcome.skip": "Explorar por mi cuenta",
    "welcomeConnect.title": "Bienvenido a AdKavi",
    "welcomeConnect.desc": "Conecta tus cuentas publicitarias para ver datos reales de tus campañas en todas las plataformas.",
    "welcomeConnect.meta.desc": "Campañas en Facebook e Instagram",
    "welcomeConnect.tiktok.desc": "Administrador de anuncios de TikTok",
    "welcomeConnect.google.desc": "Google Search, Display y Shopping",
    "welcomeConnect.connectBtn": "Conectar",
    "welcomeConnect.youtube.name": "YouTube Ads",
    "welcomeConnect.youtube.desc": "Incluido con Google Ads",
    "welcomeConnect.youtube.badge": "Vía Google",
    "welcomeConnect.skip": "Omitir por ahora · Conectar desde Configuración",
    "approval.title": "Revisa tu anuncio",
    "approval.copy": "Texto del anuncio",
    "approval.creative": "Creatividad",
    "approval.targeting": "Segmentación",
    "approval.requestChanges": "Solicitar cambios",
    "approval.approve": "Aprobar y programar",
    "approval.changesPlaceholder": "¿Qué te gustaría cambiar?",
    "approval.notFound": "Campaña no encontrada.",
    "approval.statusApproved": "Aprobada — pasando a programada.",
    "approval.statusChanges": "Cambios solicitados. Tu manager revisará.",
    "competitors.title": "Investigación de competidores (opcional)",
    "competitors.desc": "Busca competidores para inspirar tu mensaje antes de lanzar.",
    "competitors.placeholder": "Añade un competidor (marca o web)",
    "competitors.add": "Añadir",
    "competitors.skip": "Saltar — continuar sin investigar",
    "competitors.empty": "Escribe un competidor antes de añadirlo.",
    "competitors.duplicate": "Ese competidor ya está en tu lista.",
    "checklist.title": "Comenzar",
    "checklist.hide": "Ocultar",
    "checklist.connect": "Conectar cuenta publicitaria",
    "checklist.campaign": "Crear primera campaña",
    "checklist.budget": "Establecer presupuesto",
    "checklist.launch": "Lanzar primer anuncio",
    "tour.stepCounter": "Paso {current} de {total}",
    "tour.next": "Siguiente",
    "tour.finish": "Finalizar",
    "tour.skip": "Omitir tour",
    "tour.pause": "Pausar",
    "tour.resume": "Reanudar tour",
    "tour.step.dashboard.title": "Tu Panel",
    "tour.step.dashboard.desc": "Monitorea el rendimiento, gasto y métricas clave de todas tus campañas en un solo lugar.",
    "tour.step.campaigns.title": "Crear una Campaña",
    "tour.step.campaigns.desc": "Lanza campañas en Meta, TikTok y Google con copy y creatividades generados por IA.",
    "tour.step.aiStudio.title": "Estudio de IA",
    "tour.step.aiStudio.desc": "Genera videos, locuciones, avatares y contenido en español con sincronización labial.",
    "tour.step.content.title": "Calendario de Contenido",
    "tour.step.content.desc": "Planifica y programa publicaciones en múltiples plataformas.",
    "tour.step.competitors.title": "Investigación de Competidores",
    "tour.step.competitors.desc": "Espía los anuncios de tu competencia, ve sus estrategias y obtén ideas de IA para superarlos.",
    "plan.gate.title": "Esta función está bloqueada",
    "plan.gate.descPrefix": "Esta función está disponible en",
    "plan.gate.descSuffix": "y superiores. Mejora tu plan para desbloquearla.",
    "plan.gate.upgrade": "Mejorar plan",
    "plan.lockedCard.desc": "Disponible en {plan} y superiores. Mejora tu plan para desbloquearla.",
    "plan.lockedCard.upgrade": "Mejorar",
    "plan.upgradePrompt.desc": "Esta función está disponible en {plan} y superiores. Mejora tu plan para desbloquearla.",
    "plan.upgradePrompt.notNow": "Ahora no",
    "plan.upgradePrompt.upgrade": "Mejorar plan",
    "plan.feature.abTesting": "Pruebas A/B",
    "plan.feature.budgetOptimizer": "Optimizador de Presupuesto IA",
    "plan.limit.campaignsTitle": "Límite de campañas alcanzado",
    "plan.limit.campaignsDesc": "Tu plan incluye hasta {limit} campañas. Mejora tu plan para crear más.",
    "plan.limit.campaignsReached": "Has usado {count} de {limit} campañas de tu plan.",
    "plan.limit.creativesTitle": "Límite de creatividades alcanzado",
    "plan.limit.creativesDesc": "Tu plan incluye hasta {limit} creatividades. Mejora tu plan para crear más.",
    "approval.back": "Volver a la campaña",
    "approval.copyDesc": "Titular, descripción y llamada a la acción generados por IA.",
    "approval.creativeDesc": "Vista previa del recurso visual.",
    "approval.creativePreview": "Vista previa creativa",
    "approval.cancel": "Cancelar",
    "approval.submit": "Enviar",
    "approval.loading": "Cargando…",
    "approval.backToCampaigns": "Volver a campañas",
    "approval.field.locations": "Ubicaciones",
    "approval.field.age": "Edad",
    "approval.field.gender": "Género",
    "approval.field.interests": "Intereses",
    "approval.field.language": "Idioma",
    "approval.field.platform": "Plataforma",
    "approval.field.all": "Todos",
    "approval.field.english": "Inglés",
    "approval.headline": "Descubre",
    "approval.body": "Toca abajo para ver por qué los clientes adoran nuestra novedad. Oferta de lanzamiento por tiempo limitado — pruébala hoy.",
    "approval.cta.learn": "Saber más",
    "share.copied": "Enlace copiado — expira el {date}",
    "share.expires": "Expira el {date}",
    "invoices.exportCsv": "Exportar CSV",
    "invoices.csvNoRows": "Ninguna factura coincide con los filtros.",
    "competitors.invalidUrl": "Introduce un nombre de marca válido o una URL completa.",
    "competitors.tooLong": "El nombre del competidor es demasiado largo (máx. 80 caracteres).",
    "wizard.error.platforms": "Selecciona al menos una plataforma para continuar.",
    "wizard.error.businessName": "Introduce el nombre de tu negocio (al menos 2 caracteres).",
    "wizard.error.industry": "Elige un sector.",
    "wizard.error.promoting": "Describe brevemente lo que estás promocionando.",
    "wizard.error.goal": "Elige un objetivo de campaña para continuar.",
    "wizard.error.creative": "Elige cómo quieres crear el anuncio.",
    "wizard.error.creativeUpload": "Sube una imagen o vídeo para continuar.",
    "wizard.error.creativeAi": "Elige un tipo de creatividad con IA para continuar.",
    "share.expired.title": "Este enlace ha caducado",
    "share.expired.body": "Los enlaces compartidos del panel son válidos durante 7 días. Pide al propietario que te envíe uno nuevo.",
    "share.expired.requestNew": "Solicitar un nuevo enlace",
    "share.expired.goHome": "Ir al inicio",
    "share.request.emailLabel": "Tu correo electrónico",
    "share.request.emailPlaceholder": "tu@ejemplo.com",
    "share.request.confirmEmailLabel": "Confirma tu correo",
    "share.request.confirmEmailPlaceholder": "Vuelve a introducir tu correo",
    "share.request.note": "Mensaje opcional",
    "share.request.notePlaceholder": "Algo que quieras añadir…",
    "share.request.submit": "Enviar solicitud",
    "share.request.sending": "Enviando…",
    "share.request.success": "¡Solicitud enviada! El propietario del panel se pondrá en contacto.",
    "share.request.invalidEmail": "Introduce un correo electrónico válido.",
    "share.request.emailMismatch": "Los correos no coinciden. Revisa ambos campos.",
    "share.request.duplicate": "Ya recibimos una solicitud desde este correo para este enlace. El propietario te contactará pronto.",
    "share.request.error": "No se pudo enviar tu solicitud. Inténtalo de nuevo.",
    "share.request.successTitle": "Solicitud recibida",
    "share.request.successBody": "¡Gracias! Hemos enviado tu solicitud al propietario del panel. Recibirás una respuesta en menos de 24 horas en",
    "share.request.successFootnote": "Si no recibes respuesta, revisa tu carpeta de spam o vuelve a intentarlo mañana.",
    "share.request.rateLimited": "Has alcanzado el límite de 3 solicitudes en 24 horas para este correo. Inténtalo de nuevo mañana.",
    "share.request.rateLimitedCountdown": "Podrás enviar de nuevo en",
    "share.request.captchaLabel": "Verificación rápida: ¿cuánto es {q}?",
    "share.request.captchaPlaceholder": "Respuesta",
    "share.request.captchaError": "La respuesta no parece correcta. Inténtalo de nuevo.",
    "share.request.botBlocked": "Envío bloqueado. Recarga la página e inténtalo de nuevo.",
    "share.request.goHome": "Ir al inicio",
    "share.request.retryReady": "Ya puedes enviar tu solicitud de nuevo.",
    "share.request.captchaHelp": "Resuelve el problema matemático para demostrar que no eres un robot.",
    "share.request.formAriaLabel": "Solicitar un nuevo enlace al panel compartido",
    "assistant.title": "Asistente de AdKavi",
    "assistant.openAria": "Abrir el Asistente de AdKavi",
    "assistant.closeAria": "Cerrar asistente",
    "assistant.welcome": "¡Hola! Soy tu Asistente de AdKavi. Pregúntame lo que quieras sobre cómo usar la plataforma.",
    "assistant.placeholder": "Pregunta sobre AdKavi...",
    "assistant.support": "Contactar con soporte",
    "assistant.errorGeneric": "Lo siento, no pude generar una respuesta. Si el problema continúa, escribe a support@adkavi.com.",
    "assistant.errorNetwork": "Hubo un problema para contactar al asistente. Inténtalo de nuevo o escribe a support@adkavi.com.",
    "assistant.suggestionsTitle": "Prueba preguntando:",
    "assistant.suggestion.campaign": "¿Cómo creo una campaña?",
    "assistant.suggestion.adAccount": "¿Cómo conecto mi cuenta de anuncios?",
    "assistant.suggestion.voice": "¿Cómo clono mi voz?",
    "assistant.suggestion.upgrade": "¿Cómo mejoro mi plan?",

    // Navbar sections
    "nav.section.hero": "Inicio",
    "nav.section.features": "Funciones",
    "nav.section.howItWorks": "Cómo funciona",
    "nav.section.pricing": "Precios",
    "nav.section.testimonials": "Testimonios",
    "nav.menu.open": "Abrir menú",
    "nav.menu.close": "Cerrar menú",

    // Hero
    "hero.badge": "La plataforma de marketing con IA todo-en-uno",
    "hero.title.1": "Crea. Lanza. Escala.",
    "hero.title.2": "Todo con IA.",
    "hero.subtitle": "Todo lo que necesitas para tu marketing — anuncios, videos, voz, contenido y analíticas — desde un solo panel.",
    "hero.subtitleEmphasis": "",
    "hero.cta.start": "Unirse a la lista",
    "hero.cta.login": "Ver cómo funciona",
    "hero.trial": "Plazas limitadas. Únete gratis — sin tarjeta de crédito.",
    "hero.poweredBy": "Con tecnología de",
    "hero.static": "CREA.",
    "hero.cycling": "LANZA.|ESCALA.|GANA.|CRECE.",
    "hero.allWithAi": "TODO CON IA.",
    "hero.waitlistNote": "Los primeros 100 miembros fundadores en total bloquean el plan que elijan — 14 días gratis · Cancela cuando quieras",

    // Features
    "features.eyebrow": "Lo que hace AdKavi",
    "features.title.1": "Todo tu marketing.",
    "features.title.2": "Una sola plataforma.",
    "features.ads.title": "Lanza anuncios en Meta, TikTok, Google y YouTube",
    "features.ads.desc": "Crea y gestiona campañas directamente desde AdKavi, sin saltar entre plataformas de anuncios.",
    "features.video.title": "Videos con avatar IA",
    "features.video.desc": "Genera videos profesionales tuyos con HeyGen, sin necesidad de grabarte.",
    "features.voice.title": "Clonación de voz",
    "features.voice.desc": "Clona tu voz con ElevenLabs y narra videos automáticamente.",
    "features.social.title": "Publicación y programación social",
    "features.social.desc": "Diseña carruseles, programa publicaciones y publica automáticamente en Facebook e Instagram.",

    // How It Works
    "how.title.1": "Lanza tu",
    "how.title.2": "primera campaña con IA",
    "how.title.3": "en",
    "how.title.4": "6 pasos",
    "how.step": "Paso",
    "how.s1.title": "Conecta tus cuentas",
    "how.s1.desc": "Conecta tu cuenta de Instagram, Facebook, TikTok, Google Search & Display o YouTube. Define tu presupuesto. Lanza el mismo anuncio en varias plataformas a la vez.",
    "how.s2.title": "Cuéntanos sobre tu negocio",
    "how.s2.desc": "Introduce el nombre de tu negocio, el sector y lo que estás promocionando. La IA de AdKavi te ayuda a encontrar tu público ideal.",
    "how.s3.title": "Elige tu objetivo",
    "how.s3.desc": "Elige tu meta: más leads, ventas, seguidores o mensajes por WhatsApp.",
    "how.s4.title": "Crea tu creatividad",
    "how.s4.desc": "Dos formas de crear tu anuncio — sube lo que ya tienes o deja que la IA lo construya por ti.",
    "how.s4.optionA": "Opción A",
    "how.s4.optionAText": "Sube tu propia imagen o video.",
    "how.s4.optionB": "Opción B",
    "how.s4.optionBText": "Crea con IA — diseña un flyer o post, genera un video de avatar con tu voz clonada (sin grabar nada), o grábate y deja que la IA lo mejore.",
    "how.s5.title": "La IA construye tu campaña",
    "how.s5.desc": "AdKavi AI escribe tus anuncios — el titular, la descripción y la llamada a la acción. También elige tu público y tu estrategia de segmentación según tu creatividad.",
    "how.s6.title": "Revisa y lanza",
    "how.s6.desc": "Revisa todo y publica en un clic.",

    // Pricing
    "pricing.title.1": "Precios simples y transparentes.",
    "pricing.title.2": "Sin sorpresas.",
    "pricing.subtitle": "Todos los planes incluyen 14 días de prueba gratis. Cancela cuando quieras.",
    "pricing.popular": "Más popular",
    "pricing.startTrial": "Unirse a la lista — Asegurar este precio",
    "pricing.perMonth": "/mes",
    "pricing.gate.title": "¿Para quién lo estás creando?",
    "pricing.gate.creator.title": "Soy un negocio o creador",
    "pricing.gate.creator.desc": "Planes de cuenta única para personas y pequeños negocios.",
    "pricing.gate.agency.title": "Soy una agencia",
    "pricing.gate.agency.desc": "Gestiona hasta 10 cuentas de clientes desde un solo panel.",
    "pricing.forAgencies": "Para agencias",
    "pricing.forCreators": "Para negocios y creadores",
    "pricing.adSpendNote": "El gasto publicitario va directamente a Meta, TikTok y Google — AdKavi nunca retiene tu dinero. Tú mantienes el control total de tu presupuesto.",
    "pricing.viewIndividual": "¿Buscas un plan personal? Ver planes individuales →",
    "pricing.viewAgency": "¿Eres una agencia de marketing? Ver planes para agencias →",

    // Testimonials
    "testimonials.title.1": "Amado por",
    "testimonials.title.2": "negocios reales",
    "testimonials.t1.role": "Dueña de un negocio de reparación de crédito",
    "testimonials.t1.quote": "AdKavi reemplazó a todo mi equipo de marketing. Pasé de 5 leads por semana a 30 en el primer mes.",
    "testimonials.t2.role": "Agente inmobiliario",
    "testimonials.t2.quote": "Gastaba 3.000 $ al mes en una agencia de marketing. Ahora pago 699 $ y obtengo mejores resultados.",
    "testimonials.t3.role": "Coach de fitness",
    "testimonials.t3.quote": "La función de video con IA es una locura. Generé 10 videos profesionales en una tarde sin grabar nada.",

    // Footer
    "footer.tagline": "Con la tecnología de IA más avanzada del mundo.",
    "footer.features": "Funciones",
    "footer.pricing": "Precios",
    "footer.howItWorks": "Cómo funciona",
    "footer.privacy": "Política de privacidad",
    "footer.terms": "Términos del servicio",
    "footer.cookies": "Política de cookies",
    "footer.deleteData": "Eliminar mis datos",
    "footer.deleteDataStatus": "Estado de eliminación",
    "delete.status.title": "Estado de la solicitud de eliminación",
    "delete.status.intro": "Introduce el correo que usaste y el ID de solicitud que te mostramos al enviar tu solicitud de eliminación de datos.",
    "delete.status.email": "Correo electrónico",
    "delete.status.requestId": "ID de solicitud",
    "delete.status.cta": "Consultar estado",
    "delete.status.loading": "Buscando…",
    "delete.status.notFound": "No encontramos ninguna solicitud con ese correo e ID. Revísalos o escribe a support@adkavi.com para obtener ayuda.",
    "delete.status.back": "← Volver a solicitud de eliminación",
    "delete.status.submitted": "Enviada",
    "delete.status.processed": "Procesada",
    "delete.status.expectedBy": "Prevista para",
    "delete.status.request": "Solicitud",
    "delete.status.statusPending": "Pendiente",
    "delete.status.statusCompleted": "Completada",
    "delete.status.statusRejected": "Rechazada",
    "delete.status.helpPending": "Procesamos las solicitudes de eliminación en un plazo de 30 días, conforme al RGPD, la CCPA y los Términos de la Plataforma de Meta.",
    "delete.status.helpCompleted": "Tus datos han sido eliminados. Si tienes preguntas, escribe a support@adkavi.com.",
    "delete.status.helpRejected": "No pudimos procesar esta solicitud. Escribe a support@adkavi.com para más detalles.",
    "delete.status.shareable": "Guarda este enlace para volver a consultar el estado en cualquier momento:",
    "footer.contact": "Contacto",
    "footer.copyright": "© 2026 AdKavi. Todos los derechos reservados.",

    // Auth
    "auth.continueGoogle": "Continuar con Google",
    "auth.or": "o",
    "auth.tab.signin": "Iniciar sesión",
    "auth.tab.signup": "Crear cuenta",
    "auth.email": "Correo electrónico",
    "auth.password": "Contraseña",
    "auth.confirmPassword": "Confirmar contraseña",
    "auth.name": "Nombre",
    "auth.forgot": "¿Olvidaste tu contraseña?",
    "auth.signingIn": "Iniciando sesión…",
    "auth.signIn": "Iniciar sesión",
    "auth.signUp": "Crear cuenta",
    "auth.creating": "Creando cuenta…",
    "auth.startTrial": "Comenzar prueba gratis",
    "auth.signupChoosePlan": "Crear cuenta y elegir plan",
    "auth.passwordHint": "Mínimo 8 caracteres. Verificada contra filtraciones conocidas.",
    "auth.showPassword": "Mostrar contraseña",
    "auth.hidePassword": "Ocultar contraseña",
    "auth.googleFailed": "No se pudo iniciar sesión con Google",
    "auth.passwordMismatch": "Las contraseñas no coinciden.",
    "auth.welcomeBack": "¡Bienvenido de vuelta!",
    "auth.returning.title": "¡Bienvenido de vuelta!",
    "auth.returning.desc": "Como usuario que regresa, elige un plan para continuar. La prueba gratis solo está disponible una vez por cliente.",
    "auth.toast.checkInbox": "Revisa tu correo para verificar tu cuenta.",
    "auth.toast.verifyFirst": "Por favor verifica tu correo primero.",
    "auth.toast.invalidCreds": "Correo o contraseña no válidos.",
    "auth.toast.exists": "La cuenta ya existe. Intenta iniciar sesión.",
    "auth.demoHint": "Explora el panel completo con datos de ejemplo — sin necesidad de cuenta.",
    // Zod validation messages
    "auth.zod.nameRequired": "El nombre es obligatorio",
    "auth.zod.nameTooLong": "El nombre debe tener menos de 100 caracteres",
    "auth.zod.emailInvalid": "Dirección de correo no válida",
    "auth.zod.emailTooLong": "El correo debe tener menos de 255 caracteres",
    "auth.zod.passwordTooShort": "La contraseña debe tener al menos 8 caracteres",
    "auth.zod.passwordTooLong": "La contraseña debe tener menos de 72 caracteres",
    "auth.zod.passwordUppercase": "La contraseña debe incluir una letra mayúscula",
    "auth.zod.passwordLowercase": "La contraseña debe incluir una letra minúscula",
    "auth.zod.passwordNumber": "La contraseña debe incluir un número",
    "auth.zod.passwordRequired": "La contraseña es obligatoria",
    "auth.zod.passwordsMismatch": "Las contraseñas no coinciden",
    "auth.zod.emailValid": "Introduce un correo electrónico válido",

    // Forgot password
    "forgot.title": "¿Olvidaste tu contraseña?",
    "forgot.desc": "Introduce tu correo y te enviaremos un enlace seguro para restablecerla.",
    "forgot.send": "Enviar enlace",
    "forgot.sending": "Enviando…",
    "forgot.sentPrefix": "Si existe una cuenta para",
    "forgot.sentSuffix": ", el enlace está en camino. Revisa tu bandeja de entrada y spam.",
    "forgot.back": "Volver al inicio de sesión",

    // Verify email
    "verify.title": "Verifica tu correo",
    "verify.descPrefix": "Enviamos un enlace de verificación a",
    "verify.descSuffix": ". Haz clic para activar tu cuenta. El enlace puede tardar un minuto — revisa también tu carpeta de spam.",
    "verify.inbox": "tu bandeja de entrada",
    "verify.resend": "Reenviar correo de verificación",
    "verify.sending": "Enviando…",
    "verify.resendIn": "Reenviar en {s}s",
    "verify.back": "Volver al inicio de sesión",
    "verify.toastVerified": "¡Correo verificado!",
    "verify.toastSent": "Correo de verificación enviado. Revisa tu bandeja.",
    "verify.toastWait": "Espera un momento antes de pedir otro correo.",
    "verify.toastMaybe": "Si tu correo no está verificado, hay un nuevo enlace en camino.",
    "verify.placeholder": "tu@ejemplo.com",

    // Reset password
    "reset.title": "Establecer nueva contraseña",
    "reset.descReady": "Introduce tu nueva contraseña.",
    "reset.descValidating": "Validando tu enlace…",
    "reset.newPassword": "Nueva contraseña",
    "reset.confirm": "Confirmar contraseña",
    "reset.hint": "Mínimo 8 caracteres, con mayúscula, minúscula y un número.",
    "reset.update": "Actualizar contraseña",
    "reset.updating": "Actualizando…",
    "reset.toastDone": "Contraseña actualizada. Has iniciado sesión.",
    "reset.expired": "Este enlace ha caducado o no es válido. Los enlaces son de un solo uso y caducan después de 1 hora.",
    "reset.requestNew": "Solicitar un nuevo enlace →",

    // Sidebar
    "sidebar.workspace": "Espacio de trabajo",
    "sidebar.dashboard": "Panel",
    "sidebar.campaigns": "Campañas",
    "sidebar.content": "Calendario de contenido",
    "sidebar.aiStudio": "Estudio IA",
    "sidebar.creative": "Estudio Creativo",
    "sidebar.benchmarks": "Benchmarks",
    "sidebar.clients": "Clientes",
    "sidebar.settings": "Ajustes",
    "sidebar.locked": "bloqueado",
    "sidebar.lockedTitle": "está bloqueado",
    "sidebar.lockedDescPrefix": "Esta función está disponible en",
    "sidebar.lockedDescSuffix": "y planes superiores. Mejora tu plan para desbloquearla.",
    "sidebar.notNow": "Ahora no",
    "sidebar.upgrade": "Mejorar plan",

    // For Creators — clon de IA y objetivos
    "creators.eyebrow": "Para creadores de contenido",
    "creators.title.1": "Tu clon de IA",
    "creators.title.2": "nunca duerme.",
    "creators.subtitle": "1 hora de configuración = 30 días de contenido. Nunca te pierdas una publicación. Nunca te quedes sin ideas. Nunca te quemes.",
    "creators.flow.1.title": "Sube tu cara y tu voz — una sola vez",
    "creators.flow.1.desc": "Un video corto y una muestra de voz. Eso es todo.",
    "creators.flow.2.title": "La IA te clona al instante",
    "creators.flow.2.desc": "Tu gemelo digital se encarga del trabajo.",
    "creators.flow.3.title": "30 días de contenido en minutos",
    "creators.flow.3.desc": "Videos, carruseles, captions — todo programado.",
    "creators.pickGoal": "Elige un objetivo — AdKavi hace el resto",
    "goal.followers.label": "Conseguir seguidores",
    "goal.followers.desc": "Lanza campañas segmentadas en Instagram y Facebook para atraer seguidores reales en tu nicho.",
    "goal.views.label": "Más visualizaciones",
    "goal.views.desc": "Maximiza las vistas de video en Instagram, Facebook, TikTok y YouTube.",
    "goal.leads.label": "Más leads",
    "goal.leads.desc": "Recoge nombres y datos de contacto dentro del propio anuncio — sin necesidad de web.",
    "goal.sales.label": "Aumentar ventas",
    "goal.sales.desc": "Lleva a la gente directo a tu página de producto o servicio, lista para comprar.",
    "goal.whatsapp.label": "Mensajes por WhatsApp",
    "goal.whatsapp.desc": "Las personas tocan tu anuncio y se abre un chat de WhatsApp con tu negocio al instante.",
    "goal.event.label": "Promocionar un evento",
    "goal.event.desc": "Anuncios de difusión y RSVP dirigidos a personas cerca de tu evento o en tu nicho.",
    "goal.traffic.label": "Tráfico al sitio web",
    "goal.traffic.desc": "Lleva a la gente directamente a cualquier página de tu web.",
    "goal.calls.label": "Llamadas telefónicas",
    "goal.calls.desc": "Las personas tocan tu anuncio y llaman a tu negocio directamente — sin necesidad de web.",

    // Visión
    "vision.eyebrow": "Nuestra visión",
    "vision.title.1": "Cada dueño de negocio y creador merece una",
    "vision.title.2": "Plataforma de marketing completa",
    "vision.title.3": "— impulsada por IA.",
    "vision.body": "AdKavi te ayuda a lanzar, monitorear y optimizar tus campañas en cada plataforma — para que puedas hacer crecer tu negocio y construir tu marca desde un solo panel.",
    "vision.grow": "",
    "vision.allInOne": "",

    // Problema
    "problem.eyebrow": "¿Te suena familiar?",
    "problem.title.1": "Crecer en línea no debería",
    "problem.title.2": "ser tan difícil.",
    "problem.subtitle": "Cada dueño de negocio y creador choca con los mismos muros — quemando dinero, viéndose amateur y viendo ganar a la competencia. AdKavi se creó para terminar con todo eso.",
    "problem.p1.title": "Quemar $2K–$10K/mes en agencias",
    "problem.p1.desc": "Tiempos lentos, creatividad mediocre, cero transparencia — y aún así eres tú quien escribe el brief.",
    "problem.p2.title": "Contratar un equipo que no puedes pagar",
    "problem.p2.desc": "Un media buyer, editor, copywriter y estratega cuestan más de lo que muchos negocios facturan en un año.",
    "problem.p3.title": "Odiar estar frente a la cámara",
    "problem.p3.desc": "Sabes que el video vende, pero grabarte se siente incómodo, lento y nunca se ve profesional.",
    "problem.p4.title": "Publicar y que te ignoren",
    "problem.p4.desc": "Horas de trabajo, 47 vistas, cero leads. El algoritmo no premia el esfuerzo — premia los sistemas.",
    "problem.p5.title": "Burnout de publicar a diario",
    "problem.p5.desc": "Ser tu propio creador, editor, marketer y CEO no es sostenible. Algo siempre se cae.",
    "problem.p6.title": "Hacer malabares con 6 herramientas",
    "problem.p6.desc": "Canva, CapCut, Meta Ads, ChatGPT, un programador, un CRM — cambiando de pestañas todo el día y pagando por cada uno.",
    "problem.p7.title": "Mala creatividad mata buen presupuesto",
    "problem.p7.desc": "Puedes tener el mejor producto, pero anuncios feos y hooks débiles desperdician cada dólar que gastas.",
    "problem.p8.title": "Sin idea de a quién segmentar",
    "problem.p8.desc": "Audiencias, lookalikes, intereses, ubicaciones — las plataformas están hechas para confundirte y hacerte gastar de más.",
    "problem.p9.title": "Costos suben, resultados bajan",
    "problem.p9.desc": "Los CPM suben sin parar. Sin testeo constante y creatividad fresca, tu ROAS se desploma en silencio.",
    "problem.p10.title": "Miedo a verte poco profesional",
    "problem.p10.desc": "Un typo, un video cringe, un anuncio malo — y pierdes la confianza de los clientes que necesitas.",
    "problem.p11.title": "Cero tiempo, demasiadas plataformas",
    "problem.p11.desc": "Meta, TikTok, Google, YouTube, Instagram — no puedes estar en todas, pero tienes que estar.",
    "problem.p12.title": "Invisible mientras la competencia escala",
    "problem.p12.desc": "No son más inteligentes — solo tienen un sistema. Sin uno, te quedas como el secreto mejor guardado de tu mercado.",
    "problem.outro.1": "No necesitas más presupuesto ni otra herramienta.",
    "problem.outro.2": "Necesitas un sistema que simplemente funcione.",

    // WhoFor
    "whofor.title.1": "Hecho para",
    "whofor.title.2": "cada industria",
    "whofor.title.3": "que quiere crecer",
    "whofor.subtitle": "Desde creadores en solitario hasta agencias — si necesitas más clientes, más vistas o más ventas, AdKavi fue hecho para ti.",
    "whofor.creators.label": "Creadores de contenido",
    "whofor.creators.desc": "Hazte viral con videos y carruseles clonados con IA",
    "whofor.credit.label": "Empresas de reparación de crédito",
    "whofor.credit.desc": "Atrae clientes que necesitan empezar de nuevo",
    "whofor.realestate.label": "Agentes inmobiliarios",
    "whofor.realestate.desc": "Lista más. Cierra más rápido. Automatiza tus anuncios.",
    "whofor.medspa.label": "Spas médicos y estética",
    "whofor.medspa.desc": "Llena tu agenda con clientes de alto valor",
    "whofor.lawyers.label": "Abogados y bufetes",
    "whofor.lawyers.desc": "Consigue leads cualificados sin una gran agencia",
    "whofor.finance.label": "Empresas de financiamiento",
    "whofor.finance.desc": "Llega a dueños de negocios que necesitan capital",
    "whofor.coaches.label": "Coaches y consultores",
    "whofor.coaches.desc": "Vende tu experiencia. Escala tu marca.",
    "whofor.contractors.label": "Contratistas y brokers",
    "whofor.contractors.desc": "Más llamadas. Más trabajos. Menos esfuerzo.",
    "whofor.health.label": "Salud y bienestar",
    "whofor.health.desc": "Haz crecer tu práctica con anuncios conformes",
    "whofor.any.label": "Cualquier negocio que haga anuncios",
    "whofor.any.desc": "Si vendes algo, AdKavi lo hace crecer",

    // AI Manager
    "ai.eyebrow": "Tu manager de campañas con IA nunca duerme",
    "ai.title": "AdKavi cuida tus anuncios para que tú no tengas que hacerlo",
    "ai.subtitle": "La mayoría de los negocios desperdician dinero en anuncios que dejaron de funcionar hace días. AdKavi lo detecta al instante.",
    "ai.card1.label": "Campaña saludable",
    "ai.card1.text": "Tu costo por lead es de $12. AdKavi está escalando tu mejor anuncio automáticamente.",
    "ai.card2.label": "Advertencia detectada",
    "ai.card2.text": "Tu tasa de clics bajó 40% hoy. AdKavi recomienda un nuevo titular — haz clic para revisar.",
    "ai.card3.label": "Auto-protegido",
    "ai.card3.text": "Campaña pausada. Estabas gastando $50/día con 0 leads. AdKavi la detuvo y reescribió tu anuncio.",
    "ai.footer.1": "Se acabó despertar con presupuesto desperdiciado.",
    "ai.footer.2": "AdKavi te cuida 24/7.",

    // Resultados
    "results.title.1": "Lo que logran",
    "results.title.2": "nuestros clientes",
    "results.s1": "Anuncios más rápidos",
    "results.s2": "Más barato que las agencias",
    "results.s3": "Mejor ROI en gasto publicitario",
    "results.s4": "IA trabajando en segundo plano",
    "results.s5": "Inglés y español",

    // Trust / Seguridad
    "trust.badge": "Tus datos están seguros con AdKavi",
    "trust.title.1": "Los datos de tu negocio están",
    "trust.title.2": "100% protegidos",
    "trust.subtitle": "AdKavi está construido con seguridad de nivel empresarial. Tus datos son tuyos — siempre.",
    "trust.c1.title": "Nunca guardamos tu contraseña",
    "trust.c1.desc": "Conectas tus cuentas de Meta, TikTok, Google y YouTube mediante OAuth oficial — el mismo método seguro usado por las apps más grandes del mundo. Nunca vemos tu contraseña.",
    "trust.c2.title": "Tus datos siguen siendo tuyos",
    "trust.c2.desc": "Tus datos publicitarios, campañas e información de clientes nunca se venden, comparten ni se usan para otro propósito. Lo tuyo, tuyo es.",
    "trust.c3.title": "Cifrado de nivel bancario",
    "trust.c3.desc": "Todos los datos viajan por HTTPS cifrado. Las claves de API e información sensible se almacenan con cifrado de nivel empresarial — nunca en texto plano.",
    "trust.c4.title": "Aislamiento total de datos",
    "trust.c4.desc": "Cada cuenta de cliente está totalmente aislada. Ningún negocio puede ver las campañas, leads o datos de otro. Construido con seguridad a nivel de fila.",
    "trust.c5.title": "Elimina cuando quieras",
    "trust.c5.desc": "Puedes desconectar tus cuentas publicitarias y solicitar la eliminación total de tus datos en cualquier momento. Sin preguntas. Tú siempre tienes el control.",
    "trust.c6.title": "Nunca vendemos tus datos",
    "trust.c6.desc": "AdKavi nunca vende los datos de tu negocio a anunciantes, brokers de datos ni terceros. Jamás.",
    "trust.bar.1": "Asegurado en la nube empresarial",
    "trust.bar.2": "Cifrado con HTTPS",
    "trust.bar.3": "Protegido por OAuth",
    "trust.bar.4": "Política de privacidad incluida",
    "trust.bar.5": "Aislamiento total de datos",

    // Features Explained
    "fx.title.1": "Todo lo Incluido",
    "fx.title.2": "En Tu Plan",
    "fx.subtitle": "Cada herramienta, función y flujo de IA que desbloqueas con AdKavi — mira exactamente lo que recibes antes de elegir un plan.",
    "fx.cat.adCreation": "CREACIÓN DE ANUNCIOS",
    "fx.cat.campaign": "GESTIÓN DE CAMPAÑAS",
    "fx.cat.audience": "SEGMENTACIÓN DE AUDIENCIA",
    "fx.cat.goals": "OBJETIVOS DE CAMPAÑA",
    "fx.cat.analytics": "ANALÍTICAS",
    "fx.cat.content": "CONTENIDO",
    "fx.cat.competitor": "INVESTIGACIÓN DE COMPETIDORES",
    "fx.cat.video": "VIDEO Y VOZ",
    "fx.video.note": "Incluido en los planes Dominio y Agencia Dominio",
    // Creación de anuncios
    "fx.adCreation.1.name": "Redactor de copy con IA",
    "fx.adCreation.1.desc": "Describe tu negocio y la IA escribe tu titular, texto y llamada a la acción en segundos.",
    "fx.adCreation.2.name": "Constructor de creatividades",
    "fx.adCreation.2.desc": "Herramienta drag and drop para diseñar la imagen de tu anuncio dentro de AdKavi.",
    "fx.adCreation.3.name": "Anuncios carrusel con IA",
    "fx.adCreation.3.desc": "La IA crea automáticamente un anuncio multi-imagen deslizable, listo para publicar.",
    "fx.adCreation.4.name": "Anuncios de imagen con IA",
    "fx.adCreation.4.desc": "La IA genera un anuncio fotográfico listo para publicar en tu campaña.",
    // Gestión de campañas
    "fx.campaign.1.name": "Monitor de salud de campaña",
    "fx.campaign.1.desc": "Mira qué anuncios van bien, cuáles necesitan atención y cuáles deberías pausar.",
    "fx.campaign.2.name": "Auto-pausa de protección",
    "fx.campaign.2.desc": "Si un anuncio gasta sin resultados durante 48 horas, el sistema lo pausa automáticamente.",
    "fx.campaign.3.name": "Optimizador de presupuesto con IA",
    "fx.campaign.3.desc": "Mueve automáticamente el dinero hacia los anuncios que están funcionando.",
    "fx.campaign.4.name": "Pruebas A/B",
    "fx.campaign.4.desc": "Lanza 2 versiones del anuncio a la vez, conserva la ganadora y detiene la perdedora.",
    "fx.campaign.5.name": "Lanzamiento multiplataforma",
    "fx.campaign.5.desc": "Lanza en Meta, TikTok, Google Search + Display y YouTube al mismo tiempo con un clic.",
    "fx.campaign.6.name": "Flujo de aprobación",
    "fx.campaign.6.desc": "Revisa y aprueba cada anuncio antes de publicarlo. Siempre tienes el control.",
    "fx.campaign.7.name": "Lanza ya o programa",
    "fx.campaign.7.desc": "Elige publicar al instante o seleccionar la fecha y hora perfectas.",
    // Segmentación
    "fx.audience.1.name": "Audiencias personalizadas",
    "fx.audience.1.desc": "Llega a personas que ya te siguen o visitaron tu sitio web.",
    "fx.audience.2.name": "Retargeting",
    "fx.audience.2.desc": "Muestra anuncios a personas que visitaron tu página pero aún no actuaron.",
    "fx.audience.3.name": "Audiencias similares",
    "fx.audience.3.desc": "Encuentra personas nuevas que se parecen exactamente a tus mejores clientes.",
    // Objetivos
    "fx.goals.1.name": "Formularios de leads",
    "fx.goals.1.desc": "La persona toca tu anuncio, se abre un formulario dentro de Facebook o Instagram y envía su nombre y correo sin salir de la app.",
    "fx.goals.2.name": "Campañas de WhatsApp",
    "fx.goals.2.desc": "El botón de tu anuncio lleva a la persona directo a tu chat de WhatsApp al instante.",
    "fx.goals.3.name": "Ventas",
    "fx.goals.3.desc": "Lleva a las personas directo a tu página de producto o servicio, listas para comprar.",
    "fx.goals.4.name": "Llamadas telefónicas",
    "fx.goals.4.desc": "Las personas tocan tu anuncio y llaman a tu negocio directamente — sin necesidad de web.",
    "fx.goals.5.name": "Tráfico al sitio web",
    "fx.goals.5.desc": "Lleva a las personas directamente a cualquier página de tu sitio web.",
    "fx.goals.6.name": "Visualizaciones",
    "fx.goals.6.desc": "Maximiza las vistas de video en Instagram, Facebook, TikTok y YouTube.",
    "fx.goals.7.name": "Conseguir seguidores",
    "fx.goals.7.desc": "Lanza campañas segmentadas en Instagram, Facebook y TikTok para atraer seguidores reales en tu nicho.",
    "fx.goals.8.name": "Promocionar un evento",
    "fx.goals.8.desc": "Anuncios de difusión y RSVP dirigidos a personas cerca de tu evento o en tu nicho.",
    // Analíticas
    "fx.analytics.1.name": "Todas las plataformas, un solo panel",
    "fx.analytics.1.desc": "Mira todos tus resultados de Meta, Facebook, TikTok, Google Search + Display y YouTube en un solo lugar. Sin saltar entre apps.",
    "fx.analytics.2.name": "Benchmarks por industria",
    "fx.analytics.2.desc": "AdKavi te dice si tu costo por resultado es bueno o malo para tu industria específica.",
    "fx.analytics.3.name": "Alertas de costo",
    "fx.analytics.3.desc": "Recibe avisos en cuanto gastes demasiado por resultado, para no desperdiciar dinero.",
    "fx.analytics.4.name": "Seguimiento del gasto",
    "fx.analytics.4.desc": "Mira exactamente cuánto gastaste vs cuánto generaste.",
    "fx.analytics.5.name": "Mejor anuncio",
    "fx.analytics.5.desc": "AdKavi te muestra qué anuncio está generando más dinero.",
    "fx.analytics.6.name": "Comparativa de plataformas",
    "fx.analytics.6.desc": "Mira cuál te funciona mejor: Meta, TikTok, Google Search + Display o YouTube.",
    // Contenido
    "fx.content.1.name": "Calendario de contenido",
    "fx.content.1.desc": "Mira todas tus publicaciones programadas en una vista mensual de calendario en cada plataforma.",
    "fx.content.2.name": "Redactor de captions con IA",
    "fx.content.2.desc": "La IA escribe automáticamente tus captions de Instagram y TikTok con hashtags.",
    "fx.content.3.name": "Publicación multiplataforma",
    "fx.content.3.desc": "Crea una publicación y publícala en Meta, TikTok, Google y YouTube al mismo tiempo.",
    // Competencia
    "fx.competitor.1.name": "Anuncios de la competencia",
    "fx.competitor.1.desc": "Mira los anuncios exactos que están corriendo tus competidores. Úsalos como inspiración para crear mejores anuncios más rápido.",
    "fx.competitor.2.name": "Duración de anuncios",
    "fx.competitor.2.desc": "Mira cuánto tiempo lleva activo el anuncio de un competidor. Más tiempo = está funcionando.",
    "fx.competitor.3.name": "Espionaje de plataformas",
    "fx.competitor.3.desc": "Mira en qué plataformas anuncia tu competencia: Meta, TikTok, Google Search + Display y YouTube.",
    "fx.competitor.4.name": "Insight con IA",
    "fx.competitor.4.desc": "AdKavi analiza qué está funcionando en sus anuncios y te dice exactamente cómo superarlos.",
    // Video y voz
    "fx.video.1.name": "Clonación de voz",
    "fx.video.1.desc": "Grábate durante 30 segundos. La IA duplica tu voz exacta para siempre. Cada voz en off suena como tú.",
    "fx.video.2.name": "Voces en off con IA",
    "fx.video.2.desc": "Añade tu voz clonada a cualquier video automáticamente. Sin necesidad de grabar.",
    "fx.video.3.name": "Traducir y doblar al español",
    "fx.video.3.desc": "Sube tu video en inglés y AdKavi reemplaza el audio con una versión perfecta en español, automáticamente.",
    "fx.video.4.name": "Avatar IA personal",
    "fx.video.4.desc": "Sube un video tuyo de 2 minutos. La IA clona tu rostro. Nunca tendrás que volver a grabarte.",
    "fx.video.5.name": "Videos con avatar IA",
    "fx.video.5.desc": "Escribe un guion y tu yo IA aparece en cámara diciéndolo. Anuncios de video profesionales en minutos.",
    "fx.video.6.name": "Redactor de guiones con IA",
    "fx.video.6.desc": "La IA escribe exactamente lo que dirá tu avatar según el objetivo de tu campaña.",
    "fx.video.7.name": "Lip sync en español",
    "fx.video.7.desc": "La boca de tu avatar IA se mueve perfectamente sincronizada con las palabras en español. Se ve 100% natural en cámara.",
    "fx.video.8.name": "Directo a la campaña",
    "fx.video.8.desc": "El video terminado se publica directo en tu campaña. Sin descargar, sin subir, sin editar.",

    // Afiliados
    "affiliate.title.1": "Gana con",
    "affiliate.title.2": "AdKavi",
    "affiliate.subtitle": "¿Te encanta AdKavi? Compártelo y gana. Recibe el 10% del primer pago mensual de tu referido — sin límites ni topes.",
    "affiliate.c1.title": "Comparte fácil",
    "affiliate.c1.desc": "Obtén tu enlace único en segundos.",
    "affiliate.c2.title": "Seguimiento al instante",
    "affiliate.c2.desc": "Mira cada referido desde tu panel.",
    "affiliate.c3.title": "Cobra",
    "affiliate.c3.desc": "Recibe tu comisión automáticamente.",
    "affiliate.cta": "Únete al programa de afiliados",

    // Pricing checklist en español
    "plan.cat.AD CREATION": "CREACIÓN DE ANUNCIOS",
    "plan.cat.CAMPAIGN MANAGEMENT": "GESTIÓN DE CAMPAÑAS",
    "plan.cat.AUDIENCE TARGETING": "SEGMENTACIÓN DE AUDIENCIA",
    "plan.cat.CAMPAIGN GOALS": "OBJETIVOS DE CAMPAÑA",
    "plan.cat.ANALYTICS": "ANALÍTICAS",
    "plan.cat.CONTENT": "CONTENIDO",
    "plan.cat.COMPETITOR RESEARCH": "INVESTIGACIÓN DE COMPETIDORES",
    "plan.cat.VIDEO & VOICE": "VIDEO Y VOZ",
    "plan.meta.Up to 15 campaigns": "Hasta 15 campañas",
    "plan.meta.Unlimited campaigns": "Campañas ilimitadas",
    "plan.meta.14-day free trial — cancel anytime": "14 días de prueba gratis — cancela cuando quieras",
    "plan.meta.Email support": "Soporte por correo",
    "plan.meta.Priority email support": "Soporte por correo prioritario",
    "plan.meta.30 AI videos included every month": "30 videos con IA incluidos cada mes",
    "plan.meta.Early access to all new features": "Acceso anticipado a todas las nuevas funciones",
    "plan.meta.1 team member": "1 miembro del equipo",
    "plan.meta.Priority chat support": "Soporte por chat prioritario",
    "plan.meta.Up to 15 campaigns per client": "Hasta 15 campañas por cliente",
    "plan.meta.Up to 2 team members": "Hasta 2 miembros del equipo",
    "plan.meta.14-day free trial — 1 client included, cancel anytime": "14 días de prueba gratis — 1 cliente incluido, cancela cuando quieras",
    "plan.meta.Dedicated support": "Soporte dedicado",
    "plan.meta.Unlimited campaigns per client": "Campañas ilimitadas por cliente",
    "plan.meta.Up to 3 team members": "Hasta 3 miembros del equipo",
    "plan.meta.Priority dedicated support": "Soporte dedicado prioritario",
    "plan.meta.30 AI videos per month per client": "30 videos con IA por mes por cliente",
    "plan.meta.Up to 5 team members": "Hasta 5 miembros del equipo",

    // Dashboard
    "dash.title": "Dashboard",
    "dash.live": "En vivo",
    "dash.connecting": "Conectando…",
    "dash.metaConnected": "Conectado a la cuenta de Meta: {account}",
    "dash.connectMeta": "Conectar Meta",
    "dash.newAiCampaign": "Nueva campaña con IA",
    "dash.dataPoints": "{shown} de {total} punto{plural} de datos",
    "dash.range.7": "Últimos 7 días",
    "dash.range.30": "Últimos 30 días",
    "dash.range.90": "Últimos 90 días",
    "dash.range.custom": "Rango personalizado",
    "dash.range.all": "Todo el tiempo",
    "dash.platform.all": "Todas las plataformas",
    "dash.allCampaigns": "Todas las campañas",
    "dash.pickDateRange": "Elige un rango de fechas",
    "dash.useBrowserCurrency": "Usar la moneda de mi navegador",
    "dash.useBrowserCurrencyTitle": "Establecer moneda según el idioma de tu navegador",
    "dash.clear": "Limpiar",
    "dash.exportCsv": "Exportar CSV",
    "dash.exportCsvTitle": "Descargar la vista actual como CSV",
    "dash.share": "Compartir",
    "dash.shareTitle": "Copiar un enlace para compartir esta vista (caduca en 7 días)",
    "dash.shareCopied": "Enlace copiado al portapapeles",
    "dash.shareExpires": "Caduca {date}",
    "dash.shareFallback": "Enlace para compartir",
    "dash.metric.spend": "Inversión",
    "dash.metric.reach": "Alcance",
    "dash.metric.clicks": "Clics",
    "dash.metric.leads": "Leads",
    "dash.metric.conversions": "Conversiones",
    "dash.metric.cpl": "CPL",
    "dash.metric.roas": "ROAS",
    "dash.metric.revenue": "Ingresos",
    "dash.metric.impressions": "Impresiones",
    "dash.metric.ctr": "CTR",
    "dash.metric.cpc": "CPC",
    "dash.metric.convRate": "Tasa conv.",
    "dash.empty.title": "Esperando tu primer dato",
    "dash.empty.desc": "Este panel se actualiza en cuanto llegan métricas. Conecta tu fuente de anuncios — los números aparecerán en vivo.",
    "dash.recent": "Actividad reciente",
    "dash.sampleData": "Datos de muestra",
    "dash.recentLast": "Últimos {n}",
    "dash.col.when": "Cuándo",
    "dash.col.spend": "Inversión",
    "dash.col.reach": "Alcance",
    "dash.col.clicks": "Clics",
    "dash.col.leads": "Leads",
    "dash.col.conv": "Conv.",
    "dash.col.revenue": "Ingresos",
    "dash.chart.spendRevenue": "Inversión vs. ingresos",
    "dash.chart.spendRevenueSub": "Totales diarios",
    "dash.chart.funnel": "Clics, leads y conversiones",
    "dash.chart.funnelSub": "Embudo en el tiempo",
    "dash.chart.roas": "ROAS",
    "dash.chart.roasSub": "Retorno de la inversión publicitaria (×)",
    "dash.chart.reach": "Alcance",
    "dash.chart.reachSub": "Personas alcanzadas por día",
    "dash.chart.download": "Descargar gráfico",
    "dash.chart.downloadPng": "Descargar como PNG",
    "dash.chart.downloadSvg": "Descargar como SVG",
    "dash.chart.exportFailed": "Error de exportación",
    "dash.connect.prompt": "Conecta una cuenta de anuncios",
    "dash.connect.desc": "para ver datos reales de campañas aquí. Estás viendo datos de muestra.",
    "dash.connect.btn": "Conectar cuenta",
    "dash.autoPause.title": "Campaña pausada automáticamente",
    "dash.autoPause.viewCampaigns": "Ver campañas",
    "dash.bestAd.title": "Anuncio más rentable",
    "dash.bestAd.spent": "invertido",
    "dash.bestAd.revenue": "ingresos",
    "dash.spendToday.title": "Inversión hoy",
    "dash.benchmark.title": "vs Referencia del sector",
    "dash.benchmark.yours": "Tuyo",
    "dash.benchmark.industry": "Promedio sector",
    "dash.benchmark.viewAll": "Ver todas las referencias",
    // Platform breakdown
    "platform.title": "Por plataforma",
    "platform.subtitle": "Desempeño por canal en Meta, TikTok, Google y YouTube.",
    "platform.sample": "Datos de muestra · en vivo al conectar",
    "platform.mock": "Demo",
    "platform.impr": "Impr.",
    "platform.clicks": "Clics",
    "platform.spend": "Inversión",
    "platform.roas": "ROAS",
    "platform.ctr": "CTR",
    "platform.cpc": "CPC",
    "platform.conv": "Conv.",
    "platform.spendShare": "Participación de inversión",
    "platform.allPlatforms": "Todas las plataformas:",
    "platform.revenue": "Ingresos",
    // Campaign Health Widget
    "health.title": "Salud de campañas",
    "health.subtitle": "La IA de AdKavi monitorea tus campañas cada 24 h y recomienda ajustes cuando los resultados bajan.",
    "health.rescan": "Re-escanear",
    "health.status.healthy": "Saludable",
    "health.status.warning": "Advertencia",
    "health.status.alert": "Alerta",
    "health.pausedOne": "Pausamos {n} campaña para proteger tu presupuesto.",
    "health.pausedMany": "Pausamos {n} campañas para proteger tu presupuesto.",
    "health.reviewToRelaunch": "Revisa las recomendaciones para relanzar.",
    "health.alertSpent": "ha gastado {amount} con pocos resultados.",
    "health.alertHas3Recs": "La IA de AdKavi tiene 3 recomendaciones para arreglarlo.",
    "health.empty": "Aún no hay campañas que monitorear. Crea una para empezar.",
    "health.review": "Revisar",
    "health.recsTitle": "Recomendaciones de la IA de AdKavi",
    "health.close": "Cerrar",
    "health.generating": "Generando recomendaciones…",
    "health.autoFix": "Aplicar Auto-Ajuste",
    "health.reviewFirst": "Revisar cambios primero",
    "health.toast.applied": "Auto-Ajuste aplicado — monitoreando la nueva variante.",
    "health.fallbackName": "Tu campaña",
    "health.campaign": "Campaña",
    // Wizard toasts
    "wizard.toast.platformLocked": "Esta plataforma requiere un plan superior.",
    "wizard.toast.upgrade": "Mejorar plan",
    "wizard.toast.trialLimit.title": "Límite de prueba alcanzado",
    "wizard.toast.trialLimit.desc": "Cambia de cliente o espera hasta el día 15 para lanzar más campañas.",
    "wizard.toast.launched": "¡Campaña lanzada!",
    "wizard.toast.generationFailed": "Error al generar. Por favor, inténtalo de nuevo.",
    "wizard.toast.translationFailed": "Error al traducir. Por favor, inténtalo de nuevo.",
    "wizard.toast.launchFailed": "Error al lanzar. Por favor, inténtalo de nuevo.",
    "wizard.toast.saveFailed": "No se pudo guardar la campaña. Por favor, inténtalo de nuevo.",
    // Campaign list — no-account dialog
    "campaignsList.connect.title": "Conecta una cuenta publicitaria primero",
    "campaignsList.connect.desc": "Para lanzar campañas necesitas conectar al menos una cuenta — Meta, Google o TikTok.",
    "campaignsList.connect.cancel": "Cancelar",
    "campaignsList.connect.btn": "Conectar cuenta",
    // Delete data page
    "deleteData.toast.invalidInput": "Entrada no válida",
    "deleteData.toast.alreadySubmitted": "Ya hemos recibido una solicitud reciente para este correo. Procesamos las solicitudes en 30 días — revisa el estado o escribe a support@adkavi.com si necesitas ayuda.",
    "deleteData.toast.submitFailed": "No se pudo enviar la solicitud. Por favor, escribe a support@adkavi.com.",
    "deleteData.toast.idCopied": "ID de solicitud copiado",
    "deleteData.toast.copyFailed": "No se pudo copiar",
    // Campaign status badges
    "campaignsList.status.active": "Activa",
    "campaignsList.status.paused": "Pausada",
    "campaignsList.status.draft": "Borrador",
    "campaignsList.status.scheduled": "Programada",
    "campaignsList.status.failed": "Fallida",
    "campaignsList.status.needs_changes": "Requiere cambios",
    "campaignsList.status.archived": "Archivada",
    // Wizard — navigation
    "wizard.back": "Atrás",
    "wizard.next": "Siguiente",
    "wizard.backToList": "Volver a campañas",
    // Wizard — step labels
    "wizard.step.accounts": "Cuentas",
    "wizard.step.business": "Negocio",
    "wizard.step.goal": "Objetivo",
    "wizard.step.budget": "Presupuesto",
    "wizard.step.creative": "Creatividad",
    "wizard.step.aiBuild": "IA",
    "wizard.step.review": "Revisión",
    // Wizard — post-launch success
    "wizard.success.scheduledTitle": "¡Campaña programada!",
    "wizard.success.liveTitle": "¡Tu campaña está activa!",
    "wizard.success.scheduledDesc": "Tu campaña se lanzará el {date}.",
    "wizard.success.liveDesc": "Meta suele tardar entre 5 y 30 minutos en revisar y activar tus anuncios.",
    "wizard.success.label.name": "Nombre de la campaña",
    "wizard.success.label.platform": "Plataforma",
    "wizard.success.label.budget": "Presupuesto",
    "wizard.success.label.duration": "Duración",
    "wizard.success.label.duration.value": "{n} días",
    "wizard.success.metaCampaignId": "ID de campaña en Meta",
    "wizard.success.googleCampaignId": "ID de campaña en Google Ads",
    "wizard.success.youtubeCampaignId": "ID de campaña en YouTube",
    "wizard.success.tiktokUpsell.title": "¿Quieres lanzarla en TikTok también?",
    "wizard.success.tiktokUpsell.desc": "Llega a una audiencia más joven — ya tienes el copy, la creatividad y la segmentación. Tarda 2 minutos.",
    "wizard.success.tiktokUpsell.btn": "Crear versión en TikTok",
    "wizard.success.tiktokUpsell.later": "Quizás más tarde",
    "wizard.success.viewAll": "Ver todas las campañas",
    "wizard.success.dashboard": "Ir al Panel",
    // Wizard — header
    "wizard.header.title": "Lanza tu campaña con IA",
    "wizard.header.subtitle": "Siete pasos simples. Cada uno desbloquea el siguiente.",
    // Wizard — Step 1 (Accounts)
    "wizard.step1.title": "Paso 1 — Conecta tus cuentas",
    "wizard.step1.desc": "Selecciona las plataformas donde quieres publicar este anuncio.",
    "wizard.step1.loading": "Cargando conexiones…",
    "wizard.step1.noAccounts": "Sin cuentas conectadas.",
    "wizard.step1.settingsLink": "Ve a Ajustes › Integraciones para conectar tus plataformas.",
    "wizard.step1.settingsLinkPre": "Ve a",
    "wizard.step1.settingsLinkPost": "para conectar tus plataformas.",
    "wizard.step1.platformsLabel": "Plataformas",
    "wizard.step1.connectBadge": "Conectar",
    "wizard.step1.tip": "Consejo: publica el mismo anuncio en varias plataformas a la vez.",
    "wizard.step1.manageIntegrations": "Gestionar integraciones",
    // Wizard — Step 2 (Business)
    "wizard.step2.title": "Paso 2 — Cuéntanos sobre tu negocio",
    "wizard.step2.desc": "AdKavi IA usará esto para encontrar tu audiencia ideal automáticamente.",
    "wizard.step2.bizName": "Nombre del negocio",
    "wizard.step2.bizNamePlaceholder": "p. ej. Café Acme",
    "wizard.step2.industry": "Sector",
    "wizard.step2.industryPlaceholder": "Selecciona tu sector",
    "wizard.step2.promoting": "¿Qué estás promocionando?",
    "wizard.step2.promotingPlaceholder": "p. ej. Nuestra nueva mezcla de otoño, $18/bolsa con envío gratis desde $40",
    // Wizard — Step 3 (Goal)
    "wizard.step3.title": "Paso 3 — Elige tu objetivo",
    "wizard.step3.desc": "Elige qué quieres lograr con esta campaña.",
    "wizard.step3.selectedBadge": "Seleccionado",
    "wizard.step3.competitors.toggle": "Investigación de competidores",
    "wizard.step3.competitors.optional": "(opcional)",
    "wizard.step3.competitors.show": "Mostrar",
    "wizard.step3.competitors.hide": "Ocultar",
    "wizard.step3.competitors.desc": "Busca competidores para enriquecer tu mensaje antes de lanzar. Los guardaremos junto a tu campaña.",
    "wizard.step3.competitors.placeholder": "Añade un competidor (marca o sitio web)",
    // Wizard — Goal cards
    "wizard.goal.followers.title": "Ganar seguidores",
    "wizard.goal.followers.desc": "Crece tu audiencia y presencia de marca.",
    "wizard.goal.views.title": "Obtener más visualizaciones",
    "wizard.goal.views.desc": "Maximiza las reproducciones y el alcance.",
    "wizard.goal.leads.title": "Conseguir más leads",
    "wizard.goal.leads.desc": "Recoge datos de contacto de personas interesadas.",
    "wizard.goal.sales.title": "Impulsar ventas",
    "wizard.goal.sales.desc": "Lleva personas a tu tienda o proceso de compra.",
    "wizard.goal.whatsapp.title": "Recibir mensajes de WhatsApp",
    "wizard.goal.whatsapp.desc": "Inicia conversaciones en WhatsApp.",
    "wizard.goal.calls.title": "Recibir llamadas",
    "wizard.goal.calls.desc": "Las personas tocan tu anuncio y te llaman directamente.",
    "wizard.goal.event.title": "Promocionar un evento",
    "wizard.goal.event.desc": "Impulsa confirmaciones o venta de entradas.",
    "wizard.goal.traffic.title": "Atraer tráfico web",
    "wizard.goal.traffic.desc": "Lleva visitantes a tu sitio web.",
    // Wizard — Step 4 (Budget)
    "wizard.step4.title": "Paso 4 — Presupuesto y cuenta publicitaria",
    "wizard.step4.desc": "Define cuánto quieres gastar y cuánto tiempo dura la campaña.",
    "wizard.step4.budgetTypeLabel": "Tipo de presupuesto",
    "wizard.step4.dailyBudget.title": "Presupuesto diario",
    "wizard.step4.dailyBudget.desc": "Gasta una cantidad fija cada día (p. ej. $20/día).",
    "wizard.step4.totalBudget.title": "Presupuesto total",
    "wizard.step4.totalBudget.desc": "Distribuye un monto total en toda la campaña (p. ej. $500 en total).",
    "wizard.step4.dailyLabel": "Presupuesto diario (USD)",
    "wizard.step4.totalLabel": "Presupuesto total (USD)",
    "wizard.step4.durationLabel": "Duración de la campaña (días)",
    "wizard.step4.reach.label": "Alcance estimado",
    "wizard.step4.reach.value": "{n} personas",
    "wizard.step4.reach.desc": "Basado en un gasto total de ${total} con CPM promedio del sector. El alcance real varía según plataforma, audiencia y creatividad.",
    "wizard.step4.reach.note": "Las estimaciones se basan en promedios del sector. Conecta tu cuenta para datos de alcance en tiempo real.",
    "wizard.step4.warning.title": "Presupuesto por debajo del mínimo de la plataforma",
    "wizard.step4.warning.item": "{platform} requiere al menos ${min}/día. Tu gasto diario efectivo es ${spend}/día.",
    "wizard.step4.warning.hint": "Aumenta tu presupuesto o elimina plataformas que no cumplen el mínimo para continuar.",
    "wizard.step4.notice": "Tu presupuesto publicitario va directamente a las plataformas. AdKavi nunca retiene ni toca tu gasto publicitario — solo lo gestionamos por ti.",
    "wizard.step4.setup.title": "¿Aún no tienes una cuenta publicitaria? Crea una gratis:",
    "wizard.step4.setup.desc": "Crea la cuenta en la propia plataforma y luego conéctala en los Ajustes de AdKavi.",
    "wizard.step4.setup.meta": "Crear cuenta Meta Ads",
    "wizard.step4.setup.tiktok": "Crear cuenta TikTok Ads",
    "wizard.step4.setup.google": "Crear cuenta Google Ads",
    // Wizard — Step 5 (Creative)
    "wizard.step5.title": "Paso 5 — Crea tu creatividad publicitaria",
    "wizard.step5.desc": "Dos maneras de hacer tu creatividad.",
    "wizard.step5.optionA": "Opción A",
    "wizard.step5.upload.title": "Sube tu propia imagen o vídeo",
    "wizard.step5.upload.desc": "Usa una creatividad que ya tienes.",
    "wizard.step5.optionB": "Opción B",
    "wizard.step5.ai.title": "Crear con IA",
    "wizard.step5.ai.desc": "Diseña un flyer o publicación, genera un vídeo avatar con clonación de voz, o mejora una grabación.",
    "wizard.step5.uploadLabel": "Sube imagen o vídeo",
    "wizard.step5.selectedFile": "Seleccionado: {name}",
    "wizard.step5.aiType": "Elige un tipo de creatividad con IA",
    "wizard.step5.flyer": "Flyer / Publicación",
    "wizard.step5.avatar": "Vídeo Avatar IA",
    "wizard.step5.enhance": "Mejorar grabación",
    "wizard.step5.aiHint": "También puedes crearlo en nuestro Estudio IA y volver a lanzar.",
    "wizard.step5.aiHintPre": "También puedes crearlo en nuestro",
    "wizard.step5.aiHintPost": "y volver a lanzar.",
    // Wizard — Step 6 (AI Build)
    "wizard.step6.title": "Paso 6 — La IA construye tu campaña",
    "wizard.step6.desc": "Revisa y edita el copy, la ubicación y la audiencia.",
    "wizard.step6.generating": "AdKavi IA está escribiendo tu anuncio…",
    "wizard.step6.adLang": "Idioma del anuncio:",
    "wizard.step6.adCopy": "Copy del anuncio",
    "wizard.step6.regenerate": "Regenerar",
    "wizard.step6.english": "Inglés",
    "wizard.step6.spanish": "Español",
    "wizard.step6.headline": "Titular",
    "wizard.step6.description": "Descripción",
    "wizard.step6.headlineEs": "Titular (Español)",
    "wizard.step6.descriptionEs": "Descripción (Español)",
    "wizard.step6.headlineEsPlaceholder": "Titular en español",
    "wizard.step6.descriptionEsPlaceholder": "Descripción en español",
    "wizard.step6.esTip": "Consejo: haz clic en \"Regenerar\" con Español seleccionado y la IA escribirá la versión en español automáticamente.",
    "wizard.step6.cta": "Llamada a la acción",
    "wizard.step6.location.title": "Ubicación objetivo",
    "wizard.step6.location.label": "Añade ciudades, estados, países o códigos postales",
    "wizard.step6.location.placeholder": "p. ej. Miami, FL o 33101",
    "wizard.step6.location.radius": "Radio alrededor de la ubicación",
    "wizard.step6.audience.title": "Describe tu audiencia",
    "wizard.step6.audience.optional": "opcional",
    "wizard.step6.audience.placeholder": "p. ej. Mujeres de 25 a 45 años en Miami interesadas en el fitness y la alimentación saludable",
    "wizard.step6.audience.translate": "Traducir a segmentación Meta",
    "wizard.step6.audience.translating": "Traduciendo…",
    "wizard.step6.audience.translated": "Audiencia traducida — revisa los ajustes a continuación",
    "wizard.step6.target.title": "Audiencia objetivo",
    "wizard.step6.target.age": "Rango de edad",
    "wizard.step6.target.gender": "Género",
    "wizard.step6.target.genderAll": "Todos",
    "wizard.step6.target.genderMen": "Hombres",
    "wizard.step6.target.genderWomen": "Mujeres",
    "wizard.step6.target.interests": "Intereses",
    "wizard.step6.target.interestsHint": "La IA los preseleccionó según tu negocio. Añade o elimina según necesites.",
    "wizard.step6.target.interestPlaceholder": "Añade un interés",
    "wizard.step6.target.language": "Idioma",
    "wizard.step6.confirm": "He revisado y confirmado el copy del anuncio, la ubicación y la audiencia.",
    // Wizard — Step 7 (Review & Launch)
    "wizard.step7.title": "Paso 7 — Revisa tu campaña",
    "wizard.step7.desc": "Comprueba cada detalle. Cuando todo esté correcto, lanza.",
    "wizard.step7.label.name": "Nombre de la campaña",
    "wizard.step7.label.industry": "Sector",
    "wizard.step7.label.promoting": "Promocionando",
    "wizard.step7.label.platforms": "Plataformas",
    "wizard.step7.label.goal": "Objetivo",
    "wizard.step7.label.budget": "Presupuesto",
    "wizard.step7.label.creative": "Creatividad",
    "wizard.step7.label.competitors": "Investigación de competidores",
    "wizard.step7.noneAdded": "Ninguno añadido",
    "wizard.step7.adCopy": "Copy del anuncio",
    "wizard.step7.targeting": "Segmentación",
    "wizard.step7.label.locations": "Ubicaciones",
    "wizard.step7.label.radius": "Radio",
    "wizard.step7.label.age": "Edad",
    "wizard.step7.label.gender": "Género",
    "wizard.step7.label.interests": "Intereses",
    "wizard.step7.label.language": "Idioma",
    "wizard.step7.schedule.title": "¿Cuándo debe salir esto?",
    "wizard.step7.schedule.desc": "Lanza de inmediato o programa para una fecha y hora concretas.",
    "wizard.step7.schedule.now.title": "Lanzar ahora",
    "wizard.step7.schedule.now.desc": "Se activa inmediatamente tras la revisión",
    "wizard.step7.schedule.later.title": "Programar para más tarde",
    "wizard.step7.schedule.later.desc": "Elige una fecha y hora futuras",
    "wizard.step7.schedule.dateLabel": "Fecha y hora de inicio",
    "wizard.step7.launching": "Lanzando…",
    "wizard.step7.scheduleCampaign": "Programar campaña",
    "wizard.step7.launchCampaign": "Lanzar campaña",
    // Wizard — budget summary values
    "wizard.budget.daily": "${daily}/día × {days} días",
    "wizard.budget.total": "${total} en total",
    "wizard.budget.dailyFull": "${daily}/día × {days} días = ${computed}",
    "wizard.budget.totalFull": "${total} en total durante {days} días",
    // Campaign detail toasts
    "campaign.toast.loadFailed": "No se pudo cargar la campaña",
    "campaign.toast.notFound": "Campaña no encontrada",
    "campaign.toast.durationMin": "La duración debe ser de al menos 1 día",
    "campaign.toast.dailyMin": "El presupuesto diario debe ser mayor que 0",
    "campaign.toast.totalMin": "El presupuesto total debe ser mayor que 0",
    "campaign.toast.budgetUpdated": "Presupuesto actualizado",
    // Common
    "common.signInFirst": "Inicia sesión primero",
    "common.copied": "Copiado al portapapeles",
    "common.copyFailed": "No se pudo copiar",
    "common.saveFailed": "No se pudo guardar",
    "common.deleteFailed": "No se pudo eliminar",
    "common.signInToSave": "Inicia sesión para guardar",
    "common.signInToSchedule": "Inicia sesión para programar",
    "common.somethingWrong": "Algo salió mal. Inténtalo de nuevo.",
    "common.tryAgain": "Inténtalo de nuevo en un momento.",
    "common.undo": "Deshacer",
    "common.undone": "Deshecho",
    "common.undoFailed": "No se pudo deshacer",
    // AI Studio toasts
    "ai.toast.tellAdvertising": "Dinos qué estás anunciando",
    "ai.toast.rateLimit": "Más despacio — límite alcanzado. Inténtalo en un momento.",
    "ai.toast.creditsExhausted": "Créditos de IA agotados",
    "ai.toast.generationFailed": "Falló la generación",
    "ai.toast.noVariations": "No se devolvieron variaciones. Refina tu descripción.",
    "ai.toast.generated": "Se generaron {n} variaciones",
    "ai.toast.savedDraft": "Guardado como borrador en el calendario",
    "ai.toast.addAvatarScript": "Añade un guion para que el avatar lo lea",
    "ai.toast.startVideoFailed": "No se pudo iniciar el video",
    "ai.toast.heygenNoId": "HeyGen no devolvió un ID de video",
    "ai.toast.videoReady": "Video listo",
    "ai.toast.heygenFailed": "Falló la generación de HeyGen",
    "ai.toast.heygenShorter": "Prueba con un guion más corto",
    "ai.toast.addNarrationText": "Añade texto para narrar",
    "ai.toast.voFailed": "No se pudo generar la locución",
    "ai.toast.noAudio": "No se devolvió audio",
    "ai.toast.voReady": "Locución lista",
    "ai.toast.describeClip": "Describe el clip cinematográfico que quieres",
    "ai.toast.clipFailed": "No se pudo generar el clip",
    "ai.toast.noVideo": "No se devolvió video",
    "ai.toast.tellVideoAbout": "Cuéntanos de qué trata el video",
    "ai.toast.scriptFailed": "No se pudo generar el guion",
    "ai.toast.scriptReady": "Guion listo",
    "ai.toast.copiedScript": "Guion de locución copiado",
    "ai.toast.pasteEnglishUrl": "Pega una URL pública del MP3 o MP4 en inglés",
    "ai.toast.dubFailed": "No se pudo iniciar el doblaje",
    "ai.toast.dubReady": "Doblaje en español listo",
    "ai.toast.dubAudioFailed": "Doblado pero no se pudo obtener el audio",
    "ai.toast.dubError": "Falló el doblaje",
    "ai.toast.dubTimeout": "Tiempo de espera agotado para el doblaje",
    // Pro Cinematográfico
    "cinematicPro.title": "Pro Cinematográfico",
    "cinematicPro.subtitle": "La IA escribe tu historia → 4 clips Kling → locución ElevenLabs → unido en un video profesional.",
    "cinematicPro.briefLabel": "Describe tu marca o producto",
    "cinematicPro.briefPlaceholder": "ej. AdKavi — plataforma IA que escribe anuncios bilingüe, genera videos y lanza campañas en Meta y TikTok en minutos",
    "cinematicPro.advanced": "Opciones avanzadas",
    "cinematicPro.aspect": "Relación de aspecto",
    "cinematicPro.aspect.landscape": "Horizontal (YouTube, Meta)",
    "cinematicPro.aspect.vertical": "Vertical (Reels, TikTok)",
    "cinematicPro.aspect.square": "Cuadrado (Feed)",
    "cinematicPro.showTitles": "Mostrar títulos en pantalla",
    "cinematicPro.generate": "Generar video Pro Cinematográfico",
    "cinematicPro.generating": "Iniciando pipeline…",
    "cinematicPro.stage.generating_clips": "Kling renderizando {done}/{total} clips…",
    "cinematicPro.stage.merging": "Uniendo clips + locución con Creatomate…",
    "cinematicPro.stage.completed": "¡Video listo!",
    "cinematicPro.finalVideo": "Video final (unido)",
    "cinematicPro.voiceover": "Locución (40 seg)",
    "cinematicPro.download": "Descargar",
    "cinematicPro.addToCampaign": "Agregar a campaña",
    "cinematicPro.regenerate": "Regenerar",
    "cinematicPro.poweredBy": "Claude · Kling 1.6 · ElevenLabs · Creatomate",
    "cinematicPro.beats": "Escenas de la historia",
    "cinematicPro.clipDone": "Clip listo",
    "cinematicPro.clipPending": "Generando…",
    "cinematicPro.clipFailed": "Falló",
    "cinematicPro.toast.briefRequired": "Describe tu marca primero",
    "cinematicPro.toast.started": "Iniciado — historia + 4 clips + locución + unión (~5 min)",
    "cinematicPro.toast.failed": "Falló la generación",
    "cinematicPro.toast.ready": "¡Video listo!",
    // AI Image Ad
    "ai.image.title": "Anuncios con imagen IA",
    "ai.image.subtitle": "Describe tu negocio o campaña y la IA generará una imagen lista para anunciar.",
    "ai.image.briefLabel": "Describe tu negocio, producto o campaña",
    "ai.image.briefPlaceholder": "Ej. Marca premium de cuidado de la piel orgánico que lanza un sérum hidratante para mujeres de 25 a 45. Estilo limpio, minimalista, con luz natural.",
    "ai.image.format": "Formato",
    "ai.image.style": "Estilo",
    "ai.image.format.square": "Cuadrado (1:1)",
    "ai.image.format.story": "Historia (9:16)",
    "ai.image.format.landscape": "Horizontal (16:9)",
    "ai.image.format.portrait": "Vertical (4:5)",
    "ai.image.style.photoreal": "Fotorrealista",
    "ai.image.style.lifestyle": "Estilo de vida",
    "ai.image.style.studio": "Producto en estudio",
    "ai.image.style.minimal": "Minimalista / limpio",
    "ai.image.style.bold": "Atrevido y gráfico",
    "ai.image.generate": "Generar anuncio con imagen",
    "ai.image.generating": "Generando imagen…",
    "ai.image.download": "Descargar",
    "ai.image.addToCampaign": "Añadir a campaña",
    "ai.image.regenerate": "Regenerar",
    "ai.image.toast.brief": "Primero describe tu negocio o campaña",
    "ai.image.toast.failed": "No se pudo generar la imagen",
    "ai.image.toast.ready": "Anuncio con imagen listo",
    "ai.image.toast.saved": "Guardado en tus creatividades",
    "ai.image.toast.saveFailed": "No se pudo guardar la creatividad",
    "ai.carousel.title": "Anuncios de carrusel IA",
    "ai.carousel.subtitle": "La IA crea un anuncio deslizable con copy e imágenes por diapositiva.",
    "ai.carousel.briefLabel": "Describe tu negocio, producto o campaña",
    "ai.carousel.briefPlaceholder": "Ej: servicio de meal-prep para profesionales ocupados — 5 cenas por semana.",
    "ai.carousel.format": "Formato",
    "ai.carousel.style": "Estilo",
    "ai.carousel.slides": "Diapositivas",
    "ai.carousel.generate": "Generar carrusel",
    "ai.carousel.generating": "Generando diapositivas…",
    "ai.carousel.regenSlide": "Regenerar",
    "ai.carousel.upload": "Subir",
    "ai.carousel.downloadAll": "Descargar todo",
    "ai.carousel.toast.brief": "Primero describe tu negocio o campaña",
    "ai.carousel.toast.failed": "No se pudo generar el carrusel",
    "ai.carousel.toast.ready": "Carrusel listo",
    "ai.carousel.toast.regenerated": "Diapositiva regenerada",
    "ai.carousel.toast.uploaded": "Imagen subida",
    "ai.carousel.poweredBy": "Copy por Claude · Imágenes por Nano Banana",
    "ai.image.poweredBy": "Prompt con Claude · Imagen con Luma Photon",
    // AI Caption Writer
    "ai.caption.title": "Redactor de captions con IA",
    "ai.caption.subtitle": "Describe tu publicación o sube una imagen y obtén un caption listo con hashtags.",
    "ai.caption.descLabel": "Describe tu publicación (opcional si subes una imagen)",
    "ai.caption.descPlaceholder": "Ej: nueva colección de verano — vestidos de lino, vibra playera, lanzamiento el viernes.",
    "ai.caption.imageLabel": "Imagen de referencia (opcional)",
    "ai.caption.uploadImage": "Subir imagen",
    "ai.caption.platform": "Plataforma",
    "ai.caption.tone": "Tono",
    "ai.caption.tone.professional": "Profesional",
    "ai.caption.tone.fun": "Divertido",
    "ai.caption.tone.urgent": "Urgente",
    "ai.caption.tone.inspirational": "Inspirador",
    "ai.caption.language": "Idioma",
    "ai.caption.generate": "Generar caption",
    "ai.caption.generating": "Escribiendo caption…",
    "ai.caption.copy": "Copiar",
    "ai.caption.saveToCalendar": "Guardar en el calendario",
    "ai.caption.regenerate": "Regenerar",
    "ai.caption.toast.needInput": "Agrega una descripción o sube una imagen primero",
    "ai.caption.toast.failed": "No se pudo generar el caption",
    "ai.caption.toast.ready": "Caption listo",
    "ai.caption.toast.savedToCalendar": "Guardado en el calendario como borrador",
    "ai.caption.toast.imageTooLarge": "La imagen es demasiado grande (máx 4MB)",
    "ai.caption.poweredBy": "Con tecnología de Claude — revisa antes de publicar.",
    "ai.caption.variants": "Variantes",
    "ai.caption.variantsHint": "Genera varias versiones para elegir",
    "ai.caption.variant": "Variante",
    "ai.caption.pickVariant": "Elige una variante",
    "ai.caption.hashtagCount": "Cantidad de hashtags",
    "ai.caption.hashtagFew": "Pocos (3-5)",
    "ai.caption.hashtagMedium": "Medio (7-12)",
    "ai.caption.hashtagMany": "Muchos (12-20)",
    "ai.caption.modeLabel": "Formato",
    "ai.caption.modeSingle": "Publicación única",
    "ai.caption.modeCarousel": "Carrusel",
    "ai.caption.slideCount": "Diapositivas",
    "ai.caption.slidesTitle": "Diapositivas del carrusel",
    "ai.caption.slidesHint": "Toca una diapositiva para previsualizar",
    "ai.caption.slide": "Diapositiva",
    "ai.caption.slideHook": "Gancho",
    "ai.caption.slideCta": "CTA",
    "ai.caption.hashtagsExport.button": "Exportar",
    "ai.caption.hashtagsExport.copyAs": "Copiar como…",
    "ai.caption.hashtagsExport.withHash": "Con # (separados por espacio)",
    "ai.caption.hashtagsExport.plain": "Palabras sin #",
    "ai.caption.hashtagsExport.comma": "Separados por coma",
    "ai.caption.hashtagsExport.lines": "Uno por línea",
    "ai.caption.hashtagsExport.download": "Descargar .txt",
    "ai.caption.hashtagsExport.copied": "Copiados {n} hashtag(s)",
    "ai.caption.hashtagsExport.downloaded": "Hashtags descargados",
    "ai.caption.hashtagsTitle": "Hashtags",
    "ai.caption.hashtagsHint": "Haz clic para quitar. Se sincronizan al copiar y guardar.",
    "ai.caption.dropImage": "Suelta una imagen, pega o haz clic para subir",
    "ai.caption.dropHint": "PNG/JPG hasta 4MB",
    "ai.caption.removeImage": "Quitar imagen",
    "ai.caption.preview": "Vista previa",
    "ai.caption.previewHint": "Así se verá en la plataforma seleccionada",
    "ai.caption.scheduleFor": "Programar para",
    "ai.caption.pickDate": "Elige una fecha",
    "ai.caption.time": "Hora",
    "ai.caption.toast.imagePasted": "Imagen pegada",
    // Settings toasts
    "settings.toast.saveFailed": "No se pudo guardar",
    "settings.toast.profileUpdated": "Perfil actualizado",
    "settings.toast.dataDownloaded": "Tus datos se han descargado",
    "settings.toast.downloadFailed": "Error de descarga",
    "settings.toast.accountDeleted": "Tu cuenta ha sido eliminada permanentemente.",
    "settings.toast.accountDeleteFailed": "No se pudo eliminar la cuenta",
    "settings.toast.portalFailed": "No se pudo abrir el portal de facturación",
    "settings.toast.noInvoicePdfs": "No hay PDFs de facturas para descargar.",
    "settings.toast.invoicesDownloadedOne": "Se descargó {n} factura.",
    "settings.toast.invoicesDownloadedMany": "Se descargaron {n} facturas.",
    "settings.toast.zipFailed": "No se pudo crear el ZIP",
    "settings.toast.noInvoicesExport": "No hay facturas para exportar.",
    "settings.toast.exportedCsvOne": "Se exportó {n} factura como CSV.",
    "settings.toast.exportedCsvMany": "Se exportaron {n} facturas como CSV.",
    "settings.toast.savedPreference": "Guardado como preferencia",
    // Settings UI
    "settings.title": "Ajustes",
    "settings.subtitle": "Gestiona tu cuenta y suscripción.",
    "settings.tab.profile": "Perfil",
    "settings.tab.aiAgent": "Agente IA",
    "settings.tab.channels": "Canales",
    "settings.tab.integrations": "Integraciones",
    "settings.tab.controls": "Controles",
    "settings.tab.billing": "Facturación",
    "settings.profile.desc": "Tu nombre de visualización y moneda de informes.",
    "settings.profile.email": "Correo electrónico",
    "settings.profile.emailHint": "El correo es gestionado por tu inicio de sesión y no se puede cambiar aquí.",
    "settings.profile.displayName": "Nombre de visualización",
    "settings.profile.displayNamePlaceholder": "ej. Juan Marketing",
    "settings.profile.currency": "Moneda de informes",
    "settings.profile.currencyHint": "Todas las métricas del panel se muestran en esta moneda.",
    "settings.profile.save": "Guardar perfil",
    "settings.danger.title": "Zona de peligro",
    "settings.danger.desc": "Acciones irreversibles para tu cuenta.",
    "settings.danger.downloadTitle": "Descargar mis datos",
    "settings.danger.downloadDesc": "Descarga una copia de todos tus datos de campañas antes de eliminar.",
    "settings.danger.downloadBtn": "Descargar mis datos",
    "settings.danger.preparing": "Preparando…",
    "settings.danger.deleteTitle": "Eliminar cuenta",
    "settings.danger.deleteDesc": "Elimina permanentemente tu cuenta de AdKavi, todas las campañas y cuentas publicitarias conectadas. Esto no se puede deshacer.",
    "settings.danger.deleteBtn": "Eliminar mi cuenta",
    "settings.danger.confirmTitle": "¿Estás seguro?",
    "settings.danger.confirmWhat": "Esto eliminará permanentemente:",
    "settings.danger.confirmList1": "Tu cuenta de AdKavi",
    "settings.danger.confirmList2": "Todas tus campañas y datos publicitarios",
    "settings.danger.confirmList3": "Todas las cuentas conectadas de Meta, TikTok, Google y YouTube",
    "settings.danger.confirmList4": "Todo tu contenido y creatividades",
    "settings.danger.confirmTypePre": "Escribe",
    "settings.danger.confirmTypePost": "para confirmar",
    "settings.danger.deleting": "Eliminando…",
    "settings.danger.permanentlyDelete": "Eliminar permanentemente",
    "settings.billing.loading": "Cargando facturación…",
    "settings.billing.noActivePlan": "Sin plan activo",
    "settings.billing.noActivePlanDesc": "Elige un plan para iniciar tu prueba gratuita de 14 días. Se requiere tarjeta, cancela cuando quieras.",
    "settings.billing.startTrialTitle": "Inicia tu prueba gratuita",
    "settings.billing.startTrialDesc": "8 planes para creadores, empresas y agencias. 14 días gratis.",
    "settings.billing.viewPlans": "Ver planes",
    "settings.billing.cancelAtPeriodEnd": "· Cancela al final del período",
    "settings.billing.trialEnds": "La prueba gratuita termina el",
    "settings.billing.trialChargeWarning": "Tu tarjeta será cobrada ${price} ese día a menos que canceles.",
    "settings.billing.accessEnds": "El acceso termina",
    "settings.billing.renews": "Se renueva",
    "settings.billing.on": "el",
    "settings.billing.opening": "Abriendo…",
    "settings.billing.manageBilling": "Gestionar facturación",
    "settings.billing.upgradePlan": "Cambiar o mejorar plan",
    "settings.billing.comparePlans": "Comparar planes",
    "settings.billing.support": "Soporte de facturación",
    "settings.billing.supportDesc": "¿Preguntas sobre cargos, reembolsos o facturas? Estamos aquí para ayudarte.",
    "settings.billing.contactSupport": "Contactar soporte de facturación",
    "settings.billing.supportFootnote": "Normalmente respondemos en un día hábil.",
    "settings.inv.title": "Facturas",
    "settings.inv.desc": "Descarga tus facturas y recibos anteriores.",
    "settings.inv.loading": "Cargando facturas…",
    "settings.inv.loadError": "No se pudieron cargar las facturas: {error}",
    "settings.inv.empty": "Sin facturas aún — aparecerán aquí después de tu primer pago.",
    "settings.inv.search": "Buscar facturas",
    "settings.inv.searchPlaceholder": "Busca por número de factura, monto o fecha…",
    "settings.inv.status": "Estado",
    "settings.inv.statusAll": "Todos los estados",
    "settings.inv.statusPaid": "Pagado",
    "settings.inv.statusPending": "Pendiente",
    "settings.inv.statusFailed": "Fallido",
    "settings.inv.statusVoided": "Anulado",
    "settings.inv.statusDraft": "Borrador",
    "settings.inv.from": "Desde",
    "settings.inv.to": "Hasta",
    "settings.inv.exportCsv": "Exportar CSV",
    "settings.inv.zipping": "Comprimiendo…",
    "settings.inv.downloadAll": "Descargar todo ({n})",
    "settings.inv.noMatch": "No hay facturas que coincidan con los filtros.",
    "settings.inv.colDate": "Fecha",
    "settings.inv.colInvoice": "Factura",
    "settings.inv.colAmount": "Monto",
    "settings.inv.colStatus": "Estado",
    "settings.inv.colPdf": "PDF",
    "settings.inv.colView": "Ver",
    "settings.controls.title": "Cumplimiento y controles",
    "settings.controls.desc": "Establece salvaguardas para que cada campaña cumpla con tus requisitos de marca y legales.",
    "settings.controls.preview": "Vista previa",
    "settings.controls.activateHint": "Los controles de cumplimiento completos se activan cuando tu cuenta publicitaria esté conectada.",
    "settings.controls.ageTitle": "Restricciones de edad",
    "settings.controls.ageDesc": "Limita las campañas a rangos de edad específicos (ej. 21+ para alcohol, 18+ para juegos).",
    "settings.controls.geoTitle": "Restricciones geográficas",
    "settings.controls.geoDesc": "Bloquea anuncios en países, estados o ciudades específicos.",
    "settings.controls.contentTitle": "Restricciones de categoría de contenido",
    "settings.controls.contentDesc": "Evita que los anuncios aparezcan junto a contenido sensible (violencia, política, etc.).",
    "settings.controls.effectHint": "Esto tendrá efecto una vez que tu cuenta publicitaria esté conectada.",
    // Creative toasts
    "creative.toast.loadFailed": "No se pudieron cargar las creatividades",
    "creative.toast.signInToSave": "Inicia sesión para guardar",
    "creative.toast.saveFailed": "Error al guardar",
    "creative.toast.updated": "Creatividad actualizada",
    "creative.toast.saved": "Creatividad guardada",
    "creative.toast.deleteFailed": "Error al eliminar",
    "creative.toast.deleted": "Creatividad eliminada",
    "creative.toast.exportedPng": "PNG exportado",
    "creative.toast.exportFailed": "Error de exportación",
    // Content toasts
    "content.toast.loadFailed": "No se pudieron cargar las publicaciones",
    "content.toast.deleteFailed": "Error al eliminar",
    "content.toast.deleted": "Publicación eliminada",
    "content.toast.addTitle": "Añade un título",
    "content.toast.pickDate": "Elige fecha y hora",
    "content.toast.saveFailed": "No se pudo guardar",
    "content.toast.updated": "Publicación actualizada",
    "content.toast.trialLimit": "Límite de prueba alcanzado",
    "content.toast.scheduleFailed": "No se pudo programar",
    "content.toast.scheduled": "Publicación programada",
    "publish.title": "Publicar en plataformas",
    "publish.desc": "Elige dónde publicar. Cada plataforma puede tener su propio caption, formato y hora.",
    "publish.empty": "Selecciona una o más plataformas arriba.",
    "publish.field.format": "Formato",
    "publish.field.when": "Programar",
    "publish.field.caption": "Caption",
    "publish.field.captionPlaceholder": "Caption específico de la plataforma (opcional)",
    "publish.status.scheduled": "Programado",
    "publish.status.published": "Publicado",
    "publish.status.failed": "Fallido",
    "publish.toast.syncFailed": "No se pudieron sincronizar las publicaciones por plataforma.",
    "content.generate.button": "Generar con IA",
    "content.generate.generating": "Generando…",
    "content.generate.pick": "Elige una sugerencia — haz clic para usarla",
    "content.generate.failed": "No se pudo generar el caption",
    // Content Calendar UI
    "content.title": "Calendario de contenido",
    "content.subtitle": "Programa y administra tus publicaciones en todas las plataformas.",
    "content.createPost": "Crear publicación",
    "content.stat.thisMonth": "Este mes",
    "content.stat.scheduled": "Programadas",
    "content.stat.published": "Publicadas",
    "content.stat.drafts": "Borradores",
    "content.cal.today": "Hoy",
    "content.cal.sun": "DOM",
    "content.cal.mon": "LUN",
    "content.cal.tue": "MAR",
    "content.cal.wed": "MIÉ",
    "content.cal.thu": "JUE",
    "content.cal.fri": "VIE",
    "content.cal.sat": "SÁB",
    "content.cal.more": "+{n} más",
    "content.empty": "No hay publicaciones programadas. Haz clic en cualquier fecha o en + para crear tu primera publicación.",
    "content.dialog.editTitle": "Editar publicación",
    "content.dialog.scheduleTitle": "Programar una publicación",
    "content.dialog.editDesc": "Actualiza los detalles o cambia la programación.",
    "content.dialog.scheduleDesc": "Planifica una nueva publicación en tu calendario.",
    "content.field.title": "Título",
    "content.field.titlePlaceholder": "Lanzamiento de venta de primavera",
    "content.field.platform": "Plataforma",
    "content.field.type": "Tipo",
    "content.field.when": "Cuándo",
    "content.field.status": "Estado",
    "content.field.caption": "Caption",
    "content.field.captionPlaceholder": "Escribe tu caption…",
    "content.status.draft": "Borrador",
    "content.status.scheduled": "Programada",
    "content.status.published": "Publicada",
    "content.status.failed": "Fallida",
    "content.kind.post": "Publicación",
    "content.kind.carousel": "Carrusel",
    "content.kind.reel": "Reel",
    "content.kind.story": "Historia",
    "content.kind.video": "Video",
    "content.badge.createdPost": "Publicación creada",
    "content.pub.loading": "Cargando…",
    "content.btn.delete": "Eliminar",
    "content.btn.cancel": "Cancelar",
    "content.btn.save": "Guardar cambios",
    "content.btn.schedule": "Programar publicación",
    "content.cal.prevMonth": "Mes anterior",
    "content.cal.nextMonth": "Mes siguiente",
    // Growth Analytics
    "growth.title": "Analíticas de crecimiento",
    "growth.subtitle": "Rastrea tu actividad de publicación de contenido",
    "growth.stat.thisMonth": "Este mes",
    "growth.stat.published": "Publicados",
    "growth.stat.scheduled": "Programados",
    "growth.stat.drafts": "Borradores",
    "growth.chart.weekly": "Publicaciones por semana",
    "growth.chart.weeklySub": "Últimas 8 semanas",
    "growth.chart.platforms": "Por plataforma",
    "growth.upcoming.title": "Próximos 7 días",
    "growth.upcoming.empty": "No hay publicaciones programadas en los próximos 7 días.",
    "growth.connect.title": "Ve métricas de engagement reales",
    "growth.connect.desc": "Conecta tus cuentas sociales para ver likes, comentarios, shares y crecimiento de seguidores.",
    "growth.connect.cta": "Ir a Configuración",
    "sidebar.growth": "Analíticas de crecimiento",
    "sidebar.inbox": "Conversaciones",
    // Clients toasts
    "clients.toast.deleteFailed": "No se pudo eliminar",
    "clients.toast.removed": "Cliente eliminado",
    "clients.toast.addName": "Añade un nombre de cliente",
    "clients.toast.saveFailed": "No se pudo guardar",
    "clients.toast.updated": "Cliente actualizado",
    "clients.toast.addFailed": "No se pudo añadir",
    "clients.toast.added": "Cliente añadido",
    // Clients page UI
    "clients.heading": "Clientes",
    "clients.desc": "Cada cliente tiene sus propias campañas, publicaciones y creatividades. Usa el selector de la barra lateral para cambiar de cliente.",
    "clients.addBtn": "Añadir cliente",
    "clients.loading": "Cargando clientes…",
    "clients.empty.title": "Sin clientes aún",
    "clients.empty.desc": "Añade tu primera cuenta de cliente para organizar campañas, publicaciones y creatividades.",
    "clients.activeBadge": "Activo",
    "clients.activateHint": "Haz clic en activar para trabajar con este cliente",
    "clients.activateBtn": "Activar",
    "clients.editBtn": "Editar",
    "clients.deleteBtn": "Eliminar",
    "clients.deleteConfirm": "¿Eliminar esta cuenta de cliente? Las campañas y publicaciones existentes quedarán desvinculadas.",
    "clients.dialog.editTitle": "Editar cliente",
    "clients.dialog.addTitle": "Añadir un cliente",
    "clients.dialog.desc": "Dale un nombre al cliente y elige un color de marca para identificarlo fácilmente en las listas.",
    "clients.dialog.nameLabel": "Nombre",
    "clients.dialog.colorLabel": "Color de marca",
    "clients.dialog.cancel": "Cancelar",
    "clients.dialog.saving": "Guardando…",
    "clients.dialog.saveBtn": "Guardar cambios",
    "clients.dialog.addBtn": "Añadir cliente",
    // Integration toasts
    "integration.toast.disconnectFailed": "No se pudo desconectar",
    "integration.toast.googleDisconnected": "Google desconectado",
    "integration.toast.metaDisconnected": "Meta desconectado",
    "integration.toast.tiktokDisconnected": "TikTok desconectado",
    "integration.toast.metaConnected": "Meta conectado",
    "integration.toast.metaConnectFailed": "No se pudo conectar Meta",
    "integration.toast.tiktokStartFailed": "No se pudo iniciar la conexión con TikTok",
    "integration.toast.googleStartFailed": "No se pudo iniciar la conexión con Google",
    // Voice/Avatar toasts
    "voice.toast.invalidFormat": "Formato no válido",
    "voice.toast.invalidFormatDesc": "Sube un archivo MP3 o WAV.",
    "voice.toast.tooLarge": "Archivo demasiado grande",
    "voice.toast.tooLargeDesc": "Máximo 50 MB. El tuyo pesa {size} MB.",
    "voice.toast.cloning": "Clonando tu voz — listo en unos minutos.",
    "voice.toast.uploadFailed": "Error al subir",
    "avatar.toast.invalidFormatDesc": "Sube un archivo MP4 o WebM.",
    "avatar.toast.tooLargeDesc": "Máximo 100 MB. El tuyo pesa {size} MB.",
    "avatar.toast.processing": "Procesando tu avatar — 20–30 minutos.",
    // Video library
    "video.toast.deleteFailed": "No se pudo eliminar el video",
    "video.toast.deleted": "Video eliminado",
    // Promo code
    "promo.toast.validateFailed": "No se pudo validar el código",
    "promo.toast.invalid": "Código promocional no válido",
    "promo.toast.applied": "{code} aplicado",
    // Billing/checkout
    "checkout.toast.signInFirst": "Inicia sesión primero",
    "checkout.toast.startFailed": "No se pudo iniciar el pago",
    // Account type / onboarding
    "onboarding.toast.saveChoiceFailed": "No se pudo guardar tu elección. Inténtalo de nuevo.",
    // Admin audit
    "admin.toast.noRowsExport": "No hay filas para exportar",
    "admin.toast.exportedRows": "Se exportaron {n} filas",
    "admin.toast.exportFailed": "Error al exportar",
    // Audience Insights
    "audience.title": "Insights de audiencia",
    "audience.subtitle": "Quién interactúa con tus anuncios — edad, ubicación, dispositivo y mejor segmento.",
    "audience.demoData": "Datos de demo",
    "audience.age": "Rango de edad",
    "audience.gender": "Género",
    "audience.gender.female": "Mujeres",
    "audience.gender.male": "Hombres",
    "audience.gender.other": "Otro",
    "audience.location": "Principales ubicaciones",
    "audience.country.us": "Estados Unidos",
    "audience.country.mx": "México",
    "audience.country.es": "España",
    "audience.country.ar": "Argentina",
    "audience.country.co": "Colombia",
    "audience.device": "Dispositivo",
    "audience.device.mobile": "Móvil",
    "audience.device.desktop": "Escritorio",
    "audience.device.tablet": "Tablet",
    "audience.topSegment": "Segmento con mejor rendimiento",
    "audience.segment.urban25": "Urbanos 25–34, primero móvil",
    "audience.segment.parents": "Padres y madres 30–44",
    "audience.segment.smbOwners": "Dueños de pequeños negocios",
    "audience.segment.fitness": "Fitness y bienestar",
    "audience.segment.techEarly": "Adoptadores tempranos de tecnología",
    "audience.topSegmentDetail": "{share}% de las conversiones · ROAS {roas}×",
    // Audit Trail
    "audit.title": "Registro de actividad",
    "audit.subtitle": "Cronología de cada acción realizada en esta campaña.",
    "audit.demoNote": "Mostrando actividad reciente. Las entradas más antiguas se archivan tras 90 días.",
    "audit.user.you": "Tú",
    "audit.user.aiManager": "Manager IA",
    "audit.event.created": "Campaña creada",
    "audit.event.budget_updated": "Presupuesto actualizado",
    "audit.event.approved": "Aprobada para lanzamiento",
    "audit.event.launched": "Lanzada en vivo",
    "audit.event.paused": "Pausada",
    "audit.event.edited": "Creatividad editada",
    "audit.event.viewed": "Vista",
    "audit.detail.creativeUpdated": "Titular y CTA refinados por AI Studio.",
    "audit.detail.budgetSet": "Presupuesto diario configurado y guardado.",
    "audit.detail.budgetGuardrail": "Pausa automática — límite de gasto diario alcanzado.",
    // Campaign detail page
    "campaign.back": "Volver a campañas",
    "campaign.export": "Exportar informe",
    "campaign.loading": "Cargando campaña…",
    "campaign.budget.title": "Presupuesto vs Gasto",
    "campaign.budget.desc": "Cuánto de tu presupuesto total se ha gastado hasta ahora.",
    "campaign.budget.spent": "{amount} gastado",
    "campaign.budget.budget": "{amount} de presupuesto",
    "campaign.budget.used": "{pct}% usado",
    "campaign.budget.remaining": "{amount} restantes",
    "campaign.platform.title": "Desglose por plataforma",
    "campaign.platform.desc": "Presupuesto y gasto por plataforma publicitaria.",
    "campaign.platform.note": "El presupuesto se asigna a la plataforma elegida al crear la campaña. Próximamente: asignación multiplataforma.",
    "campaign.platform.active": "Activa",
    "campaign.details.title": "Detalles del presupuesto",
    "campaign.details.desc": "Presupuesto diario, total, duración y alcance estimado.",
    "campaign.details.edit": "Editar",
    "campaign.details.cancel": "Cancelar",
    "campaign.details.save": "Guardar cambios",
    "campaign.details.budgetType": "Tipo de presupuesto",
    "campaign.details.budgetTypeDaily": "Presupuesto diario",
    "campaign.details.budgetTypeTotal": "Presupuesto total",
    "campaign.details.dailyLabel": "Presupuesto diario (USD)",
    "campaign.details.totalLabel": "Presupuesto total (USD)",
    "campaign.details.durationLabel": "Duración (días)",
    "campaign.details.newTotal": "Nuevo total",
    "campaign.details.newReach": "Nuevo alcance estimado",
    "campaign.details.people": "{n} personas",
    "campaign.details.dailyValue": "{amount}/día",
    "campaign.details.daysValue": "{n} días",
    "campaign.field.budgetType": "Tipo de presupuesto",
    "campaign.field.dailyBudget": "Presupuesto diario",
    "campaign.field.totalBudget": "Presupuesto total",
    "campaign.field.duration": "Duración",
    "campaign.field.estimatedReach": "Alcance estimado",
    "campaign.perf.title": "Rendimiento",
    "campaign.perf.desc": "Totales acumulados de las plataformas conectadas.",
    "campaign.perf.reach": "Alcance",
    "campaign.perf.clicks": "Clics",
    "campaign.perf.leads": "Leads",
    "campaign.perf.conversions": "Conversiones",
    "campaign.perf.revenue": "Ingresos",
    "campaign.perf.spend": "Gasto",
    "campaign.perf.roas": "ROAS",
    "campaign.confirm.title": "Confirmar cambios de presupuesto",
    "campaign.confirm.dailyBody": "Vas a cambiar el presupuesto de esta campaña a {daily}/día × {days} días = {total}. Esto afectará al ritmo de gasto y a la entrega en plataforma. ¿Continuar?",
    "campaign.confirm.totalBody": "Vas a cambiar el presupuesto de esta campaña a {total} en total durante {days} días. Esto afectará al ritmo de gasto y a la entrega en plataforma. ¿Continuar?",
    "campaign.confirm.cancel": "Cancelar",
    "campaign.confirm.confirm": "Sí, guardar cambios",
    // Campaigns list page
    "campaignsList.title": "Campañas",
    "campaignsList.subtitle": "Todas las campañas publicitarias de tus cuentas conectadas.",
    "campaignsList.newAi": "Nueva campaña con IA",
    "campaignsList.trialLimit": "Límite de prueba alcanzado para este cliente",
    "campaignsList.loading": "Cargando campañas…",
    "campaignsList.empty.title": "Aún no hay campañas",
    "campaignsList.empty.body": "Genera tu primera campaña con IA — brief, creatividades y segmentación en segundos.",
    "campaignsList.empty.cta": "Crear campaña",
    "campaignsList.col.name": "Nombre",
    "campaignsList.col.platform": "Plataforma",
    "campaignsList.col.budget": "Presupuesto",
    "campaignsList.col.spend": "Progreso de gasto",
    "campaignsList.col.status": "Estado",
    "campaignsList.col.created": "Creada",
    // Audience AI recommendation
    "audience.rec.title": "Recomendación de IA",
    "audience.rec.refresh": "Actualizar",
    "audience.rec.loading": "Analizando tu audiencia…",
    "audience.rec.empty": "Aún no hay recomendación disponible.",
    "audience.rec.error": "No pudimos generar una recomendación. Inténtalo de nuevo.",
    // Audit trail filters & PDF export
    "audit.filter.actions": "Acciones",
    "audit.filter.actionType": "Filtrar por acción",
    "audit.filter.dateRange": "Rango de fechas",
    "audit.filter.from": "Desde",
    "audit.filter.to": "Hasta",
    "audit.filter.reset": "Restablecer",
    "audit.empty.filtered": "Ninguna acción coincide con los filtros.",
    "audit.export.pdf": "Exportar PDF",
    "audit.pdf.title": "Registro de actividad de la campaña",
    "audit.pdf.campaign": "Campaña",
    "audit.pdf.dateRange": "Rango de fechas",
    "audit.pdf.generated": "Generado",
    "audit.pdf.actions": "Acciones",
    "audit.pdf.noResults": "Sin acciones en el rango seleccionado.",
    "audit.pdf.exported": "Registro de actividad exportado.",
    "audit.pdf.exportFailed": "No se pudo exportar el registro.",
    // Metric drill-down
    "metric.detail.subtitle": "Desglose diario del periodo seleccionado.",
    "metric.detail.empty": "Sin datos para esta métrica en el rango seleccionado.",
    "metric.detail.total": "Total",
    "metric.detail.avg": "Promedio / día",
    "metric.detail.peak": "Pico",
    "metric.detail.trend": "Tendencia",
    "metric.detail.openAria": "abrir desglose detallado",
    "costAlerts.title": "Alertas de costo",
    "costAlerts.desc": "Recibe un email cuando tu costo por resultado supere un umbral (máx. 1 email por día por alerta).",
    "costAlerts.loading": "Cargando alertas…",
    "costAlerts.empty": "Aún no hay alertas. Agrega una abajo para recibir notificaciones.",
    "costAlerts.addTitle": "Agregar una nueva alerta",
    "costAlerts.add": "Agregar",
    "costAlerts.added": "Alerta agregada",
    "costAlerts.deleted": "Alerta eliminada",
    "costAlerts.delete": "Eliminar alerta",
    "costAlerts.duplicate": "Ya existe una alerta para esta métrica",
    "costAlerts.invalidThreshold": "Ingresa un umbral mayor que 0",
    "costAlerts.metricLabel": "Métrica",
    "costAlerts.thresholdLabel": "Umbral",
    "costAlerts.thresholdPlaceholder": "ej. 2.50",
    "costAlerts.thresholdLine": "Umbral: {value}",
    "costAlerts.lastSent": "Última alerta enviada {date}",
    "costAlerts.checkNow": "Verificar ahora",
    "costAlerts.checkSent": "Se enviaron {n} email(s) de alerta",
    "costAlerts.checkBreachOnly": "Umbral superado pero ya se notificó en las últimas 24h",
    "costAlerts.checkNoBreach": "Todas las alertas dentro del umbral",
    "costAlerts.metric.cpc": "Costo por Clic (CPC)",
    "costAlerts.metric.cpl": "Costo por Lead (CPL)",
    "costAlerts.metric.cpa": "Costo por Conversión (CPA)",
    "costAlerts.metric.spend": "Gasto total (últimas 24h)",
    "costAlerts.history.title": "Historial de alertas",
    "costAlerts.history.desc": "Notificaciones recientes enviadas para esta campaña.",
    "costAlerts.history.empty": "Aún no se han enviado alertas.",
    "costAlerts.history.loading": "Cargando historial…",
    "costAlerts.history.show": "Mostrar historial",
    "costAlerts.history.hide": "Ocultar historial",
    "costAlerts.history.row": "{metric} — observado {observed}, umbral {threshold}",
    "costAlerts.history.sentAt": "Enviado {date}",
    "costAlerts.sendTest": "Enviar email de prueba",
    "costAlerts.testSent": "Email de prueba enviado a {email}",
    "costAlerts.testFailed": "No se pudo enviar el email de prueba",
    // Budget Optimizer
    "optimizer.title": "Optimizador de presupuesto IA",
    "optimizer.subtitle": "Reasigna presupuesto hacia tus anuncios con mejor rendimiento.",
    "optimizer.loading": "Analizando el rendimiento de los anuncios…",
    "optimizer.empty": "Aún no hay anuncios registrados para esta campaña.",
    "optimizer.seed": "Crear anuncios de muestra",
    "optimizer.seeded": "Anuncios de muestra creados",
    "optimizer.bestPerformer": "Mejor rendimiento",
    "optimizer.spend": "Gasto",
    "optimizer.conversions": "Conv.",
    "optimizer.shareSum": "El total actual es {sum}% — la recomendación se normalizará al 100%.",
    "optimizer.recTitle": "Cambio de presupuesto recomendado",
    "optimizer.recBody": "Mueve presupuesto de {losers} a {winner} según ROAS y coste por conversión.",
    "optimizer.apply": "Aplicar recomendación",
    "optimizer.applied": "Reparto de presupuesto actualizado",
    "optimizer.needMoreData": "Se necesitan al menos dos anuncios con gasto para hacer una recomendación.",
    "optimizer.noChange": "El reparto de presupuesto ya está optimizado.",
    // A/B Testing
    "ab.title": "Pruebas A/B",
    "ab.subtitle": "Compara dos variantes de anuncio cara a cara y elige la ganadora.",
    "ab.loading": "Cargando pruebas…",
    "ab.empty": "Aún no hay pruebas. Inicia una para comparar dos anuncios.",
    "ab.needTwoAds": "Añade al menos dos anuncios activos a esta campaña para iniciar una prueba A/B.",
    "ab.missingAds": "La prueba hace referencia a anuncios que ya no existen",
    "ab.newTest": "Nueva prueba A/B",
    "ab.created": "Prueba A/B iniciada",
    "ab.stopped": "Prueba detenida",
    "ab.completedWithPause": "Ganador declarado — perdedor en pausa",
    "ab.completedTie": "Prueba completada — empate",
    "ab.adPaused": "Anuncio en pausa",
    "ab.started": "Iniciada",
    "ab.durationDays": "Ventana de {n}d",
    "ab.spendThreshold": "Detener al gastar ${v}",
    "ab.stop": "Detener",
    "ab.pause": "Pausar",
    "ab.paused": "En pausa",
    "ab.variant": "Variante",
    "ab.declareWinner": "Declarar ganador y pausar perdedor",
    "ab.markTie": "Marcar como empate",
    "ab.triggerSpend": "Umbral de gasto alcanzado",
    "ab.triggerDuration": "Ventana de prueba finalizada",
    "ab.recWinner": "{winner} va ganando. Pausa {loser} y traslada el esfuerzo al ganador.",
    "ab.recTie": "Ambas variantes rinden de forma similar — sin un ganador claro.",
    "ab.endedWinner": "Ganador: {winner}",
    "ab.endedTie": "Terminó en empate",
    "ab.status.running": "En curso",
    "ab.status.completed": "Completada",
    "ab.status.stopped": "Detenida",
    "ab.stat.spend": "Gasto",
    "ab.stat.conv": "Conv.",
    "ab.stat.clicks": "Clics",
    "ab.stat.revenue": "Ingresos",
    "ab.error.nameRequired": "Pon un nombre a la prueba",
    "ab.error.pickTwo": "Elige dos anuncios diferentes",
    "ab.dialog.title": "Nueva prueba A/B",
    "ab.dialog.desc": "Elige dos anuncios de esta campaña y define una condición de parada.",
    "ab.dialog.name": "Nombre de la prueba",
    "ab.dialog.namePh": "Copy de gancho v1 vs v2",
    "ab.dialog.variantA": "Variante A",
    "ab.dialog.variantB": "Variante B",
    "ab.dialog.pickAd": "Elige un anuncio…",
    "ab.dialog.duration": "Duración (días)",
    "ab.dialog.spend": "Umbral de gasto",
    "ab.dialog.note": "Declaramos un ganador cuando se cumpla cualquiera de las condiciones. Deja un campo vacío para ignorarlo.",
    "ab.dialog.create": "Iniciar prueba",
    "ai.caption.preset.title": "Preset de voz de marca",
    "ai.caption.preset.placeholder": "Elige un preset…",
    "ai.caption.preset.none": "Sin preset",
    "ai.caption.preset.saveCurrent": "Guardar como preset",
    "ai.caption.preset.styleNotes": "Notas de estilo",
    "ai.caption.preset.styleNotesPlaceholder": "ej. cercano pero experto, sin emojis al inicio, siempre terminar con una pregunta",
    "ai.caption.preset.keywords": "Palabras clave de marca",
    "ai.caption.preset.keywordsPlaceholder": "separadas por coma, ej. artesanal, sostenible, lote-pequeño",
    "ai.caption.preset.keywordsHint": "Hasta 20 palabras clave. Sepáralas con comas.",
    "ai.caption.preset.dialogTitle": "Guardar preset de voz de marca",
    "ai.caption.preset.dialogDesc": "Guarda tu tono, notas de estilo y palabras clave actuales para reutilizarlas con un clic.",
    "ai.caption.preset.nameLabel": "Nombre del preset",
    "ai.caption.preset.namePlaceholder": "ej. Voz fundadora, Rebajas",
    "ai.caption.preset.nameRequired": "Ingresa un nombre para el preset",
    "ai.caption.preset.save": "Guardar preset",
    "ai.caption.preset.cancel": "Cancelar",
    "ai.caption.preset.saved": "Preset guardado",
    "ai.caption.preset.deleted": "Preset eliminado",
    "sendToCampaign.title": "Enviar a campaña",
    "sendToCampaign.description": "Adjunta este video como creatividad en una campaña.",
    "sendToCampaign.loading": "Cargando campañas…",
    "sendToCampaign.tab.existing": "Campaña existente",
    "sendToCampaign.tab.new": "Nueva campaña",
    "sendToCampaign.pickLabel": "Elige una campaña",
    "sendToCampaign.pickPlaceholder": "Selecciona una campaña…",
    "sendToCampaign.noCampaigns": "Aún no tienes campañas. Crea una abajo.",
    "sendToCampaign.newNameLabel": "Nombre de la campaña",
    "sendToCampaign.newNamePlaceholder": "ej. Lanzamiento de primavera",
    "sendToCampaign.cancel": "Cancelar",
    "sendToCampaign.submit": "Enviar",
    "sendToCampaign.defaultName": "Video del AI Studio",
    "sendToCampaign.error.nameRequired": "Ingresa un nombre para la campaña",
    "sendToCampaign.error.pickCampaign": "Elige una campaña",
    "sendToCampaign.toast.sent": "Video enviado a la campaña",
    "sendToCampaign.toast.view": "Ver campaña",
    "sidebar.competitors": "Investigación de competencia",
    "sidebar.audiences": "Segmentación de audiencias",
    "audiences.eyebrow": "Segmentación de audiencias",
    "audiences.title": "Construye audiencias más inteligentes",
    "audiences.subtitle": "Crea audiencias personalizadas, de retargeting y similares. Sincronízalas con Meta, TikTok y Google cuando tus cuentas estén conectadas.",
    "audiences.status.ready": "Lista",
    "audiences.status.pending": "Pendiente de conexión API",
    "audiences.tabs.custom": "Audiencias personalizadas",
    "audiences.tabs.retarget": "Retargeting",
    "audiences.tabs.lookalike": "Similares",
    "audiences.field.name": "Nombre de la audiencia",
    "audiences.custom.title": "Audiencia personalizada",
    "audiences.custom.desc": "Crea una audiencia con visitantes de tu sitio web o subiendo una lista de clientes (CSV).",
    "audiences.custom.namePlaceholder": "ej. Visitantes del sitio – últimos 30 días",
    "audiences.custom.website": "Desde una URL",
    "audiences.custom.upload": "Desde una lista de clientes",
    "audiences.custom.contacts": "contactos",
    "audiences.custom.createFromWebsite": "Crear desde sitio web",
    "audiences.custom.createFromList": "Crear desde lista",
    "audiences.custom.listTitle": "Tus audiencias personalizadas",
    "audiences.custom.empty": "Aún no hay audiencias personalizadas. Crea la primera arriba.",
    "audiences.retarget.title": "Audiencia de retargeting",
    "audiences.retarget.desc": "Dirige tus anuncios a personas que vieron tu ad pero no convirtieron.",
    "audiences.retarget.namePlaceholder": "ej. Promo primavera – no convirtieron",
    "audiences.retarget.campaign": "Campaña de origen",
    "audiences.retarget.pickCampaign": "Elige una campaña",
    "audiences.retarget.noCampaigns": "Aún no tienes campañas — crea una primero.",
    "audiences.retarget.window": "Ventana de retroceso",
    "audiences.retarget.days": "días",
    "audiences.retarget.conditionLabel": "Condición: vieron el anuncio pero no convirtieron.",
    "audiences.retarget.create": "Crear audiencia de retargeting",
    "audiences.retarget.listTitle": "Tus audiencias de retargeting",
    "audiences.retarget.empty": "Aún no hay audiencias de retargeting.",
    "audiences.lookalike.title": "Audiencia similar",
    "audiences.lookalike.desc": "Encuentra personas nuevas que se comportan como tus mejores clientes.",
    "audiences.lookalike.namePlaceholder": "ej. Top compradores — similar 1%",
    "audiences.lookalike.source": "Audiencia de origen",
    "audiences.lookalike.pickSource": "Elige una audiencia personalizada",
    "audiences.lookalike.noSources": "Crea primero una audiencia personalizada.",
    "audiences.lookalike.country": "País",
    "audiences.lookalike.similarity": "Similitud",
    "audiences.lookalike.similarityHint": "Menor % = más parecido a tu origen. Mayor % = mayor alcance.",
    "audiences.lookalike.create": "Crear audiencia similar",
    "audiences.lookalike.listTitle": "Tus audiencias similares",
    "audiences.lookalike.empty": "Aún no hay audiencias similares.",
    "audiences.error.nameRequired": "Ingresa un nombre.",
    "audiences.error.urlRequired": "Ingresa la URL del sitio web.",
    "audiences.error.fileRequired": "Sube una lista de clientes.",
    "audiences.error.retargetFields": "Ingresa un nombre y elige una campaña.",
    "audiences.error.lookalikeFields": "Ingresa un nombre y elige una audiencia de origen.",
    "audiences.error.createFailed": "No se pudo guardar la audiencia. Intenta de nuevo.",
    "audiences.toast.customCreated": "Audiencia personalizada guardada.",
    "audiences.toast.retargetCreated": "Audiencia de retargeting guardada.",
    "audiences.toast.lookalikeCreated": "Audiencia similar guardada.",
    "competitorsPage.eyebrow": "Investigación de competencia",
    "competitorsPage.title": "Espía a tu competencia",
    "competitorsPage.subtitle": "Busca una marca o página, mira todos los anuncios activos que está corriendo, cuánto tiempo llevan vivos y un plan de IA para superarlos.",
    "competitorsPage.searchPlaceholder": "Nombre del competidor o sitio web (ej. nike.com)",
    "competitorsPage.searchCta": "Buscar anuncios",
    "competitorsPage.error.emptyQuery": "Ingresa un competidor para buscar.",
    "competitorsPage.error.searchFailed": "No se pudieron cargar los anuncios. Intenta de nuevo.",
    "competitorsPage.error.insightFailed": "No se pudieron generar los insights.",
    "competitorsPage.mockBadge": "Mostrando datos simulados",
    "competitorsPage.mockDesc": "La conexión en vivo con la Meta Ad Library llegará pronto. El diseño, los filtros y el análisis de IA funcionarán igual cuando se conecte.",
    "competitorsPage.empty.title": "Elige un competidor para empezar",
    "competitorsPage.empty.desc": "Prueba con una marca como \"Gymshark\" o un dominio como \"hubspot.com\". Traeremos todos los anuncios activos que podamos ver.",
    "competitorsPage.platformSpy.title": "Espía de plataformas",
    "competitorsPage.platformSpy.desc": "Dónde está corriendo anuncios {brand} ahora.",
    "competitorsPage.ads.title": "Anuncios activos de {brand}",
    "competitorsPage.ads.summary": "{count} anuncios · {long} de larga duración",
    "competitorsPage.ads.longBadge": "Larga duración = funcionando",
    "competitorsPage.ads.testingBadge": "En pruebas",
    "competitorsPage.ads.runningFor": "{days} días activo",
    "competitorsPage.insight.title": "Insight de IA — cómo superarlos",
    "competitorsPage.insight.desc": "Nuestra IA analiza sus anuncios y te entrega un plan concreto para tus campañas.",
    "competitorsPage.insight.cta": "Generar insights con IA",
    "competitorsPage.insight.regenerate": "Regenerar",
    "competitorsPage.insight.loading": "Analizando anuncios…",
    "competitorsPage.insight.working": "Qué les está funcionando",
    "competitorsPage.insight.weak": "Dónde están débiles",
    "competitorsPage.insight.beat": "Cómo superarlos",
    "competitorsPage.insight.hooks": "Ganchos para probar",
    "lipsync.title": "Doblaje + Lip Sync en español",
    "lipsync.subtitle": "Dobla un video al español y genera una versión con lip sync de avatar automáticamente.",
    "lipsync.tabs.upload": "Subir video",
    "lipsync.tabs.library": "Desde biblioteca",
    "lipsync.upload.cta": "Haz clic para subir un video",
    "lipsync.upload.hint": "MP4, MOV, WebM hasta 100 MB",
    "lipsync.library.empty": "Aún no tienes videos. Genera uno arriba primero.",
    "lipsync.library.placeholder": "Elige un video de tu biblioteca",
    "lipsync.library.start": "Iniciar lip sync en español",
    "lipsync.libraryItem": "Video sin título",
    "lipsync.step.uploading": "Subiendo",
    "lipsync.step.dubbing": "Doblando al español",
    "lipsync.step.lipsync": "Lip sync",
    "lipsync.step.ready": "Listo",
    "lipsync.actions.download": "Descargar",
    "lipsync.actions.sendToCampaign": "Enviar a campaña",
    "lipsync.actions.startOver": "Empezar de nuevo",
    "lipsync.toast.signInRequired": "Inicia sesión para subir videos.",
    "lipsync.toast.invalidFileType": "Elige un archivo de video.",
    "lipsync.toast.fileTooBig": "El video debe pesar menos de 100 MB.",
    "lipsync.toast.uploadFailed": "Falló la subida",
    "lipsync.toast.dubFailed": "Falló el doblaje al español",
    "lipsync.toast.dubTimeout": "El doblaje tardó demasiado. Prueba un clip más corto.",
    "lipsync.toast.transcriptFailed": "No se pudo obtener la transcripción en español.",
    "lipsync.toast.lipsyncFailed": "Falló la generación del lip sync",
    "lipsync.toast.lipsyncTimeout": "El lip sync tardó demasiado. Intenta de nuevo.",
    "lipsync.toast.ready": "¡Tu video con lip sync en español está listo!",
    "notifications.aria": "Notificaciones",
    "notifications.title": "Notificaciones",
    "notifications.markAllRead": "Marcar todo como leído",
    "notifications.empty": "Estás al día.",
    "notifications.manage": "Ajustes de notificaciones",
    "notifications.costAlert.title": "Alerta de coste activada",
    "notifications.costAlert.body": "{metric} en {campaign} superó tu umbral.",
    "notifications.abWinner.title": "Ganador de prueba A/B detectado",
    "notifications.abWinner.body": "{name}: la variante {winner} lidera en ROAS/CPA.",
    "notifications.autoPause.title": "Campaña pausada automáticamente",
    "notifications.autoPause.body": "{campaign} se pausó: {reason}",
    "notifications.deletion.title": "Solicitud de eliminación enviada",
    "notifications.deletion.body": "Solicitud {id} recibida. Te avisaremos por email.",
    "whitelabel.tab": "Marca",
    "whitelabel.title": "Marca blanca",
    "whitelabel.description": "Reemplaza la marca AdKavi dentro de tu panel con tu propio logo, color y nombre de empresa.",
    "whitelabel.lockedDesc": "La marca blanca está disponible desde el plan {plan}.",
    "whitelabel.upgrade": "Mejorar plan",
    "whitelabel.enableLabel": "Activar marca blanca",
    "whitelabel.enableDesc": "Muestra tu marca en lugar de AdKavi dentro del panel.",
    "whitelabel.companyNameLabel": "Nombre de la empresa",
    "whitelabel.companyNamePlaceholder": "Agencia Acme",
    "whitelabel.companyNameHelp": "Reemplaza el nombre AdKavi en la barra lateral y los títulos.",
    "whitelabel.logoLabel": "Logo",
    "whitelabel.logoHelp": "PNG, JPG, SVG o WebP. Recomendado 256×256, máx. 2 MB.",
    "whitelabel.uploadLogo": "Subir logo",
    "whitelabel.removeLogo": "Quitar logo",
    "whitelabel.colorLabel": "Color principal de marca",
    "whitelabel.colorHelp": "Se usa para botones, resaltados y estados activos en todo el panel.",
    "whitelabel.previewLabel": "Vista previa",
    "whitelabel.save": "Guardar marca",
    "whitelabel.toast.saved": "Marca actualizada",
    "whitelabel.toast.saveFailed": "No se pudo guardar la marca",
    "whitelabel.toast.uploaded": "Logo subido",
    "whitelabel.toast.uploadFailed": "Error al subir el logo",
    "whitelabel.toast.tooLarge": "El logo debe pesar 2 MB o menos",
    "webhooks.tab": "Webhooks",
    "webhooks.title": "Webhooks",
    "webhooks.description": "Envía datos de eventos en tiempo real a Zapier, Make o cualquier endpoint HTTPS.",
    "webhooks.add.title": "Añadir un webhook",
    "webhooks.add.label": "Etiqueta",
    "webhooks.add.url": "URL del endpoint",
    "webhooks.add.events": "Eventos a enviar",
    "webhooks.add.cta": "Añadir webhook",
    "webhooks.empty": "Aún no hay webhooks. Añade uno arriba para empezar a enviar eventos.",
    "webhooks.loading": "Cargando webhooks…",
    "webhooks.enabled": "Activo",
    "webhooks.disabled": "Inactivo",
    "webhooks.toggleEnabled": "Activar/desactivar webhook",
    "webhooks.test": "Probar",
    "webhooks.delete": "Eliminar",
    "webhooks.confirmDelete": "¿Eliminar este webhook?",
    "webhooks.secret": "Clave de firma",
    "webhooks.toggleSecret": "Mostrar/ocultar clave",
    "webhooks.lastDelivery": "Última entrega",
    "webhooks.event.campaign.launched": "Campaña lanzada",
    "webhooks.event.ad.paused": "Anuncio pausado",
    "webhooks.event.ab.winner_declared": "Ganador A/B declarado",
    "webhooks.event.cost_alert.triggered": "Alerta de coste activada",
    "webhooks.toast.added": "Webhook añadido",
    "webhooks.toast.deleted": "Webhook eliminado",
    "webhooks.toast.badUrl": "La URL debe empezar por http:// o https://",
    "webhooks.toast.noEvents": "Selecciona al menos un evento",
    "webhooks.toast.testOk": "Prueba entregada ({status})",
    "webhooks.toast.testFail": "Prueba fallida: {error}",

    // Navbar labels
    "nav.home": "Inicio",
    "nav.agencies": "Agencias",

    // Footer
    "footer.slogan": "Crea. Lanza. Escala.",

    // FAQ
    "faq.title": "¿Tienes preguntas? Tenemos",
    "faq.titleHighlight": "Respuestas.",
    "faq.q1": "¿Necesito experiencia en publicidad para usar AdKavi?",
    "faq.a1": "Para nada. AdKavi fue diseñado para personas sin experiencia en anuncios. Nuestra IA lo gestiona todo — solo describe tu negocio y tu objetivo, y AdKavi hace el resto.",
    "faq.q2": "¿Funcionará AdKavi para mi sector?",
    "faq.a2": "Sí. AdKavi funciona para cualquier negocio — comercio electrónico, bienes raíces, salud, restaurantes, coaches, agencias, creadores de contenido y más. Si vendes algo, AdKavi puede ayudarte a crecer.",
    "faq.q3": "¿Necesito un gran presupuesto publicitario?",
    "faq.a3": "No. Puedes empezar con tan solo $5 al día. AdKavi optimiza tu presupuesto automáticamente para que cada dólar rinda al máximo.",
    "faq.q4": "¿Están seguros mis datos y mi cuenta de anuncios?",
    "faq.a4": "Al 100%. Nos conectamos mediante OAuth oficial — nunca vemos ni guardamos tus contraseñas. Todos los datos están cifrados y nunca se venden ni se comparten.",
    "faq.q5": "¿Puedo cancelar en cualquier momento?",
    "faq.a5": "Sí. Sin contratos ni compromisos. Cancela cuando quieras desde tu panel con un solo clic. Sin preguntas.",
    "faq.q6": "¿Qué plataformas admite AdKavi?",
    "faq.a6": "Meta (Facebook e Instagram), TikTok, Google Search & Display y YouTube — con más plataformas en camino.",
    "faq.q7": "¿En qué se diferencia AdKavi de contratar una agencia de marketing?",
    "faq.a7": "AdKavi te da todo lo que hace una agencia — estrategia, creatividades, campañas, optimización e informes — a una fracción del coste, con total transparencia y control. Sin retainers, sin esperas, sin adivinar.",
    "faq.q8": "¿Qué pasa si cancelo mi plan de miembro fundador?",
    "faq.a8": "Si cancelas tu suscripción de miembro fundador y regresas más tarde, es posible que el precio de fundador ya no esté disponible. Tendrías que unirte de nuevo al precio regular, actualmente $297/mes. El precio de miembro fundador se mantiene activo mientras tu suscripción permanezca activa.",

    // ValueStack
    "valuestack.title": "Lo que",
    "valuestack.titleHighlight": "AdKavi",
    "valuestack.titleEnd": "reemplaza",
    "valuestack.copywriter": "Redactor publicitario",
    "valuestack.videoEditor": "Editor de video",
    "valuestack.adManager": "Gestor de anuncios",
    "valuestack.designTool": "Herramienta de diseño",
    "valuestack.total": "Total si contratas por separado:",
    "valuestack.starter": "AdKavi Lanzamiento: $149/mes",
    "valuestack.savings": "Eso es un 97% menos — con mejores resultados.",
    "valuestack.cta": "Unirse a la lista",

    // Comparison
    "comparison.title": "Por qué los negocios inteligentes eligen",
    "comparison.bestValue": "MEJOR VALOR",
    "comparison.col.agency": "Agencia de marketing",
    "comparison.col.diy": "Anuncios propios",
    "comparison.col.tools": "Herramientas por separado",
    "comparison.row.cost": "Coste mensual",
    "comparison.row.launch": "Tiempo hasta el primer anuncio",
    "comparison.row.copyAi": "IA escribe tu texto publicitario",
    "comparison.row.videoAi": "Creación de video con IA (sin grabar)",
    "comparison.row.voice": "Clonación de voz",
    "comparison.row.multiplatform": "Multiplataforma desde un panel",
    "comparison.row.optimize": "Optimización automática 24/7",
    "comparison.row.spy": "Espionaje de anuncios de competidores",
    "comparison.row.cancel": "Cancela cuando quieras",
    "comparison.row.creative": "Constructor de creatividades",
    "comparison.row.carousel": "Anuncios carrusel con IA",
    "comparison.row.image": "Anuncios de imagen con IA",
    "comparison.cell.adkavi.cost": "Desde $149/mes",
    "comparison.cell.agency.cost": "$2.000–$10.000+",
    "comparison.cell.diy.cost": "Presupuesto desperdiciado",
    "comparison.cell.tools.cost": "$178–$400+/mes",
    "comparison.cell.tools.cost.note": "(6+ apps combinadas)",
    "comparison.cell.adkavi.launch": "Menos de 10 min",
    "comparison.cell.agency.launch": "2–6 semanas",
    "comparison.cell.diy.launch": "Días de configuración",
    "comparison.cell.tools.launch": "Configuración manual",

    // ForBusinesses
    "forbiz.eyebrow": "Para negocios",
    "forbiz.title": "Hecho para dueños que necesitan",
    "forbiz.titleHighlight": "clientes reales",
    "forbiz.titleEnd": "— rápido.",
    "forbiz.desc": "Del rush del almuerzo al cierre del día — AdKavi escribe, diseña y lanza lo que los trae.",
    "forbiz.biz1.label": "Dueño de restaurante",
    "forbiz.biz1.outcome": "Llena mesas vacías cada fin de semana",
    "forbiz.biz2.label": "Agente inmobiliario",
    "forbiz.biz2.outcome": "Consigue consultas de propiedades en piloto automático",
    "forbiz.biz3.label": "Empresa de reparación de crédito",
    "forbiz.biz3.outcome": "Genera 100+ leads calificados al mes",
    "forbiz.biz4.label": "Tienda boutique",
    "forbiz.biz4.outcome": "Convierte seguidores en compradores",
    "forbiz.biz5.label": "Dueño de gimnasio",
    "forbiz.biz5.outcome": "Capta nuevos miembros cada semana",
    "forbiz.biz6.label": "Concesionario de autos",
    "forbiz.biz6.outcome": "Atrae visitas al showroom con anuncios de video",
    "forbiz.output1": "Videos publicitarios con IA",
    "forbiz.output2": "Carruseles y publicaciones",
    "forbiz.output3": "Campañas publicitarias completas",
    "forbiz.banner": "Todo lanzado a Meta · TikTok · Google — en minutos",

    // SeeInAction
    "seeinaction.title": "Lanza tu primera campaña con IA en",
    "seeinaction.titleHighlight": "menos de 10 minutos.",
    "seeinaction.desc": "Mira cómo negocios reales pasan de cero a lanzar anuncios en Meta, TikTok, Google y YouTube — en una tarde.",
    "seeinaction.playAria": "Reproducir video de demostración",
    "seeinaction.watchDemo": "Mira el demo de AdKavi en 60 segundos",
    "seeinaction.comingSoon": "Demo próximamente",

    // Problem stats
    "problem.stat1.value": "Horas",
    "problem.stat1.label": "PERDIDAS CADA SEMANA",
    "problem.stat1.desc": "Plataformas complejas, cambio constante de herramientas y configuración manual consumen el tiempo que deberías dedicar a tu negocio.",
    "problem.stat2.value": "Miles",
    "problem.stat2.label": "GASTADOS EN AGENCIAS",
    "problem.stat2.desc": "Honorarios altos, entregas lentas, sin transparencia y creatividades genéricas que rara vez generan resultados.",
    "problem.stat3.value": "La mayoría",
    "problem.stat3.label": "DE PRESUPUESTOS SE DESPERDICIAN SIN UN SISTEMA",
    "problem.stat3.desc": "Sin optimización con IA y creatividad fresca, el presupuesto en anuncios se consume silenciosamente sin retorno medible.",

    // Pricing
    "pricing.foundingOffer": "Limitado a los primeros 100 miembros fundadores en total",

    // AccountTypeOnboarding
    "onboarding.welcome": "Bienvenido a AdKavi 👋",
    "onboarding.desc": "Cuéntanos quién eres para mostrarte los planes y herramientas correctos.",
    "onboarding.business.title": "Soy un negocio o creador",
    "onboarding.business.desc": "Gestiona anuncios para una sola marca o cuenta.",
    "onboarding.agency.title": "Soy una agencia",
    "onboarding.agency.desc": "Gestiona múltiples cuentas de clientes en un solo panel.",
    "onboarding.footer": "Puedes cambiar esto en cualquier momento desde Configuración.",
    "onboarding.saving": "Guardando…",

    // Integration connect dialogs
    "connect.meta.title": "Conecta tu cuenta de Meta",
    "connect.meta.demoNote": "Modo demo: el OAuth en vivo está pendiente de revisión por Meta.",
    "connect.meta.login.title": "Iniciar sesión en Facebook",
    "connect.meta.login.desc": "Pantalla de prueba — no se envían credenciales.",
    "connect.meta.email": "Correo o teléfono",
    "connect.meta.password": "Contraseña",
    "connect.meta.emailMismatch.title": "Correos no coinciden",
    "connect.meta.adkavi.email": "Correo de AdKavi",
    "connect.meta.meta.email": "Correo de Meta",
    "connect.meta.choose.title": "Elige una cuenta de anuncios",
    "connect.meta.choose.desc": "Selecciona qué cuenta de anuncios de Meta debe conectar AdKavi.",
    "connect.tiktok.title": "Conecta tu cuenta de TikTok Ads",
    "connect.tiktok.continue": "Continuar con TikTok",
    "connect.google.title": "Conecta tu cuenta de Google Ads",
    "connect.google.continue": "Continuar con Google",
    "connect.redirecting": "Redirigiendo…",
    "connect.disconnect": "Desconectar",
    "connect.disconnecting": "Desconectando…",
    "connect.meta.desc": "Sincroniza el rendimiento de anuncios de Facebook e Instagram.",
    "connect.tiktok.desc": "Sincroniza el rendimiento de anuncios de TikTok Ads Manager.",
    "connect.google.desc": "Sincroniza el rendimiento de Google Ads — incluye YouTube.",

    // AdAccount coming soon
    "adaccount.title": "Conectar {platform}",
    "adaccount.badge": "Próximamente",
    "adaccount.desc": "La conexión de cuenta de anuncios estará disponible pronto. Podrás lanzar anuncios a {platform} directamente desde AdKavi.",
    "adaccount.feature1.title": "Lanzamientos con un clic",
    "adaccount.feature1.desc": "Envía creatividades aprobadas directamente a tu cuenta de anuncios.",
    "adaccount.feature2.title": "Gasto y alcance en tiempo real",
    "adaccount.feature2.desc": "Ve el rendimiento, el gasto y el ROAS en vivo dentro de AdKavi.",
    "adaccount.feature3.title": "Controles de cumplimiento completos",
    "adaccount.feature3.desc": "Restricciones de edad, geo y contenido aplicadas desde la fuente.",
    "adaccount.gotIt": "Entendido",

    // Agency waitlist dialog
    "agency.waitlist.title": "Unirse a la lista de espera",
    "agency.waitlist.desc": "Sé el primero en saber cuando AdKavi esté en vivo.",
    "agency.waitlist.success.title": "¡Estás en la lista!",
    "agency.waitlist.success.desc": "Nos pondremos en contacto cuando AdKavi esté en vivo.",
    "agency.waitlist.close": "Cerrar",
    "agency.waitlist.fullName": "Nombre completo",
    "agency.waitlist.agencyName": "Nombre de la agencia",
    "agency.waitlist.email": "Correo electrónico",
    "agency.waitlist.businessType": "Tipo de negocio",
    "agency.waitlist.placeholder": "Selecciona tu tipo de negocio",
    "agency.waitlist.joining": "Uniéndose…",
    "agency.waitlist.join": "Unirse a la lista",

    // AgencyHero
    "agency.hero.badge": "Diseñado para agencias",
    "agency.hero.title1": "Gestiona los anuncios de tus clientes.",
    "agency.hero.title2": "Escala tu agencia.",
    "agency.hero.desc": "AdKavi le da a las agencias un solo panel para gestionar las campañas de cada cliente en Meta, TikTok, Google y YouTube — con IA haciendo el trabajo pesado.",
    "agency.hero.joinWaitlist": "Unirse a la lista",
    "agency.hero.seePricing": "Ver precios para agencias",

    // AgencyBenefits
    "agency.benefits.title": "Lo que obtienen las agencias",
    "agency.benefits.desc": "Todo lo que necesitas para gestionar clientes, lanzar campañas y demostrar resultados.",
    "agency.benefits.c1.title": "Gestión de clientes",
    "agency.benefits.c1.desc": "Cambia entre cuentas de clientes al instante. Cada cliente solo ve sus propios datos.",
    "agency.benefits.c2.title": "Panel unificado",
    "agency.benefits.c2.desc": "Todas las campañas, el gasto y el ROAS de los clientes en una sola vista. Sin cambiar pestañas.",
    "agency.benefits.c3.title": "La IA hace el trabajo",
    "agency.benefits.c3.desc": "AdKavi escribe el copy, crea las creatividades, selecciona la audiencia y optimiza automáticamente.",
    "agency.benefits.c4.title": "Informes para clientes",
    "agency.benefits.c4.desc": "Comparte un panel de rendimiento en vivo directamente con cada cliente mostrando su gasto, ROAS, clics y leads en tiempo real.",

    // AgencyFeatures
    "agency.features.title": "Todo lo que puedes hacer para cada cliente",
    "agency.features.desc": "Una cuenta de AdKavi. Todas las herramientas que tus clientes necesitan para crecer.",
    "agency.features.f1.title": "Anuncios multiplataforma",
    "agency.features.f1.desc": "Lanza campañas en Meta, TikTok, Google Search & Display y YouTube para cualquier cliente con un clic.",
    "agency.features.f2.title": "Videos con avatar IA",
    "agency.features.f2.desc": "Genera anuncios de video profesionales usando el avatar IA de tu cliente — sin necesidad de grabar.",
    "agency.features.f3.title": "Clonación de voz",
    "agency.features.f3.desc": "Clona la voz de cualquier cliente en 30 segundos. Cada locución suena como ellos automáticamente.",
    "agency.features.f4.title": "Programación de contenido",
    "agency.features.f4.desc": "Planifica y publica automáticamente en Meta, TikTok y YouTube para cada cliente.",
    "agency.features.f5.title": "Investigación de competidores",
    "agency.features.f5.desc": "Muestra a los clientes exactamente lo que están publicando sus competidores y cómo superarlos.",
    "agency.features.f6.title": "Segmentación de audiencia",
    "agency.features.f6.desc": "Audiencias personalizadas, de retargeting y similares creadas automáticamente para cada cliente.",
    "agency.features.f7.title": "Analítica de clientes",
    "agency.features.f7.desc": "Comparte un panel en vivo con cada cliente mostrando gasto, ROAS, clics y leads.",
    "agency.features.f8.title": "Optimizador de presupuesto IA",
    "agency.features.f8.desc": "La IA redirige automáticamente el presupuesto de cada cliente hacia sus anuncios con mejor rendimiento.",
    "agency.features.f9.title": "Protección de pausa automática",
    "agency.features.f9.desc": "Detiene el desperdicio del dinero del cliente si un anuncio tiene bajo rendimiento durante 48 horas.",
    "agency.features.f10.title": "Redactor de anuncios IA",
    "agency.features.f10.desc": "La IA escribe titulares, descripciones y CTAs para cada campaña de cliente al instante.",
    "agency.features.f11.title": "Agente IA de leads por cliente",
    "agency.features.f11.desc": "Cada cliente tiene su propio agente IA que aprende sobre su negocio y responde leads en WhatsApp, Instagram DMs y Facebook Messenger automáticamente.",
    "agency.features.f12.title": "Aislamiento de clientes",
    "agency.features.f12.desc": "Cada cliente solo ve sus propios datos. Cero contaminación cruzada entre cuentas.",

    // AgencyHowItWorks
    "agency.how.title": "Cómo funciona para agencias",
    "agency.how.desc": "Del onboarding al informe en tres pasos.",
    "agency.how.step": "PASO",
    "agency.how.s1.title": "Añade tus clientes",
    "agency.how.s1.desc": "Crea un perfil de cliente, conecta sus cuentas de anuncios.",
    "agency.how.s2.title": "Lanza campañas",
    "agency.how.s2.desc": "La IA crea y lanza en todas las plataformas simultáneamente.",
    "agency.how.s3.title": "Reporta resultados",
    "agency.how.s3.desc": "Comparte paneles de rendimiento en vivo directamente con los clientes.",

    // AgencyCtaBanner
    "agency.cta.title": "Deja de hacer malabares con herramientas. Empieza a escalar.",
    "agency.cta.desc": "Únete a las agencias que ya están en la lista de espera.",
    "agency.cta.button": "Unirse a la lista",

    // Video Library
    "video.library.title": "Mi biblioteca de videos",
    "video.library.empty": "Aún no hay videos",
    "video.library.deleteTitle": "¿Eliminar este video?",
    "video.library.cancel": "Cancelar",
    "video.library.campaign": "Campaña: ",

    // Credits / Avatar / Voice
    "credits.dialog.title": "Añadir créditos de video",
    "credits.total": "Total",
    "avatar.dialog.title": "Crear tu avatar con IA",
    "avatar.uploading": "Subiendo…",
    "avatar.choose": "Elegir archivo de video",
    "voice.dialog.title": "Clonar tu voz",
    "voice.uploading": "Subiendo…",
    "voice.choose": "Elegir archivo de audio",

    // Dashboard Preview
    "dashboard.title": "Ve cada resultado",
    "dashboard.titleHighlight": "en tiempo real",
    "dashboard.sampleNote": "Datos de muestra con fines ilustrativos",
    "dashboard.desc": "AdKavi rastrea cada métrica importante para que siempre sepas cómo se desempeñan tus anuncios y contenido.",
    "dashboard.cardTitle": "Panel de campañas",
    "dashboard.cardPeriod": "Últimos 30 días",
    "dashboard.cardLive": "Datos en vivo",
    "dashboard.metric.spend": "Gasto total en anuncios",
    "dashboard.metric.reach": "Alcance",
    "dashboard.metric.clicks": "Clics",
    "dashboard.metric.leads": "Leads generados",
    "dashboard.metric.sales": "Conversiones de ventas",
    "dashboard.metric.cpl": "Costo por lead",
    "dashboard.metric.roas": "ROAS",
    "dashboard.metric.topCampaign": "Campaña principal",
    "dashboard.metric.topCampaignValue": "Promo Primavera",
    "dashboard.footer": "Conecta tu cuenta de Facebook y ve tus datos en vivo al instante. Sin hojas de cálculo. Sin adivinar. Solo resultados.",
    "dashboard.stat1": "Creación de anuncios más rápida",
    "dashboard.stat2": "Una fracción del costo de una agencia",
    "dashboard.stat3": "Mejor ROI en anuncios",
    "dashboard.stat4Suffix": "IA trabajando en segundo plano",

    // AI Agent Section
    "aiagent.badge": "Nueva función",
    "aiagent.title": "Nunca pierdas un lead.",
    "aiagent.subtitle": "Tu agente IA responde por ti.",
    "aiagent.desc": "AdKavi estudia tu Instagram, Facebook y sitio web — luego responde automáticamente cada mensaje de WhatsApp, DM de Instagram y mensaje de Facebook usando tu tono, tus precios y tus ofertas.",
    "aiagent.c1.title": "Cerebro de negocio",
    "aiagent.c1.desc": "La IA lee tus perfiles sociales y sitio web para aprender todo sobre tu negocio antes de responder.",
    "aiagent.c2.title": "Respuestas multicanal",
    "aiagent.c2.desc": "Responde en WhatsApp, DMs de Instagram y Facebook Messenger desde un solo lugar. Los canales disponibles varían según el plan.",
    "aiagent.c3.title": "Disponibilidad 24/7",
    "aiagent.c3.desc": "Cada lead recibe respuesta instantánea aunque estés durmiendo, en fin de semana o en festivos.",
    "aiagent.cta": "Unirse a la lista",

    // ROI Savings
    "roi.title": "Descubre exactamente cuánto estás",
    "roi.titleHighlight": "desperdiciando ahora mismo",
    "roi.desc": "Ingresa tus números actuales y descubre cuánto te ahorra AdKavi.",
    "roi.feeLabel": "Tarifa mensual de agencia o freelancer",
    "roi.spendLabel": "Gasto mensual en anuncios",
    "roi.spending": "Estás gastando {amount}/mes en comisiones de gestión",
    "roi.withAdkavi": "Con AdKavi Lanzamiento ($149/mes) ahorras",
    "roi.perMonth": "por mes",
    "roi.perYear": "por año",
    "roi.pocketPre": "Eso es",
    "roi.pocketSuf": "de vuelta en tu bolsillo",
    "roi.cta": "Empieza a ahorrar hoy — Prueba gratuita 14 días",

    // Features Lead Agent card
    "features.leadagent.title": "Agente IA de leads",
    "features.leadagent.desc": "Conecta tu WhatsApp, DMs de Instagram y Facebook Messenger. La IA de AdKavi estudia tu negocio y responde a cada lead automáticamente — 24/7.",

    // Common
    "common.cancel": "Cancelar",
    "common.saving": "Guardando…",
    "common.loading": "Cargando…",
    "app.signOut": "Cerrar sesión",
    "common.uploading": "Subiendo…",

    // Feature Showcase
    "showcase.eyebrow": "Vélo en acción",
    "showcase.title": "Cada herramienta. Una plataforma.",
    "showcase.desc": "Elige cualquier función y mira cómo trabaja AdKavi — desde crear anuncios hasta responder leads y reportes en vivo.",
    // AI Showcase section
    "aiShowcase.eyebrow": "Pruébalo en vivo",
    "aiShowcase.title.1": "Mira cómo la IA",
    "aiShowcase.title.2": "crea tus anuncios.",
    "aiShowcase.subtitle": "Escribe tu negocio y Claude redacta un anuncio real en segundos — en inglés y español al mismo tiempo.",
    "aiShowcase.tab.claude": "Redactor en vivo",
    "aiShowcase.tab.images": "Imágenes IA",
    "aiShowcase.tab.video": "Video con avatar",
    "aiShowcase.tab.voice": "Voz en off",
    "aiShowcase.tab.bilingual": "EN vs ES",
    "aiShowcase.claude.label": "Describe tu negocio",
    "aiShowcase.claude.placeholder": "Ej. Reparación de crédito para familias hispanas en Miami",
    "aiShowcase.claude.goal": "Objetivo de campaña",
    "aiShowcase.claude.goal.leads": "Captación de leads",
    "aiShowcase.claude.goal.sales": "Ventas",
    "aiShowcase.claude.goal.awareness": "Reconocimiento de marca",
    "aiShowcase.claude.goal.traffic": "Tráfico al sitio web",
    "aiShowcase.claude.generate": "Generar mi anuncio",
    "aiShowcase.claude.generating": "Claude está redactando tu anuncio…",
    "aiShowcase.claude.tryAnother": "Probar otro negocio",
    "aiShowcase.claude.hint": "IA real de Claude · Sin registro · Tus datos no se guardan",
    "aiShowcase.claude.error": "Error al generar — inténtalo de nuevo.",
    "aiShowcase.images.provider": "Recraft",
    "aiShowcase.images.title": "Creativos publicitarios con IA",
    "aiShowcase.images.subtitle": "Tu anuncio. Tu marca. Generado en segundos.",
    "aiShowcase.images.placeholder": "Imágenes de muestra",
    "aiShowcase.images.runScript": "Ejecuta scripts/generate-samples.ts para llenar",
    "aiShowcase.images.bullet.1": "Sube tu foto o genera desde un texto",
    "aiShowcase.images.bullet.2": "Adapta los colores de tu marca automáticamente",
    "aiShowcase.images.bullet.3": "Carrusel, imagen única o miniatura de video",
    "aiShowcase.images.bullet.4": "Eliminación de fondo incluida",
    "aiShowcase.images.bullet.5": "Anima tu imagen estática a video con Luma AI",
    "aiShowcase.images.bullet.6": "Lista para publicar — sin herramientas de diseño",
    "aiShowcase.video.provider": "HeyGen",
    "aiShowcase.video.title": "Anuncio con avatar IA",
    "aiShowcase.video.subtitle": "Tu cara. Tu voz. Sin grabar nada.",
    "aiShowcase.video.placeholder": "Video de muestra",
    "aiShowcase.video.runScript": "Invoca la edge function generate-samples para llenar",
    "aiShowcase.video.processing": "El video con avatar está renderizando…",
    "aiShowcase.video.processingNote": "HeyGen tarda entre 3 y 8 minutos. Vuelve pronto.",
    "aiShowcase.video.checkStatus": "Ver estado",
    "aiShowcase.video.bullet.1": "Graba una vez, clona tu voz y cara para siempre",
    "aiShowcase.video.bullet.2": "Escribe tu guión — tu avatar lo entrega en cámara",
    "aiShowcase.video.bullet.3": "Lip sync en español: mismo avatar, audio en español",
    "aiShowcase.video.bullet.4": "30 videos con avatar/mes en planes Dominio",
    "aiShowcase.video.bullet.5": "Va directo a tu campaña — sin edición",
    "aiShowcase.voice.provider": "ElevenLabs",
    "aiShowcase.voice.title": "Voz en off con IA",
    "aiShowcase.voice.subtitle": "Tu voz. En cada video. Automáticamente.",
    "aiShowcase.voice.script": "Guión",
    "aiShowcase.voice.placeholder": "Audio de muestra",
    "aiShowcase.voice.runScript": "Ejecuta scripts/generate-samples.ts para llenar",
    "aiShowcase.voice.bullet.1": "30 segundos de grabación — la IA clona tu voz",
    "aiShowcase.voice.bullet.2": "Suena como tú, no como un robot",
    "aiShowcase.voice.bullet.3": "Traduce y dobla al español automáticamente",
    "aiShowcase.voice.bullet.4": "Funciona en videos con avatar, anuncios y contenido",
    "aiShowcase.bilingual.en": "Inglés",
    "aiShowcase.bilingual.es": "Español",
    "aiShowcase.bilingual.title": "Un brief. Dos idiomas.",
    "aiShowcase.bilingual.subtitle": "Un negocio. Dos mercados. Cero trabajo extra.",
    "aiShowcase.bilingual.note": "Muestra generada en vivo arriba — un clic, dos mercados.",
    "aiShowcase.bilingual.bullet.1": "Cada anuncio en inglés Y español al mismo tiempo",
    "aiShowcase.bilingual.bullet.2": "Videos con avatar doblados y lip-synced en español",
    "aiShowcase.bilingual.bullet.3": "Respuestas automáticas de DM en el idioma del cliente",
    "aiShowcase.bilingual.bullet.4": "Llega a 41 millones de hispanohablantes en EE. UU.",
    "aiShowcase.cta": "Únete a la lista — Pruébalo Gratis",
    "aiShowcase.animate.button": "Animar mi anuncio",
    "aiShowcase.animate.animating": "Luma AI está animando tu anuncio… ~30 segundos",
    "aiShowcase.animate.tooltip": "Convierte esta imagen en un video de 5 segundos con Luma AI",
    "inbox.title": "Bandeja de conversaciones",
    "inbox.subtitle": "Mensajes gestionados por IA en WhatsApp, Instagram DMs y Facebook Messenger.",
    "inbox.refresh": "Actualizar",
    "inbox.stat.total": "Total",
    "inbox.stat.replied": "Respondido",
    "inbox.stat.pending": "Pendiente",
    "inbox.stat.failed": "Fallido",
    "inbox.filter.allChannels": "Todos los canales",
    "inbox.filter.allStatuses": "Todos los estados",
    "inbox.filter.channel": "Canal",
    "inbox.filter.status": "Estado",
    "inbox.filter.clear": "Borrar",
    "inbox.search.placeholder": "Buscar contacto, mensaje…",
    "inbox.col.channel": "Canal",
    "inbox.col.contact": "Contacto",
    "inbox.col.message": "Mensaje",
    "inbox.col.status": "Estado",
    "inbox.loading": "Cargando conversaciones…",
    "inbox.empty.title": "Aún no hay conversaciones",
    "inbox.empty.desc": "Una vez que tu agente IA esté activo y los webhooks de Meta estén conectados, las conversaciones aparecerán aquí en tiempo real.",
    "inbox.empty.setup": "Configurar agente IA →",
    "inbox.empty.noMatch": "Ninguna conversación coincide con tus filtros.",
    "inbox.showing": "Mostrando {shown} de {total} conversaciones",
    "inbox.expanded.lead": "Mensaje del lead",
    "inbox.expanded.aiReply": "Respuesta IA",
    "inbox.status.replied": "respondido",
    "inbox.status.pending": "pendiente",
    "inbox.status.failed": "fallido",
    "bench.badge": "Referencias de rendimiento",
    "bench.title": "Referencias del sector",
    "bench.desc": "Compara tus campañas con los promedios del sector para CTR, CPC, CPL y ROAS. Los números combinan Meta, TikTok y Google.",
    "bench.higher": "Más alto es mejor",
    "bench.lower": "Más bajo es mejor",
    "bench.col.industry": "Sector",
    "bench.col.ctr": "CTR (prom. / bueno)",
    "bench.col.cpc": "CPC (prom. / excelente)",
    "bench.col.cpl": "CPL (rango bueno)",
    "bench.col.roas": "ROAS (prom. / bueno)",
    "bench.sources": "Fuentes: Wordstream 2024, AdEspresso, LocaliQ. Las referencias son orientativas — tu oferta específica, la calidad creativa y la superposición de audiencia importan más que cualquier número.",
    "billing.title.switch": "Cambiar plan",
    "billing.title.choose": "Elige tu plan",
    "billing.desc.switch": "Estás en {plan}. Elige un nuevo plan para cambiar.",
    "billing.desc.trial": "Todos los planes incluyen 14 días de prueba gratis. Cancela cuando quieras.",
    "billing.audience.creator": "Para Empresas y Creadores",
    "billing.audience.agency": "Para Agencias",
    "billing.badge.popular": "Más popular",
    "billing.badge.current": "Plan actual",
    "billing.card.trial": "14 días de prueba gratis",
    "billing.card.perMonth": "/mes",
    "billing.card.current": "Estás en este plan",
    "billing.card.choose": "Elegir {name}",
    "billing.dialog.title": "Iniciar tu prueba de {name}",
    "billing.dialog.desc": "Sin cargo por 14 días. Cancela desde Configuración → Facturación.",
    // Checkout pages
    "checkout.loading": "Cargando pago…",
    "checkout.changePlan": "Cambiar plan",
    "checkout.trialHeading": "Comienza tu prueba gratis de 14 días",
    "checkout.trialDesc": "No se te cobrará durante 14 días. Cancela en cualquier momento desde Configuración → Facturación.",
    "checkout.youreStarting": "Estás empezando",
    "checkout.perMonthAfterTrial": "/mes después de la prueba",
    "checkout.return.title": "Tu prueba gratis está activa",
    "checkout.return.desc": "No se te cobrará durante 14 días. Gestiona tu plan desde Configuración → Facturación.",
    "checkout.return.cta": "Ir al panel",
    "checkout.return.reference": "Referencia:",
    "dash.agent.setupTitle": "Configura tu agente IA",
    "dash.agent.setupDesc": "Tu agente IA puede responder automáticamente en WhatsApp, Instagram DMs y Facebook Messenger 24/7 — incluso mientras duermes.",
    "dash.agent.setupCta": "Configurar agente IA",
    "dash.agent.name": "Agente IA",
    "dash.agent.active": "Activo",
    "dash.agent.monitoring": "Monitoreando WhatsApp, Instagram DMs y Messenger",
    "dash.agent.viewConvos": "Ver conversaciones",
    "dash.agent.convosToday": "Conversaciones hoy",
    "dash.agent.lastReply": "Última respuesta",
    "dash.agent.justNow": "ahora mismo",
    "dash.agent.minsAgo": "hace {n}m",
    "dash.agent.hoursAgo": "hace {n}h",
    "dash.agent.daysAgo": "hace {n}d",
    "creative.guide.template": "Elige una plantilla",
    "creative.guide.content": "Añade marca y mensaje",
    "creative.guide.colors": "Elige colores",
    "creative.guide.image": "Añade una imagen",
    "creative.guide.save": "Guardar o exportar",
    "creative.cat.all": "Todo",
    "creative.cat.ads": "Anuncios",
    "creative.cat.photo": "Foto",
    "creative.cat.minimal": "Minimalista",
    "creative.cat.promo": "Promo",
    "creative.cat.social": "Social",
    "creative.zoom.fit": "Ajustar",
    "creative.zoom.fitScreen": "Ajustar a pantalla",
    "creative.zoom.pct": "{pct}% zoom",
    "creative.palette.sunset": "Puesta de sol",
    "creative.palette.midnight": "Medianoche",
    "creative.palette.cream": "Crema",
    "creative.palette.indigo": "Índigo",
    "creative.palette.forest": "Bosque",
    "creative.palette.peach": "Durazno",
    "creative.defaultName": "Creativo sin título",
    "creative.section.content": "Contenido",
    "creative.section.colors": "Colores",
    "creative.section.image": "Imagen",
    "creative.section.format": "Formato",
    "creative.section.export": "Exportar",
    "creative.btn.save": "Guardar creativo",
    "creative.btn.update": "Actualizar creativo",
    "creative.btn.new": "Nuevo",
    "creative.btn.saveShort": "Guardar",
    "creative.btn.exportPng": "Exportar PNG",
    "creative.btn.exportPngFmt": "Exportar PNG ({w}×{h})",
    "creative.btn.chooseBrandLib": "Elegir de la Biblioteca de Marca",
    "creative.section.templates": "Plantillas",
    "creative.section.recent": "Recientes",
    "creative.section.guide": "Guía de configuración",
    "creative.tpl.empty": "Sin plantillas en esta categoría.",
    "creative.field.name": "Nombre del creativo",
    "creative.field.brand": "Marca",
    "creative.field.headline": "Titular",
    "creative.field.subheadline": "Subtitular",
    "creative.field.cta": "Botón CTA",
    "creative.field.bg": "Fondo",
    "creative.field.text": "Texto",
    "creative.field.accent": "Acento",
    "creative.img.empty": "Sin imagen aún.",
    "creative.img.emptyHint": "Usada por Superposición de Foto e Impacto Dividido.",
    "creative.img.pasteUrl": "O pega la URL de imagen…",
    "creative.img.corsHint": "Para exportar PNG, usa una URL compatible con CORS (Unsplash, tu Biblioteca de Marca o Supabase Storage).",
    "creative.savedAt": "Guardado {time}",
    "creative.lastSavedAt": "Guardado a las {time}",
    "creative.loadedFromSaved": "Cargado desde guardado",
    "creative.fmt.square": "Cuadrado 1:1",
    "creative.fmt.story": "Historia 9:16",
    "creative.fmt.landscape": "Horizontal 1.91:1",
    "creative.tpl.splitBold.name": "Impacto Dividido",
    "creative.tpl.splitBold.desc": "Tipografía grande en bloque de color, imagen a un lado.",
    "creative.tpl.overlayHero.name": "Superposición de Foto",
    "creative.tpl.overlayHero.desc": "Foto de fondo completo con degradado y CTA.",
    "creative.tpl.minimalMono.name": "Minimalista Mono",
    "creative.tpl.minimalMono.desc": "Tipografía editorial sobre fondo limpio.",
    "creative.tpl.promoBanner.name": "Banner Promocional",
    "creative.tpl.promoBanner.desc": "Titular estilo descuento con cinta de acento brillante.",
    "creative.tpl.quoteCard.name": "Cita / Testimonio",
    "creative.tpl.quoteCard.desc": "Cita grande, atribución debajo. Ideal para prueba social.",
    "brand.lib.title": "Mi biblioteca de marca",
    "brand.lib.desc": "Sube una vez, reutiliza en todas las herramientas",
    "brand.lib.upload": "Subir",
    "brand.lib.catAll": "Todo ({n})",
    "brand.lib.storageFull": "Almacenamiento lleno — borra imágenes para subir más.",
    "brand.lib.storageUsed": "{used} de {total} utilizados",
    "brand.lib.emptyAll": "Aún no hay imágenes de marca. Haz clic para subir tu primera.",
    "brand.lib.emptyCat": "Aún no hay {cat}.",
    "brand.lib.err.imagesOnly": "Solo imágenes",
    "brand.lib.err.maxSize": "Máximo 10 MB por imagen",
    "brand.lib.success.added": "Imagen añadida a la biblioteca de marca",
    "brand.lib.cat.photo": "Fotos",
    "brand.lib.cat.logo": "Logos",
    "brand.lib.cat.product": "Productos",
    "brand.lib.cat.background": "Fondos",
    "brand.lib.cat.graphic": "Gráficos",
    "studio.tab.start": "Empezar aquí",
    "studio.tab.copy": "Copias de anuncio",
    "studio.tab.images": "Imágenes",
    "studio.tab.videos": "Videos",
    "studio.tab.voice": "Voz + Español",
    "studio.tab.library": "Mi biblioteca",
    "studio.tone.confident": "Seguro",
    "studio.tone.playful": "Juguetón",
    "studio.tone.luxury": "Lujo",
    "studio.tone.friendly": "Amigable",
    "studio.tone.urgent": "Urgente",
    "studio.tone.professional": "Profesional",
    "studio.tone.bold": "Audaz",
    "studio.goal.awareness": "Reconocimiento de marca",
    "studio.goal.traffic": "Tráfico",
    "studio.goal.leads": "Leads",
    "studio.goal.sales": "Ventas",
    "studio.goal.appInstalls": "Instalaciones de app",
    "studio.subtitle": "Crea anuncios, captions, imágenes, videos y locuciones desde un solo lugar.",
    "studio.creativeBuilderLink": "Creative Builder",
    "studio.guided.campaign.title": "Campaña publicitaria completa",
    "studio.guided.campaign.desc": "Textos de anuncio, ideas de imagen, sugerencias de video y CTA para una oferta completa",
    "studio.guided.post.title": "Publicación en redes sociales",
    "studio.guided.post.desc": "Caption, hashtags y texto para cualquier plataforma",
    "studio.guided.video.title": "Anuncio en video",
    "studio.guided.video.desc": "Script, gancho, CTA, texto de locución y prompt de video",
    "studio.guided.spanish.title": "Contenido en español / bilingüe",
    "studio.guided.spanish.desc": "Versiones en inglés y español, doblaje y opciones de lip-sync",
    "studio.guided.tool.title": "Usar una herramienta específica",
    "studio.guided.tool.desc": "Explora todas las herramientas de IA y elige exactamente lo que necesitas",
    "studio.adWriter.title": "Redactor de Anuncios con IA",
    "studio.adWriter.productLabel": "¿Qué estás publicitando?",
    "studio.adWriter.audienceLabel": "Audiencia objetivo (opcional)",
    "studio.adWriter.offerLabel": "Oferta / gancho (opcional)",
    "studio.adWriter.platformLabel": "Plataforma",
    "studio.adWriter.goalLabel": "Objetivo",
    "studio.adWriter.toneLabel": "Tono",
    "studio.adWriter.variationsLabel": "Variaciones",
    "studio.adWriter.generating": "Generando…",
    "studio.adWriter.generateBtn": "Generar copia del anuncio",
    "studio.adWriter.poweredBy": "Desarrollado por Claude. Los resultados son generados por IA — revisa antes de publicar.",
    "studio.adWriter.variationsCount": "{n} variaciones generadas",
    "studio.adWriter.openInCreative": "Abrir en Creative Builder",
    "studio.images.subtitle": "Genera imágenes de anuncios, carruseles y visuales animados para tus campañas.",
    "studio.videos.lockedTitle": "Generación de Video con IA",
    "studio.videos.lockedDesc": "Crea videos con voz, videos de estilo de vida y producción cinematográfica con IA. Disponible en Dominio y superior.",
    "studio.voice.subtitle": "Genera locuciones, clona tu voz y traduce o dobla contenido al español.",
    "studio.voice.voGeneratorTitle": "Generador de Locución",
    "studio.voice.voiceCloneTitle": "Clon de Voz",
    "studio.voice.avatarSetupTitle": "Configuración de Avatar IA",
    "studio.voice.dubTitle": "Traducir + Doblar al español",
    "studio.library.creditsTitle": "Créditos de Video Restantes",
    "studio.library.buyCredits": "Comprar Créditos",
    "studio.library.emptyTitle": "Tus videos guardados aparecerán aquí",
    "studio.library.emptyDesc": "Genera tu primer video para verlo en tu biblioteca.",
    "studio.tool.adWriter.name": "Redactor de Anuncios",
    "studio.tool.adWriter.desc": "Genera titulares, texto principal y CTAs",
    "studio.tool.caption.name": "Escritor de Captions",
    "studio.tool.caption.desc": "Captions para redes con hashtags",
    "studio.tool.scriptWriter.name": "Escritor de Scripts de Video",
    "studio.tool.scriptWriter.desc": "Scripts con gancho → cuerpo → CTA",
    "studio.tool.imageAds.name": "Anuncios de Imagen con IA",
    "studio.tool.imageAds.desc": "Genera imágenes de anuncio desde un prompt",
    "studio.tool.carousel.name": "Anuncios de Carrusel",
    "studio.tool.carousel.desc": "Copia y visuales para carruseles de varios slides",
    "studio.tool.talkingVideo.name": "Video con Voz",
    "studio.tool.talkingVideo.desc": "Video del vocero de tu marca usando tu script — sin cámara.",
    "studio.tool.cinematicPro.name": "Pro Cinematográfico",
    "studio.tool.cinematicPro.desc": "Historia con IA → 4 clips Kling → locución → video profesional.",
    "studio.tool.lifestyleVideo.name": "Video de Estilo de Vida",
    "studio.tool.lifestyleVideo.desc": "Video cinematográfico de estilo de vida con IA desde un prompt de texto con Luma.",
    "studio.tool.voiceover.name": "Generador de Locución",
    "studio.tool.voiceover.desc": "Narración natural en segundos",
    "studio.tool.dub.name": "Traducir + Doblar",
    "studio.tool.dub.desc": "Dobla contenido en inglés al español",
    "studio.start.heroTitle": "Comienza con una campaña guiada o elige una herramienta de IA específica.",
    "studio.start.heroSubtitle": "AdKavi te guía paso a paso — desde tu oferta hasta textos de anuncio, imágenes y videos terminados.",
    "studio.start.startGuided": "Iniciar Campaña Guiada",
    "studio.start.browseTools": "Ver todas las herramientas",
    "studio.start.quickAccess": "Acceso Rápido",
    "studio.start.guideTitle": "¿No sabes por dónde empezar?",
    "studio.start.guide.fullCampaign.label": "Campaña Completa",
    "studio.start.guide.fullCampaign.desc": "AdKavi crea todo para una oferta: textos, CTAs y sugerencias.",
    "studio.start.guide.adCopy.label": "Copia de Anuncio",
    "studio.start.guide.adCopy.desc": "¿Solo necesitas texto? Genera titulares, captions o scripts.",
    "studio.start.guide.images.label": "Imágenes",
    "studio.start.guide.images.desc": "¿Necesitas gráficos o anuncios de carrusel? Genera visuales de marca.",
    "studio.start.guide.videos.label": "Videos",
    "studio.start.guide.videos.desc": "Crea Reels, TikToks, Shorts o anuncios con avatar.",
    "studio.start.guide.voice.label": "Voz + Español",
    "studio.start.guide.voice.desc": "Locuciones, doblajes, traducciones o contenido bilingüe.",
    "studio.start.startSetup": "Iniciar Configuración Guiada",
    "studio.guided.header": "Configuración de Campaña Guiada",
    "studio.guided.step1Title": "Paso 1 — ¿Qué quieres crear?",
    "studio.guided.step2Title": "Paso 2 — Cuéntanos sobre tu oferta",
    "studio.guided.step3Title": "Paso 3 — ¡Tu contenido está listo!",
    "studio.guided.step1Desc": "Elige el tipo de contenido que quieres crear. AdKavi te guiará por las herramientas correctas.",
    "studio.guided.step2.campaign": "Generaremos textos de anuncio, opciones de CTA y sugerencias para lo próximo.",
    "studio.guided.step2.post": "Escribiremos captions y hashtags adaptados a tu audiencia.",
    "studio.guided.step2.video": "Te llevaremos a las herramientas de video con tus detalles precargados.",
    "studio.guided.step2.spanish": "Te llevaremos a las herramientas de Voz + Español para doblar o traducir tu contenido.",
    "studio.guided.audienceLabel": "¿Quién es tu audiencia objetivo? (opcional)",
    "studio.guided.offerLabel": "¿Cuál es tu oferta o gancho? (opcional)",
    "studio.guided.continue": "Continuar",
    "studio.guided.back": "Atrás",
    "studio.guided.step3NextTitle": "¡Tu copia está lista! ¿Qué sigue?",
    "studio.guided.step3GenVisuals": "🖼️ Generar visuales",
    "studio.guided.step3CreateVideo": "🎬 Crear un video",
    "studio.guided.step3Design": "Diseñar en Creative Builder",
    "studio.guided.step3ContinueBtn": "Continuar a la pestaña de Copias",
    "studio.guided.step3ExitBtn": "Salir del modo guiado",
    "studio.var.copy": "Copiar",
    "studio.var.copied": "Copiado",
    "studio.var.saveToCalendar": "Guardar en calendario",
    "studio.empty.title": "Genera tu primer anuncio",
    "studio.empty.desc": "Completa el formulario de la izquierda, elige plataforma y tono, y escribiremos múltiples variaciones de anuncio de alta conversión para tu audiencia.",
    "studio.empty.alreadyHave": "¿Ya tienes texto?",
    "studio.empty.creativeBuilderLink": "Ir al Creative Builder",
    "studio.avatar.hint15": "Apunta a ~40 palabras en tu script para un video de 15 segundos.",
    "studio.avatar.hint30": "Apunta a ~75 palabras en tu script para un video de 30 segundos.",
    "studio.avatar.hint60": "Apunta a ~150 palabras en tu script para un video de 60 segundos.",
    "studio.avatar.hint90": "Apunta a ~225 palabras en tu script para un video de 90 segundos.",
    "studio.avatar.regenConfirm": "Ya usaste tu regeneración gratuita para este video. Esto consumirá un crédito. ¿Continuar?",
    "studio.avatar.regenCreditUsed": "Regenerando — se usará 1 crédito",
    "studio.avatar.regenFree": "Regenerando — regeneración gratuita",
    "studio.avatar.rendering": "Renderizando video con avatar — puede tardar 1–3 minutos",
    "studio.avatar.title": "Video con Voz",
    "studio.avatar.desc": "Video del vocero de tu marca — sin cámara.",
    "studio.avatar.brandImageCaption": "Imagen de marca",
    "studio.avatar.addBrandImage": "Agregar imagen de marca",
    "studio.avatar.submitting": "Enviando…",
    "studio.avatar.renderingBtn": "Renderizando…",
    "studio.avatar.generateBtn": "Generar video",
    "studio.avatar.poweredBy": "Desarrollado por HeyGen",
    "studio.regen.credit": "Regenerar (1 crédito)",
    "studio.regen.free": "Regenerar (gratis)",
    "studio.common.download": "Descargar",
    "studio.vo.title": "Generador de Locución",
    "studio.vo.desc": "Narración natural en segundos. Ideal para reels, anuncios y explicaciones.",
    "studio.vo.generateBtn": "Generar locución",
    "studio.vo.downloadMp3": "Descargar MP3",
    "studio.vo.poweredBy": "Desarrollado por ElevenLabs",
    "studio.luma.regenConfirm": "Ya usaste tu regeneración gratuita para este video. Esto consumirá un crédito. ¿Continuar?",
    "studio.luma.toastMock": "Video de muestra listo (demo)",
    "studio.luma.toastRegenCredit": "Regenerado — 1 crédito usado",
    "studio.luma.toastRegenFree": "Regenerado (gratis)",
    "studio.luma.toastReady": "Video de estilo de vida listo",
    "studio.luma.title": "Video de Estilo de Vida",
    "studio.luma.desc": "Genera videos cinematográficos de estilo de vida con IA desde un prompt de texto con Luma Dream Machine.",
    "studio.luma.aspectRatio": "Relación de aspecto",
    "studio.luma.startingFrame": "Fotograma inicial",
    "studio.luma.addStartingImage": "Agregar imagen inicial",
    "studio.luma.renderingBtn": "Renderizando…",
    "studio.luma.generateBtn": "Generar video",
    "studio.luma.samplePreview": "Vista previa de muestra",
    "studio.luma.poweredBy": "Desarrollado por Luma AI",
    "studio.script.title": "Escritor de Scripts de Video",
    "studio.script.desc": "Gancho → cuerpo → CTA, con texto en pantalla e ideas de b-roll.",
    "studio.script.sec15": "15 seg",
    "studio.script.sec30": "30 seg",
    "studio.script.sec60": "60 seg",
    "studio.script.writing": "Escribiendo…",
    "studio.script.generateBtn": "Generar script",
    "studio.script.hook": "Gancho",
    "studio.script.body": "Cuerpo",
    "studio.script.cta": "CTA",
    "studio.script.onScreenText": "Texto en pantalla",
    "studio.script.broll": "Ideas de b-roll",
    "studio.script.copyVO": "Copiar locución",
    "studio.script.poweredBy": "Desarrollado por Claude",
    "studio.dub.submitting": "Enviando…",
    "studio.dub.title": "Traducir + Doblar al español",
    "studio.dub.desc": "Pega la URL pública de tu MP3/MP4 en inglés y lo traduciremos y doblaremos al español, conservando la voz.",
    "studio.dub.dubbing": "Doblando…",
    "studio.dub.generateBtn": "Doblar al español",
    "studio.dub.downloadMp3": "Descargar MP3 en español",
    "studio.dub.timingNote": "El doblaje generalmente tarda 1–3 minutos según la duración. La fuente debe ser descargable públicamente.",
    "studio.dub.poweredBy": "Desarrollado por ElevenLabs",
    // AI Video Creator
    "aiVideo.title": "Creador de Video con IA",
    "aiVideo.desc": "Genera videos completos — guión, storyboard, locución y edición final.",
    "aiVideo.step.purpose": "¿Qué estás creando hoy?",
    "aiVideo.purpose.organic": "Contenido Orgánico para Redes",
    "aiVideo.purpose.organic.desc": "Post, Reel, Story o contenido general para redes sociales",
    "aiVideo.purpose.paid_ad": "Anuncio de Video Pagado",
    "aiVideo.purpose.paid_ad.desc": "Video destinado a publicarse como anuncio",
    "aiVideo.purpose.promo": "Promoción de Producto o Servicio",
    "aiVideo.purpose.promo.desc": "Muestra un producto o servicio específico",
    "aiVideo.purpose.educational": "Video Educativo",
    "aiVideo.purpose.educational.desc": "Enseña, explica o demuestra",
    "aiVideo.purpose.brand": "Reconocimiento de Marca",
    "aiVideo.purpose.brand.desc": "Construye reconocimiento para tu marca",
    "aiVideo.purpose.testimonial": "Testimonio",
    "aiVideo.purpose.testimonial.desc": "Comparte la historia de éxito de un cliente",
    "aiVideo.purpose.announcement": "Anuncio",
    "aiVideo.purpose.announcement.desc": "Anuncia un lanzamiento, evento o actualización",
    "aiVideo.step.preferences": "Preferencias del Video",
    "aiVideo.prefs.about": "¿De qué trata este video?",
    "aiVideo.prefs.platform": "Plataforma",
    "aiVideo.prefs.contentStyle": "Estilo de Contenido",
    "aiVideo.prefs.length": "Duración del Video",
    "aiVideo.prefs.format": "Formato",
    "aiVideo.prefs.language": "Idioma",
    "aiVideo.prefs.voice": "Voz",
    "aiVideo.prefs.voice.cloned": "Mi Voz Clonada",
    "aiVideo.prefs.voice.library": "Elegir de la Biblioteca de Voces",
    "aiVideo.prefs.voice.music": "Sin Voz — Solo Música",
    "aiVideo.prefs.voice.noClone": "Crear Mi Voz Clonada",
    "aiVideo.prefs.captions": "Subtítulos Palabra por Palabra",
    "aiVideo.prefs.cta": "Llamada a la Acción",
    "aiVideo.prefs.cta.none": "Sin CTA",
    "aiVideo.prefs.socialCopy": "Texto para Redes (opcional)",
    "aiVideo.prefs.generateScript": "Generar Guión y Storyboard",
    "aiVideo.prefs.back": "Atrás",
    "aiVideo.generating": "Escribiendo tu guión y storyboard…",
    "aiVideo.step.review": "Revisa tu Guión y Storyboard",
    "aiVideo.review.summary": "Resumen",
    "aiVideo.review.script": "Guión",
    "aiVideo.review.scenes": "Escenas del Storyboard",
    "aiVideo.review.credits": "Créditos requeridos",
    "aiVideo.review.creditsNote": "{n} crédito se usará solo después de que el video se renderice exitosamente.",
    "aiVideo.review.editScript": "Editar Guión",
    "aiVideo.review.editStoryboard": "Editar Storyboard",
    "aiVideo.review.changePrefs": "Cambiar Preferencias",
    "aiVideo.review.generate": "Generar Video Final",
    "aiVideo.review.bilingualNote": "Inglés + Español = 2 videos = 2 créditos",
    "aiVideo.rendering.title": "Generando Tu Video",
    "aiVideo.rendering.clips": "{done} de {total} clips listos",
    "aiVideo.rendering.merging": "Combinando clips y locución…",
    "aiVideo.rendering.note": "Esto tarda 3–5 minutos. Puedes quedarte aquí o revisar Mis Videos cuando esté listo.",
    "aiVideo.complete.title": "Tu Video Está Listo",
    "aiVideo.complete.download": "Descargar Ahora",
    "aiVideo.complete.postNow": "Publicar Ahora",
    "aiVideo.complete.schedule": "Programar",
    "aiVideo.complete.convertToAd": "Convertir a Anuncio Pagado",
    "aiVideo.complete.runAsAd": "Publicar como Anuncio",
    "aiVideo.complete.downloadCaption": "Descargar Texto",
    "aiVideo.complete.copyCaption": "Copiar Texto",
    "aiVideo.complete.copyHashtags": "Copiar Hashtags",
    "aiVideo.complete.anotherSize": "Crear Otro Tamaño",
    "aiVideo.complete.viewInLibrary": "Ver en Mis Videos",
    "aiVideo.complete.connectAccount": "Conecta tu cuenta para usar esta opción.",
    "aiVideo.complete.noChargeForSize": "Crear otro tamaño no consume un crédito adicional.",
    "aiVideo.toast.aboutRequired": "Por favor describe de qué trata el video",
    "aiVideo.toast.scriptFailed": "Error al generar el guión — intenta de nuevo",
    "aiVideo.toast.renderFailed": "Error en el renderizado — tu crédito no fue cobrado",
    "aiVideo.toast.noCredits": "Necesitas al menos {n} crédito de video para generar este video",
    "aiVideo.toast.submitted": "Generación de video iniciada — el renderizado tarda 3–5 minutos",
    "studio.tool.aiVideoCreator.name": "Creador de Video con IA",
    "studio.tool.aiVideoCreator.desc": "Pipeline completo — guión, storyboard, locución y edición final.",
  },
};

type Vars = Record<string, string | number>;

type Ctx = {
  lang: Language;
  setLang: (l: Language) => void;
  /** Translate key. Falls back to EN, then to the key itself. Supports {var} interpolation. */
  t: (key: string, vars?: Vars) => string;
  /** Pluralization helper. Spanish + English both use 1 vs other. */
  tn: (oneKey: string, otherKey: string, n: number, vars?: Vars) => string;
};

const I18nContext = createContext<Ctx | null>(null);

function interpolate(str: string, vars?: Vars): string {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

function detect(): Language {
  if (typeof window === "undefined") return "en";
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (saved === "en" || saved === "es") return saved;
  } catch { /* ignore */ }
  const nav = (typeof navigator !== "undefined" ? navigator.language : "en") || "en";
  return nav.toLowerCase().startsWith("es") ? "es" : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>("en");
  useEffect(() => { setLangState(detect()); }, []);
  const setLang = (l: Language) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
    if (typeof document !== "undefined") document.documentElement.lang = l;
  };
  const t = (key: string, vars?: Vars) => {
    const raw = STRINGS[lang][key] ?? STRINGS.en[key] ?? key;
    return interpolate(raw, vars);
  };
  const tn = (oneKey: string, otherKey: string, n: number, vars?: Vars) =>
    t(n === 1 ? oneKey : otherKey, { n, ...(vars ?? {}) });
  return <I18nContext.Provider value={{ lang, setLang, t, tn }}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback if used outside provider
    const t = (k: string, vars?: Vars) => interpolate(STRINGS.en[k] ?? k, vars);
    return {
      lang: "en",
      setLang: () => {},
      t,
      tn: (oneKey, otherKey, n, vars) => t(n === 1 ? oneKey : otherKey, { n, ...(vars ?? {}) }),
    };
  }
  return ctx;
}
